<template>
  <div class="scraping-page">

    <p v-if="ds.introText">{{ ds.introText }}</p>

    <!-- Main view -->
    <div v-show="!configView && !veilleView">

    <!-- Filtres Query Builder -->
    <div class="card mb-3">
      <div class="card-header d-flex align-items-center" style="gap:8px; cursor:pointer" @click="filtersOpen = !filtersOpen">
        <i :class="filtersOpen ? 'fas fa-chevron-down' : 'fas fa-chevron-right'"></i>
        <strong>Filtres</strong>
        <span v-if="whereClause || (statusDomainFilter.domain && statusDomainFilter.statuses.length)" class="badge badge-primary ml-2">actif</span>
      </div>
      <div v-show="filtersOpen" class="card-body">
        <div :id="builderId"></div>
        <div class="mt-2 pt-2 border-top d-flex align-items-center flex-wrap" style="gap:8px">
          <small class="font-weight-bold text-muted">Statut par domaine :</small>
          <b-form-select v-model="statusDomainFilter.domain" size="sm" style="width:auto">
            <option value="">— Domaine (désactivé) —</option>
            <option v-for="cfg in visibleScrapingConfigs" :key="cfg.key" :value="cfg.key">{{ cfg.domain }}{{ cfg.groupe ? ' · ' + cfg.groupe : '' }}</option>
          </b-form-select>
          <b-dropdown v-if="statusDomainFilter.domain" size="sm" variant="outline-secondary">
            <template #button-content>
              <span v-if="!statusDomainFilter.statuses.length" class="text-muted">— Statut —</span>
              <span v-else-if="statusDomainFilter.statuses.length === 1">{{ { non_fait: 'Non fait', non_trouve: 'Non trouvé', a_verifier: 'À vérifier', valide: 'Validé' }[statusDomainFilter.statuses[0]] }}</span>
              <span v-else>{{ statusDomainFilter.statuses.length }} statuts</span>
            </template>
            <li role="presentation" v-for="opt in [{v:'non_fait',l:'Non fait'},{v:'non_trouve',l:'Non trouvé'},{v:'a_verifier',l:'À vérifier'},{v:'valide',l:'Validé'}]" :key="opt.v">
              <a class="dropdown-item" href="#" @click.prevent.stop="toggleStatusDomainFilter(opt.v)" style="font-size:0.85em">
                <div class="d-flex align-items-center" style="gap:6px">
                  <input type="checkbox" :checked="statusDomainFilter.statuses.includes(opt.v)" style="flex-shrink:0;margin:0;pointer-events:none" />
                  <span>{{ opt.l }}</span>
                </div>
              </a>
            </li>
          </b-dropdown>
          <b-button v-if="statusDomainFilter.domain" size="sm" variant="outline-secondary" @click="statusDomainFilter = { domain: '', statuses: [] }">
            <i class="fas fa-times"></i>
          </b-button>
        </div>
        <div class="mt-2 d-flex" style="gap:8px">
          <b-button size="sm" variant="success" @click="applyFilters">
            <i class="fas fa-search"></i> Appliquer
          </b-button>
          <b-button size="sm" variant="outline-secondary" @click="resetFilters">
            <i class="fas fa-times"></i> Reset
          </b-button>
        </div>
      </div>
    </div>

    <!-- Toolbar -->
    <div class="d-flex align-items-center flex-wrap mb-3" style="gap:8px">
      <b-button size="sm" variant="success" @click="reload">
        <i class="fas fa-sync-alt"></i> Actualiser
      </b-button>
      <b-button size="sm" variant="outline-success" :disabled="csvExporting" @click="exportCsvAll" title="Exporter toutes les lignes en CSV">
        <b-spinner v-if="csvExporting" small style="width:0.8em;height:0.8em" />
        <i v-else class="fas fa-file-csv"></i>
        Export CSV<span v-if="csvExporting && csvProgress"> ({{ csvProgress }}%)</span>
      </b-button>
      <b-dropdown size="sm" variant="outline-secondary" no-caret>
        <template #button-content><i class="fas fa-columns mr-1"></i>Colonnes<span v-if="listingExtraCols.length" class="badge badge-primary ml-1" style="font-size:0.75em">{{ listingExtraCols.length }}</span></template>
        <li role="presentation" class="px-2 pb-1 d-flex" style="gap:6px">
          <b-button size="sm" variant="link" class="p-0 text-primary" style="font-size:0.8em" @click.stop="listingExtraCols = allListingExtraCols.map(c => c.key)">Tout</b-button>
          <b-button size="sm" variant="link" class="p-0 text-secondary" style="font-size:0.8em" @click.stop="listingExtraCols = []">Aucun</b-button>
        </li>
        <li role="presentation" class="px-2" v-if="!allListingExtraCols.length"><small class="text-muted">Aucune colonne disponible</small></li>
        <li role="presentation" v-for="col in allListingExtraCols" :key="col.key">
          <a class="dropdown-item" href="#" @click.prevent.stop="listingExtraCols.includes(col.key) ? listingExtraCols.splice(listingExtraCols.indexOf(col.key), 1) : listingExtraCols.push(col.key)" style="font-size:0.85em">
            <div class="d-flex align-items-center" style="gap:6px">
              <input type="checkbox" :checked="listingExtraCols.includes(col.key)" style="flex-shrink:0;margin:0;pointer-events:none" />
              <span>{{ col.label }}</span>
            </div>
          </a>
        </li>
      </b-dropdown>
      <b-button size="sm" variant="outline-secondary" @click="openConfigModal">
        <i class="fas fa-cog"></i> Configurer scrapers
      </b-button>
      <b-button v-if="ds.showVeilleTarifaire !== false" size="sm" variant="outline-info" @click="openGroupRulesModal">
        <i class="fas fa-layer-group"></i> Règles de groupe
      </b-button>
      <b-form-select
        v-model="pageSize"
        :options="pageSizeOptions"
        size="sm"
        style="width:auto"
        @change="reload"
      />
      <small class="text-muted">résultats / page</small>
      <span v-if="selectedIds.length" class="text-muted ml-2">
        <strong>{{ selectedIds.length }}</strong> produit(s) sélectionné(s)
      </span>
    </div>

    <!-- Bulk actions bar -->
    <div class="d-flex align-items-center flex-wrap mb-2 p-2 rounded border bg-light" style="gap:8px">
      <strong class="text-muted mr-1" style="font-size:0.85em; white-space:nowrap">Traitement en lot :</strong>
      <b-button-group size="sm">
        <b-button :variant="bulkScope === 'selected' ? 'primary' : 'outline-secondary'" :disabled="!selectedIds.length" @click="bulkScope = 'selected'">
          Sélection <span class="badge badge-light ml-1">{{ selectedIds.length }}</span>
        </b-button>
        <b-button :variant="bulkScope === 'page' ? 'primary' : 'outline-secondary'" @click="bulkScope = 'page'">
          Page <span class="badge badge-light ml-1">{{ products.length }}</span>
        </b-button>
        <b-button :variant="bulkScope === 'all' ? 'primary' : 'outline-secondary'" @click="bulkScope = 'all'">
          Tous <span class="badge badge-light ml-1">{{ total }}</span>
        </b-button>
      </b-button-group>
      <b-dropdown size="sm" variant="outline-secondary" no-caret toggle-class="text-left" style="min-width:180px">
        <template #button-content>
          <i class="fas fa-spider mr-1"></i>
          <span v-if="!bulkCfgKeys.length" class="text-muted">— Choisir les scrapers —</span>
          <span v-else-if="bulkCfgKeys.length === 1">
            {{ visibleScrapingConfigs.find(c => c.key === bulkCfgKeys[0]) && visibleScrapingConfigs.find(c => c.key === bulkCfgKeys[0]).domain }}<span v-if="visibleScrapingConfigs.find(c => c.key === bulkCfgKeys[0]) && visibleScrapingConfigs.find(c => c.key === bulkCfgKeys[0]).groupe" class="text-muted"> · {{ visibleScrapingConfigs.find(c => c.key === bulkCfgKeys[0]).groupe }}</span>
          </span>
          <span v-else>{{ bulkCfgKeys.length }} scrapers sélectionnés</span>
        </template>
        <li
          v-for="cfg in visibleScrapingConfigs"
          :key="cfg.key"
          role="presentation"
        >
          <a class="dropdown-item" href="#" @click.prevent.stop="toggleBulkCfg(cfg.key, !bulkCfgKeys.includes(cfg.key))" style="font-size:0.85em">
            <div class="d-flex align-items-center" style="gap:6px">
              <input type="checkbox" :checked="bulkCfgKeys.includes(cfg.key)" @click.stop style="flex-shrink:0; margin:0; pointer-events:none" />
              <span>{{ cfg.domain }}<span v-if="cfg.groupe" class="text-muted"> · {{ cfg.groupe }}</span></span>
            </div>
          </a>
        </li>
        <li role="presentation"><hr class="dropdown-divider" /></li>
        <li role="presentation">
          <a class="dropdown-item" href="#" @click.prevent.stop="bulkCfgKeys = visibleScrapingConfigs.map(c => c.key)" style="font-size:0.85em">Tout sélectionner</a>
        </li>
        <li role="presentation">
          <a class="dropdown-item" href="#" @click.prevent.stop="bulkCfgKeys = []" style="font-size:0.85em">Tout désélectionner</a>
        </li>
      </b-dropdown>
      <!-- Bouton simple pour scraping-fichiers (pas de veille tarifaire) -->
      <b-button
        v-if="ds.configType === 'scraping_file'"
        size="sm"
        variant="warning"
        :disabled="bulkRunning || !scenarioUid || !bulkCfgKeys.length || (bulkScope === 'selected' && !selectedIds.length)"
        @click="bulkLaunchScraping"
      >
        <b-spinner v-if="bulkRunning" small style="width:0.8em;height:0.8em" />
        <i v-else class="fas fa-rocket"></i>
        Lancer le scraping
        <span v-if="bulkRunning" class="ml-1">({{ bulkDone }}/{{ bulkTotal }})</span>
      </b-button>
      <!-- Split button scraping/veille avec sélecteur de mode -->
      <div v-else class="d-flex" style="gap:0">
        <b-button
          size="sm"
          variant="warning"
          style="border-top-right-radius:0;border-bottom-right-radius:0"
          :disabled="bulkRunning || veilleQueueProcessing || (bulkAction === 'scraping' && (!scenarioUid || !bulkCfgKeys.length)) || (bulkScope === 'selected' && !selectedIds.length)"
          @click.stop="bulkAction === 'scraping' ? bulkLaunchScraping() : bulkLaunchVeilleTarifaire()"
        >
          <b-spinner v-if="bulkRunning || veilleQueueProcessing" small style="width:0.8em;height:0.8em" />
          <i v-else-if="bulkAction === 'scraping'" class="fas fa-rocket"></i>
          <i v-else class="fas fa-chart-line"></i>
          <span v-if="bulkRunning"> ({{ bulkDone }}/{{ bulkTotal }})</span>
          <span v-else-if="veilleQueueProcessing"> Veille ({{ veilleQueue.length }} en attente)</span>
          <span v-else-if="bulkAction === 'scraping'"> Lancer le scraping</span>
          <span v-else> Lancer la veille tarifaire</span>
        </b-button>
        <div style="position:relative">
          <b-button
            size="sm"
            variant="warning"
            style="border-top-left-radius:0;border-bottom-left-radius:0;border-left:1px solid rgba(0,0,0,0.2);height:100%"
            :disabled="bulkRunning || veilleQueueProcessing"
            @click.stop="bulkVeilleDropdownOpen = !bulkVeilleDropdownOpen"
          >
            <i class="fas fa-caret-down"></i>
          </b-button>
          <div
            v-if="bulkVeilleDropdownOpen"
            class="dropdown-menu show"
            style="min-width:240px;right:0;left:auto;top:100%;z-index:1050"
            @click.stop
          >
            <h6 class="dropdown-header">Choisir le mode</h6>
            <a
              class="dropdown-item d-flex align-items-center justify-content-between"
              href="#"
              @click.prevent="bulkAction = 'scraping'; bulkVeilleDropdownOpen = false"
            >
              <span><i class="fas fa-rocket mr-1"></i> Lancer le scraping</span>
              <i v-if="bulkAction === 'scraping'" class="fas fa-check text-success ml-2"></i>
            </a>
            <a
              v-if="hasVeilleTarifaireConfig"
              class="dropdown-item d-flex align-items-center justify-content-between"
              href="#"
              @click.prevent="bulkAction = 'veille'; bulkVeilleDropdownOpen = false"
            >
              <span><i class="fas fa-chart-line mr-1"></i> Lancer la veille tarifaire
              <span v-if="!veilleScenarioUid" class="badge badge-warning ml-1" style="font-size:0.7em">scénario introuvable</span></span>
              <i v-if="bulkAction === 'veille'" class="fas fa-check text-success ml-2"></i>
            </a>
          </div>
        </div>
      </div>
      <b-form-checkbox v-model="sequentialMode" switch class="mb-0" title="Mode file d'attente : les lignes sont traitées une par une. Clic sur la loupe = ajout en file." style="align-self:center">
        <i class="fas fa-list-ol mr-1"></i>Ligne à ligne
        <span v-if="scrapingQueue.length" class="badge badge-secondary ml-1">{{ scrapingQueue.length }}</span>
      </b-form-checkbox>
      <b-button
        v-if="queueProcessing || scrapingQueue.length"
        size="sm"
        variant="danger"
        title="Stopper et vider la file d'attente scraping"
        @click="stopQueue"
      >
        Stopper scraping
      </b-button>
      <b-button
        v-if="veilleQueueProcessing || veilleQueue.length"
        size="sm"
        variant="danger"
        title="Stopper et vider la file d'attente veille tarifaire"
        @click="stopVeilleQueue"
      >
        Stopper veille
      </b-button>
      <div class="ml-auto"><slot name="toolbar-right" /></div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" /><br>
      <small class="text-muted mt-2">Chargement...</small>
    </div>

    <!-- Table -->
    <div v-else class="table-responsive">
      <table class="table table-sm table-hover table-bordered">
        <thead class="thead-light">
          <tr>
            <th style="width:40px; text-align:center">
              <b-form-checkbox
                :checked="selectAll"
                @change="toggleSelectAll"
                :indeterminate="selectIndeterminate"
              />
            </th>
            <th>id</th>
            <th v-for="col in (ds.listingColumns || [{key:'id_cms',label:'id_cms'},{key:'status',label:'Statut'}])" :key="col.key">{{ col.label }}</th>
            <th v-if="ds.showNomColumn !== false">Nom</th>
            <th v-if="ds.showCategoriesColumn !== false">Catégories</th>
            <th v-if="globalGroupFn && !(ds.listingColumns || []).some(c => c.key === 'groupe')" style="width:100px">Groupe</th>
            <th v-if="ds.showDataKeys" style="width:80px;text-align:center">Données</th>
            <th v-if="!ds.showDataKeys && ds.rowActions && ds.rowActions.length" style="width:80px;text-align:center">Données</th>
            <th v-for="col in visibleListingExtraCols" :key="'extra-' + col.key">{{ col.label }}</th>
            <th
              v-for="cfg in visibleScrapingConfigs"
              :key="cfg.key"
              style="min-width:160px; text-align:center"
            >
              <span v-if="cfg.groupe && cfg.groupe !== 'null'" class="badge badge-info d-block mb-1" style="font-size:0.7em; letter-spacing:0.03em">{{ cfg.groupe }}</span>
              <span class="d-block font-weight-bold">{{ cfg.domain }}</span>
              <small v-if="ds.showVeilleTarifaire !== false" class="text-muted">{{ cfg.veille_tarifaire ? 'veille tarifaire' : 'scraping' }}</small>
            </th>
            <th v-if="hasVeilleTarifaireConfig" style="width:90px; text-align:center; white-space:nowrap">Veille tarif.</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="products.length === 0">
            <td :colspan="4 + (ds.listingColumns || [{key:'id_cms'},{key:'status'}]).length + visibleScrapingConfigs.length" class="text-center text-muted py-4">
              Aucun produit trouvé
            </td>
          </tr>
          <tr v-for="product in products" :key="product.id">
            <td style="text-align:center; vertical-align:middle">
              <b-form-checkbox v-model="selectedIds" :value="product.id" />
            </td>
            <td style="vertical-align:middle">{{ product.id }}</td>
            <td v-for="col in (ds.listingColumns || [{key:'id_cms'},{key:'status'}])" :key="col.key" style="vertical-align:middle">
              <template v-if="col.key === 'groupe'">
                <span v-if="product[col.key]" class="badge badge-info" style="font-size:0.8em">{{ product[col.key] }}</span>
                <span v-else class="text-muted">—</span>
              </template>
              <template v-else-if="col.fromData">{{ parseDataField(product.data, col.fromData) }}</template>
              <template v-else>{{ col.render ? col.render(product[col.key]) : product[col.key] }}</template>
            </td>
            <td v-if="ds.showNomColumn !== false" style="vertical-align:middle">{{ getNom(product) }}</td>
            <td v-if="ds.showCategoriesColumn !== false" style="vertical-align:middle">
              <span
                v-for="catId in getCategories(product)"
                :key="catId"
                class="badge mr-1 mb-1"
                :class="catId == product[ds.categoryPrimaryField || 'default_category'] ? 'badge-primary' : 'badge-secondary'"
                :title="catId == product[ds.categoryPrimaryField || 'default_category'] ? 'Catégorie principale' : ''"
                style="font-size:0.75em"
              >
                {{ getCatName(catId) }}
              </span>
            </td>
            <td v-if="globalGroupFn && !(ds.listingColumns || []).some(c => c.key === 'groupe')" style="vertical-align:middle; text-align:center">
              <span v-if="product.groupe" class="badge badge-info" style="font-size:0.8em">{{ product.groupe }}</span>
              <span v-else class="text-muted" style="font-size:0.75em">—</span>
            </td>
            <td v-if="ds.showDataKeys" style="vertical-align:middle;text-align:center">
              <b-button v-if="product.data" size="sm" variant="outline-info" title="Voir les données" @click="conditionTestDataRow = product; conditionTestDataVisible = true"><i class="fas fa-table"></i></b-button>
              <span v-else class="text-muted">—</span>
            </td>
            <td v-if="!ds.showDataKeys && ds.rowActions && ds.rowActions.length" style="vertical-align:middle;text-align:center;white-space:nowrap">
              <b-button
                v-for="action in ds.rowActions"
                :key="action.key"
                size="sm"
                :variant="action.variant || 'outline-secondary'"
                :title="action.label"
                class="mr-1"
                @click="action.onClick(product)"
              >
                <i v-if="action.icon" :class="action.icon"></i>
                <span v-else>{{ action.label }}</span>
              </b-button>
            </td>
            <td v-for="col in visibleListingExtraCols" :key="'extra-' + col.key" style="vertical-align:middle">
              <template v-if="col.fromData">{{ parseDataField(product.data, col.fromData) }}</template>
              <template v-else>{{ product[col.key] ?? '—' }}</template>
            </td>
            <td
              v-for="cfg in visibleScrapingConfigs"
              :key="cfg.key"
              style="text-align:center; vertical-align:middle"
            >
              <!-- Groupe incompatible : scraper d'un autre groupe que le produit -->
              <span
                v-if="cfg.groupe && product.groupe !== cfg.groupe"
                class="badge badge-secondary"
                style="font-size:0.8em; opacity:0.6"
                :title="'Scraper du groupe \'' + cfg.groupe + '\' — produit du groupe \'' + product.groupe + '\''"
              >
                ✗ Non applicable
              </span>
              <template v-else>
              <div v-if="getScrapingRow(product.id, cfg)" class="d-flex align-items-center justify-content-center" style="gap:3px">
                <span
                  class="badge"
                  :class="scrapingBadge(getScrapingRow(product.id, cfg)).cls"
                  style="cursor:pointer; font-size:0.8em"
                  @click="openModal(product, cfg)"
                >
                  {{ scrapingBadge(getScrapingRow(product.id, cfg)).label }}
                </span>
                <span
                  v-if="inlineLaunchKeys[`${product.id}__${cfg.key}`]"
                  style="font-size:0.8em"
                  title="Scraping en cours..."
                >
                  <b-spinner small style="width:0.7em;height:0.7em;color:#fd7e14" />
                </span>
                <span
                  v-else-if="scrapingQueueKeySet.has(`${product.id}__${cfg.key}`)"
                  style="font-size:0.85em; color:#6c757d; cursor:pointer"
                  title="En attente dans la file — cliquer pour retirer"
                  @click="removeFromQueue(`${product.id}__${cfg.key}`)"
                >
                  <i class="fas fa-hourglass-half"></i>
                </span>
              </div>
              <div v-else>
                <div v-if="inlineEditKey === `${product.id}__${cfg.key}`" class="d-flex align-items-center" style="gap:4px;min-width:180px">
                  <b-form-input
                    v-model="inlineEditUrl"
                    size="sm"
                    placeholder="https://..."
                    @keyup.enter="saveInlineUrl(product, cfg)"
                    @keyup.esc="inlineEditKey = null"
                    autofocus
                    style="font-size:0.8em"
                  />
                  <b-button size="sm" variant="primary" :disabled="inlineSaving" @click="saveInlineUrl(product, cfg)">
                    <b-spinner v-if="inlineSaving" small />
                    <i v-else class="fas fa-check"></i>
                  </b-button>
                  <b-button size="sm" variant="outline-secondary" @click="inlineEditKey = null">
                    <i class="fas fa-times"></i>
                  </b-button>
                </div>
                <div v-else class="d-flex align-items-center justify-content-center" style="gap:4px">
                  <span
                    v-if="!passesAnyConfig(product, cfg)"
                    class="badge badge-secondary"
                    style="font-size:0.8em; opacity:0.6"
                    title="Ce produit ne satisfait pas les conditions du scraper"
                  >
                    ✗ Non applicable
                  </span>
                  <template v-else>
                    <span
                      v-if="productMissingDataCols(product, cfg).length"
                      :title="'Colonnes manquantes : ' + productMissingDataCols(product, cfg).join(', ')"
                      style="color:#dc3545; font-size:0.85em; cursor:default"
                    >
                      <i class="fas fa-exclamation-triangle"></i>
                    </span>
                    <span
                      class="badge badge-light text-muted"
                      style="font-size:0.8em; border:1px solid #dee2e6; cursor:pointer"
                      @click="inlineEditKey = `${product.id}__${cfg.key}`; inlineEditUrl = ''"
                    >
                      — Non fait
                    </span>
                    <b-button
                      size="sm"
                      variant="outline-warning"
                      style="padding:1px 5px;font-size:0.75em"
                      :disabled="!!inlineLaunchKeys[`${product.id}__${cfg.key}`] || !scenarioUid"
                      :title="scrapingQueueKeySet.has(`${product.id}__${cfg.key}`) ? 'En attente dans la file' : (scenarioUid ? 'Lancer le scraping' : 'Projet Scraping - search introuvable')"
                      @click="launchScrapingInline(product, cfg)"
                    >
                      <b-spinner v-if="inlineLaunchKeys[`${product.id}__${cfg.key}`]" small />
                      <i v-else-if="scrapingQueueKeySet.has(`${product.id}__${cfg.key}`)" class="fas fa-clock" style="color:#17a2b8"></i>
                      <i v-else class="fas fa-search"></i>
                    </b-button>
                  </template>
                </div>
              </div>
              </template>
            </td>
            <td v-if="hasVeilleTarifaireConfig" style="text-align:center; vertical-align:middle; white-space:nowrap">
              <span
                v-if="veilleInlineRunning[product.id]"
                style="font-size:0.8em; color:#17a2b8" title="Veille tarifaire en cours..."
              >
                <b-spinner small style="width:0.7em;height:0.7em;color:#17a2b8" />
              </span>
              <span
                v-else-if="veilleQueueIdSet.has(product.id)"
                style="font-size:0.85em; color:#6c757d; cursor:pointer"
                title="En attente dans la file — cliquer pour retirer"
                @click="removeFromVeilleQueue(product.id)"
              >
                <i class="fas fa-hourglass-half"></i>
              </span>
              <b-button
                v-else-if="veilleScenarioUid && productHasValidScraping(product)"
                size="sm"
                variant="outline-info"
                title="Lancer la veille tarifaire"
                style="padding:1px 5px;font-size:0.75em"
                @click="launchVeilleTarifaireInline(product)"
              >
                <i class="fas fa-chart-line"></i>
              </b-button>
              <span
                v-else
                style="font-size:0.8em; color:#ced4da; cursor:default"
                :title="!veilleScenarioUid ? 'Scénario Veille tarifaire - produit introuvable' : 'Aucun scraping validé avec veille tarifaire activée'"
              >
                <i class="fas fa-chart-line"></i>
              </span>
              <b-button
                size="sm"
                variant="outline-secondary"
                class="ml-1"
                title="Voir les résultats de veille tarifaire"
                style="padding:1px 5px;font-size:0.75em"
                @click="openVeilleResultModal(product)"
              >
                <i class="fas fa-eye"></i>
              </b-button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <div v-if="!loading" class="d-flex align-items-center justify-content-between mt-2 flex-wrap" style="gap:8px">
      <small class="text-muted">{{ total }} produit(s) au total</small>
      <b-pagination
        v-model="page"
        :total-rows="total"
        :per-page="pageSize"
        size="sm"
        @input="loadPage"
      />
    </div>

    </div><!-- /main view -->

    <!-- Config view -->
    <div v-if="configView && !veilleView">
      <!-- Formulaire édition -->
      <div v-if="configEditing !== null">
        <div class="d-flex align-items-center mb-2" style="gap:8px">
          <b-button size="sm" variant="outline-secondary" @click="backToConfigList">
            <i class="fas fa-arrow-left"></i> Retour
          </b-button>
          <strong>{{ wizardActive ? (configForm.id ? 'Modifier via l\'assistant' : 'Nouvelle configuration') : 'Modifier la configuration' }}</strong>
          <b-button v-if="configForm.id" size="sm" variant="outline-secondary" v-b-tooltip.hover title="La configuration sera sauvegardée avant de dupliquer" @click="duplicateConfig">
            <i class="fas fa-copy mr-1"></i>Dupliquer
          </b-button>
          <b-button v-if="!wizardActive && configForm.id" size="sm" variant="outline-primary" class="ml-auto" @click="switchToWizard">
            <i class="fas fa-magic mr-1"></i> Modifier via l'assistant
          </b-button>
          <b-button v-if="wizardActive && configForm.id" size="sm" variant="outline-secondary" class="ml-auto" @click="exitWizardToAdvanced">
            <i class="fas fa-sliders-h mr-1"></i> Mode avancé
          </b-button>
        </div>
        <!-- Navigation entre configs du même domaine/groupe -->
        <div v-if="siblingConfigs.length > 1" class="mb-3 p-2 border rounded bg-light d-flex align-items-center flex-wrap" style="gap:6px">
          <small class="text-muted mr-2" style="white-space:nowrap"><i class="fas fa-exchange-alt mr-1"></i>Configurations :</small>
          <b-button v-for="sib in siblingConfigs" :key="sib.id"
            size="sm"
            :variant="(sib.conditions || '') === (configForm.conditions || '') ? 'primary' : 'outline-secondary'"
            :title="(sib.conditions || '') !== (configForm.conditions || '') ? 'La configuration sera sauvegardée avant de changer' : ''"
            v-b-tooltip.hover
            @click="navigateToSibling(sib)"
          >{{ sib.conditions || 'Défaut' }}</b-button>
        </div>

        <!-- Wizard -->
        <div v-if="wizardActive" class="wizard-container">
          <!-- Barre de progression -->
          <div class="wizard-progress mb-4">
            <div class="d-flex justify-content-between align-items-center mb-1">
              <small class="text-muted">Étape {{ wizardStep }} / {{ wizardTotalSteps }}</small>
              <small class="text-muted">{{ Math.round(wizardStep / wizardTotalSteps * 100) }}%</small>
            </div>
            <div class="progress" style="height:6px">
              <div class="progress-bar bg-primary" :style="{width: (wizardStep / wizardTotalSteps * 100) + '%'}"></div>
            </div>
            <div class="d-flex justify-content-between mt-1">
              <small v-for="(label, idx) in (ds.showVeilleTarifaire === false ? ['Domaine','Déclenchement','Schema',configForm.search_mode === 'site_search' ? 'URL recherche' : 'Identification','Projet'] : ['Domaine','Déclenchement','Schema',configForm.search_mode === 'site_search' ? 'URL recherche' : 'Identification','Veille','Projet'])" :key="idx"
                :class="['wizard-step-label', idx + 1 <= wizardStep ? 'text-primary font-weight-bold' : 'text-muted']"
                style="font-size:0.7rem; flex:1; text-align:center">{{ label }}</small>
            </div>
          </div>

          <!-- Étape 1 : Domaine -->
          <div v-show="wizardStep === 1" class="wizard-step">
            <h5><i class="fas fa-globe text-primary mr-2"></i>Nom de domaine</h5>
            <p class="text-muted">Entrez le site à scraper. L'adresse sera automatiquement nettoyée.</p>
            <b-form-group label="Domaine *">
              <b-form-input
                v-model="configForm.domain"
                size="lg"
                placeholder="ex: monsite.fr ou https://www.monsite.fr/"
                @blur="cleanDomain"
                autofocus
              />
              <small v-if="configForm.domain" class="text-success mt-1 d-block">
                <i class="fas fa-check-circle"></i> Sera sauvegardé comme : <strong>{{ previewCleanDomain }}</strong>
              </small>
            </b-form-group>
            <b-form-group label="Groupe" description="Optionnel. Permet de regrouper les scrapers par catégorie de produits (ex: high-tech, vêtements). Les scrapers sans groupe sont génériques.">
              <b-form-input
                v-model="configForm.groupe"
                size="lg"
                placeholder="ex: high-tech, vêtements, mobilier"
                :list="'wizard-groupes-list'"
                autocomplete="off"
              />
              <datalist :id="'wizard-groupes-list'">
                <option v-for="g in groupesList" :key="g" :value="g" />
              </datalist>
            </b-form-group>
            <b-form-group label="Conditions (label de routage)" description="Optionnel — label identifiant la stratégie parmi plusieurs configs du même domaine/groupe (ex: 'ean', 'code fabricant', 'nom'). Laisser vide pour la config par défaut (fallback).">
              <b-form-input v-model="configForm.conditions" size="lg" placeholder="ex: ean, code fabricant, nom" />
            </b-form-group>
            <b-form-group v-if="siblingConfigs.length > 1" label="Priorité" description="Ordre d'évaluation parmi les configs du même domaine/groupe — plus petit = priorité plus haute. Modifiable par glisser-déposer dans le listing.">
              <b-form-input :value="(configForm.priority || 0) + 1" type="number" size="lg" readonly plaintext />
            </b-form-group>
          </div>

          <!-- Étape 2 : Conditions de déclenchement -->
          <div v-show="wizardStep === 2" class="wizard-step">
            <h5><i class="fas fa-bolt text-primary mr-2"></i>Conditions de déclenchement</h5>
            <p class="text-muted">Ces conditions sont évaluées <strong>au tout début du scénario</strong>, avant toute recherche. Si un produit ne les satisfait pas, le scraping est ignoré pour ce produit.</p>
            <div class="d-flex align-items-center mb-2" style="gap:8px">
              <b-form-radio-group v-model="visualRulesModeKey" buttons button-variant="outline-secondary" size="sm">
                <b-form-radio value="visual"><i class="fas fa-sliders-h mr-1"></i>Mode visuel</b-form-radio>
                <b-form-radio value="code"><i class="fas fa-code mr-1"></i>Mode code</b-form-radio>
              </b-form-radio-group>
            </div>
            <!-- Mode visuel -->
            <div v-if="visualRulesMode" class="query-builder border rounded p-2" style="background:#fff">
              <div v-if="!visualRuleGroups.length" class="text-center text-muted py-2" style="font-size:0.9em">
                <i class="fas fa-info-circle mr-1"></i> Aucune condition — tous les produits déclencheront le scraping.
                <b-button size="sm" variant="outline-primary" class="ml-2" @click="addGroup"><i class="fas fa-plus mr-1"></i>Ajouter une condition</b-button>
              </div>
              <div v-for="(group, groupIdx) in visualRuleGroups" :key="'wiz-group-' + groupIdx">
                <query-builder-group
                  :group="group"
                  :fields="queryBuilderFields"
                  :categories-map="categoriesMap"
                  :marques-list="marquesList"
                  :can-remove="true"
                  @remove="removeGroup(groupIdx)"
                  @open-category="openCategoryForRule($event)"
                />
                <div v-if="groupIdx < visualRuleGroups.length - 1" class="text-center my-1">
                  <span class="badge badge-secondary">OU</span>
                </div>
              </div>
            </div>
            <!-- Mode code -->
            <div v-else>
              <ace_editor ref="wizardRulesEditor" :height="120" mode="javascript" />
              <small class="text-muted">Ce code JavaScript doit retourner <strong>true</strong> pour que le produit déclenche le scraping, <strong>false</strong> pour l'ignorer.</small>
            </div>
            <small v-if="visualRulesMode" class="d-block mt-2 text-muted"><strong>Logique :</strong> Les conditions dans un groupe sont combinées avec <strong>ET</strong>, les groupes entre eux avec <strong>OU</strong>.</small>
            <!-- Test conditions -->
            <div class="mt-3 border rounded">
              <div class="p-2 d-flex align-items-center" style="cursor:pointer;gap:8px;background:#f8f9fa" @click="conditionTestOpen = !conditionTestOpen">
                <i :class="conditionTestOpen ? 'fas fa-chevron-down' : 'fas fa-chevron-right'"></i>
                <strong>Tester les conditions</strong>
              </div>
              <div v-if="conditionTestOpen" class="p-2">
                <div :id="testFilterBuilderId" class="mb-2"></div>
                <div class="d-flex align-items-center mb-2 flex-wrap" style="gap:8px">
                  <label class="mb-0">Lignes / page :</label>
                  <b-form-input v-model.number="conditionTestLimit" type="number" size="sm" style="width:70px" min="1" max="500" />
                  <b-button size="sm" variant="outline-secondary" :disabled="conditionTestLoading" @click="conditionTestWhere = ''; initTestFilterBuilder()" v-b-tooltip.hover title="Vider les filtres de test"><i class="fas fa-times"></i></b-button>
                  <b-button size="sm" variant="primary" :disabled="conditionTestLoading" @click="runConditionTest(1)">
                    <b-spinner v-if="conditionTestLoading" small /><span v-else><i class="fas fa-play"></i> Tester</span>
                  </b-button>
                  <b-dropdown size="sm" variant="outline-secondary" no-caret right>
                    <template #button-content><i class="fas fa-columns mr-1"></i>Colonnes<span v-if="testColsVisible.length" class="badge badge-primary ml-1" style="font-size:0.75em">{{ visibleConditionTestCols.length }}</span></template>
                    <b-dropdown-form style="min-width:200px">
                      <div class="d-flex mb-2" style="gap:6px">
                        <b-button size="sm" variant="link" class="p-0 text-primary" style="font-size:0.8em" @click.stop="testColsVisible = allConditionTestCols.map(c => c.key)">Tout</b-button>
                        <b-button size="sm" variant="link" class="p-0 text-secondary" style="font-size:0.8em" @click.stop="testColsVisible = []">Aucun</b-button>
                      </div>
                      <b-form-checkbox v-for="col in allConditionTestCols" :key="col.key" :value="col.key" v-model="testColsVisible" class="mb-1" style="font-size:0.85em">{{ col.label }}</b-form-checkbox>
                    </b-dropdown-form>
                  </b-dropdown>
                  <span v-if="conditionTestWhere" class="badge badge-info ml-1" v-b-tooltip.hover :title="conditionTestWhere"><i class="fas fa-filter"></i> Filtre actif</span>
                  <template v-if="conditionTestRows.length">
                    <div class="ml-2 d-flex align-items-center" style="gap:4px">
                      <b-button size="sm" variant="outline-secondary" :disabled="conditionTestPage <= 1 || conditionTestLoading" @click="runConditionTest(conditionTestPage - 1)"><i class="fas fa-chevron-left"></i></b-button>
                      <small class="text-muted">Page {{ conditionTestPage }} / {{ Math.ceil(conditionTestTotal / conditionTestLimit) }}</small>
                      <b-button size="sm" variant="outline-secondary" :disabled="conditionTestPage >= Math.ceil(conditionTestTotal / conditionTestLimit) || conditionTestLoading" @click="runConditionTest(conditionTestPage + 1)"><i class="fas fa-chevron-right"></i></b-button>
                      <small class="text-muted ml-2">{{ conditionTestRows.filter(r => r._passes).length }} / {{ conditionTestRows.length }} passent</small>
                    </div>
                  </template>
                </div>
                <div v-if="conditionTestRows.length" class="table-responsive mt-2">
                  <table class="table table-sm table-bordered table-hover">
                    <thead class="thead-light">
                      <tr>
                        <th>Résultat</th><th>id</th><th>Nom</th>
                        <th v-for="col in visibleConditionTestCols" :key="col.key">{{ col.label }}</th>
                        <th v-if="ds.showDataKeys" style="width:80px;text-align:center">Données</th>
                        <th v-if="ds.rowActions && ds.rowActions.length" style="width:50px;text-align:center">Données</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="row in conditionTestRows" :key="row.id" :class="row._passes ? 'table-success' : 'table-danger'">
                        <td style="text-align:center">
                          <span v-if="row._passes" class="badge badge-success">✓ Déclenche</span>
                          <span v-else class="badge badge-danger">✗ Ignoré</span>
                        </td>
                        <td>{{ row.id }}</td>
                        <td>{{ getNom(row) }}</td>
                        <td v-for="col in visibleConditionTestCols" :key="col.key">
                          <template v-if="col.fromData">{{ parseDataField(row.data, col.fromData) }}</template>
                          <template v-else-if="col.key === 'categories'"><span v-for="catId in (typeof row.categories === 'string' ? JSON.parse(row.categories || '[]') : (row.categories || []))" :key="catId" class="badge badge-secondary mr-1" style="font-size:0.75em">{{ categoriesMap[catId] || catId }}</span></template>
                          <template v-else-if="col.key === 'categorie_principale'">{{ categoriesMap[row.categorie_principale] || row.categorie_principale || '—' }}</template>
                          <template v-else-if="col.key === 'default_category'">{{ categoriesMap[row.default_category] || row.default_category || '—' }}</template>
                          <template v-else-if="col.key === 'nom'">{{ getNom(row) }}</template>
                          <template v-else-if="col.key === 'data'"><small class="text-muted" style="font-size:0.75em">{{ row.data ? '...' : '—' }}</small></template>
                          <template v-else>{{ row[col.key] }}</template>
                        </td>
                        <td v-if="ds.showDataKeys" style="text-align:center;vertical-align:middle">
                          <b-button v-if="row.data" size="sm" variant="outline-info" title="Voir les données" @click="conditionTestDataRow = row; conditionTestDataVisible = true"><i class="fas fa-table"></i></b-button>
                          <span v-else class="text-muted">—</span>
                        </td>
                        <td v-if="ds.rowActions && ds.rowActions.length" @click.stop style="text-align:center;vertical-align:middle;white-space:nowrap">
                          <b-button v-for="action in ds.rowActions" :key="action.key" size="sm" :variant="action.variant || 'outline-secondary'" :title="action.label" @click="action.onClick(row)"><i v-if="action.icon" :class="action.icon"></i><span v-else>{{ action.label }}</span></b-button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <!-- Étape 3 : Schema JSON -->
          <div v-show="wizardStep === 3" class="wizard-step">
            <h5><i class="fas fa-list text-primary mr-2"></i>Informations à récupérer</h5>
            <p class="text-muted">Définissez les champs que le scraper doit extraire pour chaque produit trouvé.</p>
            <json_schema_editor ref="wizardSchemaEditor" :show_required="false" :hide_list="true" />
            <b-form-group label="Prompt" class="mt-3" description="Instruction transmise à l'IA pour guider l'extraction des données.">
              <b-form-textarea v-model="configForm.prompt" size="sm" rows="3" placeholder="ex: Extraire uniquement le prix soldé si présent, sinon le prix normal." />
            </b-form-group>
          </div>

          <!-- Étape 4 : Identification / URL de recherche -->
          <div v-show="wizardStep === 4" class="wizard-step">

            <b-form-group label="Mode de recherche" class="mb-4">
              <div class="d-flex" style="gap:12px">
                <div class="wizard-choice-card flex-fill" :class="configForm.search_mode !== 'site_search' ? 'selected' : ''" @click="configForm.search_mode = 'query'" style="cursor:pointer">
                  <i class="fas fa-globe fa-2x text-primary mb-2"></i>
                  <strong>Recherche web</strong>
                  <p class="text-muted mb-0 mt-1" style="font-size:0.85rem">Recherche sur le web via une query paramétrée avec les données du produit</p>
                </div>
                <div class="wizard-choice-card flex-fill" :class="configForm.search_mode === 'site_search' ? 'selected' : ''" @click="configForm.search_mode = 'site_search'" style="cursor:pointer">
                  <i class="fas fa-search fa-2x text-info mb-2"></i>
                  <strong>Moteur du site</strong>
                  <p class="text-muted mb-0 mt-1" style="font-size:0.85rem">Utilise le moteur de recherche interne du site avec une URL paramétrée</p>
                </div>
              </div>
            </b-form-group>

            <!-- Query (mode recherche web) -->
            <b-form-group v-if="configForm.search_mode !== 'site_search'" label="Query de recherche (générée automatiquement)">
              <div class="d-flex align-items-start" style="gap:8px">
                <b-form-textarea v-model="configForm.query" ref="queryInputWiz" size="sm" rows="2" placeholder="ex: site:monsite.fr ${data.nom} ${data.reference}" style="flex:1;font-family:monospace;font-size:0.88em" @blur="queryCursorPos = $event.target.selectionStart" @click="queryCursorPos = $event.target.selectionStart" @keyup="queryCursorPos = $event.target.selectionStart" />
                <div class="d-flex flex-column" style="gap:4px">
                  <b-dropdown size="sm" variant="outline-primary" right no-caret>
                    <template #button-content><i class="fas fa-code mr-1"></i>+ Variable</template>
                    <b-dropdown-item v-for="opt in queryVariableOptions" :key="'wqvo-' + (opt.value || opt.column + '_' + opt.lang)" @click="insertQueryVariable(opt)">
                      <span :class="opt.segType === 'caracteristique' ? 'text-secondary' : 'text-primary'" style="font-family:monospace;font-size:0.88em">{{ opt.segType === 'caracteristique' ? '📋 ' + opt.column + ' (' + opt.lang + ')' : '${data.' + opt.value + '}' }}</span>
                    </b-dropdown-item>
                    <b-dropdown-divider v-if="queryVariableOptions.length" />
                    <b-dropdown-item v-if="ds.showCaracteristiques !== false" @click="openQueryCaractModal">📋 Autre caractéristique...</b-dropdown-item>
                    <b-dropdown-item v-if="ds.showDataKeys" @click="openQueryDataKeyModal">🗂 Autre clé data...</b-dropdown-item>
                  </b-dropdown>
                  <b-button size="sm" variant="outline-info" v-b-tooltip.hover title="Générer depuis domaine et identification" @click="generateQuery"><i class="fas fa-magic"></i></b-button>
                </div>
              </div>
            </b-form-group>

            <!-- URL (mode moteur du site) -->
            <b-form-group v-else label="URL de recherche *" description="Ex: https://example.com/search?q=${data.nom} — les variables seront remplacées par les données du produit">
              <div class="d-flex align-items-start" style="gap:8px">
                <b-form-textarea v-model="configForm.search_url" size="sm" rows="2" placeholder="ex: https://example.com/search?q=${data.nom}&ref=${data.reference}" style="flex:1;font-family:monospace;font-size:0.88em" />
                <b-dropdown size="sm" variant="outline-primary" right no-caret>
                  <template #button-content><i class="fas fa-code mr-1"></i>+ Variable</template>
                  <b-dropdown-item v-for="opt in queryVariableOptions" :key="'wsuo-' + (opt.value || opt.column + '_' + opt.lang)" @click="configForm.search_url += (opt.segType === 'caracteristique' ? '' : '${data.' + opt.value + '}')">
                    <span :class="opt.segType === 'caracteristique' ? 'text-secondary' : 'text-primary'" style="font-family:monospace;font-size:0.88em">{{ opt.segType === 'caracteristique' ? '📋 ' + opt.column + ' (' + opt.lang + ')' : '${data.' + opt.value + '}' }}</span>
                  </b-dropdown-item>
                  <b-dropdown-divider v-if="queryVariableOptions.length" />
                  <b-dropdown-item v-if="ds.showDataKeys" @click="openQueryDataKeyModal">🗂 Autre clé data...</b-dropdown-item>
                </b-dropdown>
              </div>
            </b-form-group>

            <h5><i class="fas fa-fingerprint text-primary mr-2"></i>Clés d'identification</h5>
            <p class="text-muted">Sélectionnez un ou plusieurs champs produit pour construire la recherche. La query sera générée automatiquement.</p>
            <div class="d-flex align-items-center mb-3" style="gap:8px">
              <span class="text-muted" style="font-size:0.9em">Mode :</span>
              <b-form-radio-group v-model="statusIdentMode" buttons button-variant="outline-secondary" size="sm">
                <b-form-radio value="standard">Champs standard</b-form-radio>
                <b-form-radio value="code">Code JS</b-form-radio>
              </b-form-radio-group>
            </div>
            <div v-show="statusIdentMode !== 'standard'" class="mb-3">
              <b-form-group label="Code JS de statut" description="Variable disponible : data (le résultat scraping est dans data.result en JSON). Retourner une string : 'validé', 'à vérifier', 'à vérifier - grande différence', 'non trouvé'">
                <ace_editor ref="wizardStatusCodeEditor" :height="150" mode="javascript" />
              </b-form-group>
            </div>
            <div>
            <b-form-group label="Champs d'identification *">
              <div v-for="(field, idx) in identificationFields" :key="'id-field-' + idx" class="d-flex align-items-center mb-2" style="gap:8px">
                <template v-if="field && typeof field === 'object' && field.type === 'caracteristique'">
                  <span class="badge badge-secondary" style="flex:0 0 auto; font-size:0.85em; padding:5px 7px">📋</span>
                  <b-form-input :value="field.column" @input="updateCaracteristiqueObject(idx, 'column', $event)" size="sm" placeholder="Nom caract. (ex: Auteur)" style="flex:1" />
                  <b-form-select :value="field.lang" @change="updateCaracteristiqueObject(idx, 'lang', $event)" size="sm" :options="(client && client.prestashop && client.prestashop.languages || ['fr']).map(l => ({value: l, text: l.toUpperCase()}))" style="flex:0 0 auto; width:80px" />
                </template>
                <template v-else-if="field && typeof field === 'object' && field.type === 'data_key'">
                  <span class="badge badge-info" style="flex:0 0 auto; font-size:0.85em; padding:5px 7px">🗂</span>
                  <b-form-input :value="field.key" @input="updateDataKeyObject(idx, $event)" size="sm" placeholder="Clé (ex: reference)" style="flex:1" list="import-data-key-suggestions" />
                </template>
                <template v-else>
                  <b-form-select :value="field" @change="onIdentificationTypeChange(idx, $event)" size="sm" :options="[{value:'', text:'— Sélectionner un champ —'}, ...identificationOptions.map(o => ({value: o.value, text: o.text})), ...(ds.predefinedDataKeys || []).map(k => ({value: 'data_preset::' + k, text: '🗂 ' + k})), ...(ds.showCaracteristiques !== false ? [{value:'caracteristiques_custom', text:'📋 Caractéristique...'}] : []), ...(ds.showDataKeys ? [{value:'data_custom', text:'🗂 Clé data...'}] : [])]" style="flex:1" />
                </template>
                <b-button size="sm" variant="link" @click="removeIdentificationField(idx)"><i class="fas fa-trash text-danger"></i></b-button>
              </div>
              <b-button size="sm" variant="outline-primary" @click="addIdentificationField"><i class="fas fa-plus"></i> Ajouter un champ</b-button>
              <small class="text-muted d-block mt-2">Champs produit qui seront injectés dans la requête de recherche</small>
            </b-form-group>
            <b-form-group label="Champs résultat d'identification">
              <div v-for="(field, idx) in identificationResultFields" :key="'id-result-field-' + idx" class="d-flex align-items-center mb-2" style="gap:8px">
                <b-form-select v-model="identificationResultFields[idx]" size="sm" :options="[{value:'', text:'— Sélectionner un champ —'}, ...identificationSchemaProperties.map(p => ({value: p, text: p}))]" style="flex:1" @mousedown.native="refreshSchemaProperties" />
                <b-button size="sm" variant="link" @click="removeIdentificationResultField(idx)"><i class="fas fa-trash text-danger"></i></b-button>
              </div>
              <b-button size="sm" variant="outline-primary" @click="addIdentificationResultField"><i class="fas fa-plus"></i> Ajouter un champ</b-button>
              <small class="text-muted d-block mt-2">Champs du résultat scraping à comparer avec l'identification</small>
            </b-form-group>
            </div>

          </div>


          <!-- Étape 5 : Veille tarifaire -->
          <div v-show="wizardStep === 5" class="wizard-step" v-if="ds.showVeilleTarifaire !== false">
            <h5><i class="fas fa-tag text-primary mr-2"></i>Type de scraping</h5>
            <p class="text-muted">Ce scraper est-il utilisé pour surveiller les prix concurrents&nbsp;?</p>
            <div class="d-flex flex-column" style="gap:12px">
              <div 
                class="wizard-choice-card" 
                :class="configForm.veille_tarifaire ? '' : 'selected'"
                @click="configForm.veille_tarifaire = 0"
              >
                <i class="fas fa-search fa-2x text-primary mb-2"></i>
                <strong>Scraping standard</strong>
                <p class="text-muted mb-0 mt-1" style="font-size:0.85rem">Récupération d'informations produits (fiches, descriptions, caractéristiques...)</p>
              </div>
              <div 
                class="wizard-choice-card"
                :class="configForm.veille_tarifaire ? 'selected' : ''"
                @click="configForm.veille_tarifaire = 1"
              >
                <i class="fas fa-chart-line fa-2x text-success mb-2"></i>
                <strong>Veille tarifaire</strong>
                <p class="text-muted mb-0 mt-1" style="font-size:0.85rem">Surveillance des prix chez les concurrents pour comparaison</p>
              </div>
            </div>
          </div>

          <!-- Étape 6 : Projet -->
          <div v-show="wizardStep === (ds.showVeilleTarifaire === false ? 5 : 6)" class="wizard-step">
            <h5><i class="fas fa-briefcase text-primary mr-2"></i>Projet (optionnel)</h5>
            <p class="text-muted">Associez ce scraper à un projet pour l'identifier dans les listings.</p>
            <b-form-group label="Projet">
              <b-form-input v-model="configForm.projet" size="sm" list="scraping-projects-list" placeholder="ex: Scraping - search" autocomplete="off" />
              <datalist id="scraping-projects-list">
                <option v-for="p in projectsList" :key="p.libelle" :value="p.libelle" />
              </datalist>
            </b-form-group>
            <div class="mt-4 p-3 border rounded bg-light">
              <strong class="d-block mb-2"><i class="fas fa-check-circle text-success mr-1"></i>Récapitulatif</strong>
              <table class="table table-sm mb-0">
                <tr><th style="width:140px">Domaine</th><td>{{ configForm.domain }}</td></tr>
                <tr v-if="configForm.conditions"><th>Stratégie</th><td><span class="badge badge-secondary">{{ configForm.conditions }}</span></td></tr>
                <tr><th>Identification</th><td>{{ configForm.search_mode === 'site_search' ? '—' : (configForm.identification || '—') }}</td></tr>
                <tr><th>{{ configForm.search_mode === 'site_search' ? 'URL recherche' : 'Query' }}</th><td><code>{{ configForm.search_mode === 'site_search' ? (configForm.search_url || '—') : (configForm.query || '—') }}</code></td></tr>
                <tr><th>Conditions</th><td>{{ wizardSkipConditions ? 'Aucune (tous les résultats)' : (visualRuleGroups.length + ' groupe(s)') }}</td></tr>
                <tr v-if="ds.showVeilleTarifaire !== false"><th>Type</th><td>{{ configForm.veille_tarifaire ? 'Veille tarifaire' : 'Scraping standard' }}</td></tr>
                <tr><th>Projet</th><td>{{ configForm.projet || '—' }}</td></tr>
              </table>
            </div>
            <!-- Tester le scraping -->
            <div class="mt-3 border rounded">
              <div class="p-2 d-flex align-items-center" style="cursor:pointer;gap:8px;background:#f8f9fa" @click="scrapTestOpen = !scrapTestOpen">
                <i :class="scrapTestOpen ? 'fas fa-chevron-down' : 'fas fa-chevron-right'"></i>
                <strong>Tester le scraping</strong>
                <span v-if="!scenarioUid" class="badge badge-warning ml-1">Scénario introuvable</span>
                <small class="text-muted ml-2"><i class="fas fa-info-circle"></i> La configuration est automatiquement enregistrée en <span class="badge badge-warning">test</span> avant chaque test</small>
              </div>
              <div v-if="scrapTestOpen" class="p-2">
                <div :id="scrapTestFilterBuilderId" class="mb-2"></div>
                <div class="d-flex align-items-center mb-2 flex-wrap" style="gap:8px">
                  <label class="mb-0">Lignes / page :</label>
                  <b-form-input v-model.number="scrapTestLimit" type="number" size="sm" style="width:70px" min="1" max="100" />
                  <b-button size="sm" variant="outline-secondary" :disabled="scrapTestLoading" @click="scrapTestWhere = ''; initScrapTestFilterBuilder()" v-b-tooltip.hover title="Vider les filtres"><i class="fas fa-times"></i></b-button>
                  <b-dropdown size="sm" variant="outline-secondary" no-caret right>
                    <template #button-content><i class="fas fa-columns mr-1"></i>Colonnes<span v-if="testColsVisible.length" class="badge badge-primary ml-1" style="font-size:0.75em">{{ visibleConditionTestCols.length }}</span></template>
                    <b-dropdown-form style="min-width:180px">
                      <b-button size="sm" variant="link" class="p-0 text-primary" style="font-size:0.8em" @click.stop="testColsVisible = allConditionTestCols.map(c => c.key)">Tout</b-button>
                      <b-button size="sm" variant="link" class="p-0 text-secondary" style="font-size:0.8em" @click.stop="testColsVisible = []">Aucun</b-button>
                      <b-form-checkbox v-for="col in allConditionTestCols" :key="col.key" :value="col.key" v-model="testColsVisible" class="mb-1" style="font-size:0.85em">{{ col.label }}</b-form-checkbox>
                    </b-dropdown-form>
                  </b-dropdown>
                  <b-button size="sm" variant="secondary" :disabled="scrapTestLoading" @click="runScrapTestSearch(1)">
                    <b-spinner v-if="scrapTestLoading" small /><span v-else><i class="fas fa-search"></i> Chercher</span>
                  </b-button>
                  <span v-if="scrapTestWhere" class="badge badge-info ml-1" v-b-tooltip.hover :title="scrapTestWhere"><i class="fas fa-filter"></i> Filtre actif</span>
                  <template v-if="scrapTestRows.length">
                    <div class="ml-2 d-flex align-items-center" style="gap:4px">
                      <b-button size="sm" variant="outline-secondary" :disabled="scrapTestPage <= 1 || scrapTestLoading" @click="runScrapTestSearch(scrapTestPage - 1)"><i class="fas fa-chevron-left"></i></b-button>
                      <small class="text-muted">Page {{ scrapTestPage }} / {{ Math.ceil(scrapTestTotal / scrapTestLimit) }}</small>
                      <b-button size="sm" variant="outline-secondary" :disabled="scrapTestPage >= Math.ceil(scrapTestTotal / scrapTestLimit) || scrapTestLoading" @click="runScrapTestSearch(scrapTestPage + 1)"><i class="fas fa-chevron-right"></i></b-button>
                    </div>
                  </template>
                </div>
                <div v-if="scrapTestRows.length" class="mb-2">
                  <!-- Toolbar multiselect -->
                  <div class="d-flex align-items-center mb-2 flex-wrap" style="gap:8px">
                    <b-form-checkbox
                      :checked="scrapTestApplicableRows.length > 0 && scrapTestApplicableRows.every(r => scrapTestSelectedIds.includes(r.id))"
                      :indeterminate="scrapTestSelectedIds.length > 0 && !scrapTestApplicableRows.every(r => scrapTestSelectedIds.includes(r.id))"
                      @change="toggleScrapTestAll"
                    />
                    <span class="text-muted" style="font-size:0.9em">{{ scrapTestSelectedIds.length }} sélectionné(s) · {{ scrapTestApplicableRows.length }} applicable(s) sur {{ scrapTestRows.length }}</span>
                    <b-button size="sm" variant="warning" class="ml-auto" :disabled="!scrapTestSelectedApplicable.length || scrapTestRunningBulk || !scenarioUid" @click="launchBulkTest">
                      <b-spinner v-if="scrapTestRunningBulk" small class="mr-1" />
                      <i v-else class="fas fa-flask mr-1"></i> Tester la sélection ({{ scrapTestSelectedApplicable.length }})
                    </b-button>
                  </div>
                  <!-- Table -->
                  <div class="table-responsive mb-2">
                    <table class="table table-sm table-bordered table-hover">
                      <thead class="thead-light">
                        <tr>
                          <th style="width:32px"></th>
                          <th style="width:80px;text-align:center">Applicable</th>
                          <th>id</th>
                          <th v-for="col in (ds.listingColumns || [{key:'id_cms',label:'id_cms'},{key:'status',label:'Statut'}])" :key="col.key">{{ col.label }}</th>
                          <th v-if="ds.showNomColumn !== false">Nom</th>
                          <th v-for="col in visibleConditionTestCols" :key="'st-'+col.key">{{ col.label }}</th>
                          <th v-if="globalGroupFn || ds.configType === 'scraping_file'">Groupe</th>
                          <th v-if="ds.rowActions && ds.rowActions.length" style="width:50px;text-align:center">Données</th>
                          <th style="width:120px;text-align:center">Résultat</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="row in scrapTestRows" :key="row.id"
                            :class="[!scrapTestPassesCurrentConfig(row) ? 'text-muted' : '', scrapTestSelectedProduct && scrapTestSelectedProduct.id === row.id ? 'scrap-row-active' : scrapTestSelectedIds.includes(row.id) ? 'scrap-row-selected' : '']"
                            style="cursor:pointer"
                            @click="scrapTestSelectedProduct = row; scrapTestStatus = ''; scrapTestResult = null">
                          <td @click.stop style="text-align:center;vertical-align:middle">
                            <b-form-checkbox :value="row.id" v-model="scrapTestSelectedIds" :disabled="!scrapTestPassesCurrentConfig(row)" />
                          </td>
                          <td style="text-align:center;vertical-align:middle">
                            <span v-if="scrapTestPassesCurrentConfig(row)" class="badge badge-success" style="font-size:0.75em">✓ Oui</span>
                            <span v-else class="badge badge-secondary" style="font-size:0.75em;opacity:0.7">✗ Non</span>
                          </td>
                          <td>{{ row.id }}</td>
                          <td v-for="col in (ds.listingColumns || [{key:'id_cms',label:'id_cms'},{key:'status',label:'Statut'}])" :key="col.key" style="vertical-align:middle">
                            <template v-if="col.key === 'groupe'">
                              <span v-if="row[col.key]" class="badge badge-info" style="font-size:0.8em">{{ row[col.key] }}</span>
                              <span v-else class="text-muted">—</span>
                            </template>
                            <template v-else>{{ col.render ? col.render(row[col.key]) : row[col.key] }}</template>
                          </td>
                          <td v-if="ds.showNomColumn !== false" style="vertical-align:middle">{{ getNom(row) }}</td>
                          <td v-for="col in visibleConditionTestCols" :key="'st-'+col.key" style="vertical-align:middle">
                            <template v-if="col.key === 'categories'"><span v-for="catId in (typeof row.categories === 'string' ? JSON.parse(row.categories || '[]') : (row.categories || []))" :key="catId" class="badge badge-secondary mr-1" style="font-size:0.75em">{{ categoriesMap[catId] || catId }}</span></template>
                            <template v-else-if="col.key === 'categorie_principale'">{{ categoriesMap[row.categorie_principale] || row.categorie_principale || '—' }}</template>
                            <template v-else-if="col.fromData">{{ parseDataField(row.data, col.fromData) }}</template>
                            <template v-else>{{ row[col.key] }}</template>
                          </td>
                          <td v-if="globalGroupFn || ds.configType === 'scraping_file'" style="vertical-align:middle;text-align:center">
                            <span v-if="row.groupe" class="badge badge-info" style="font-size:0.8em">{{ row.groupe }}</span>
                            <span v-else class="text-muted" style="font-size:0.75em">—</span>
                          </td>
                          <td v-if="ds.rowActions && ds.rowActions.length" @click.stop style="text-align:center;vertical-align:middle;white-space:nowrap">
                            <b-button v-for="action in ds.rowActions" :key="action.key" size="sm" :variant="action.variant || 'outline-secondary'" :title="action.label" @click="action.onClick(row)">
                              <i v-if="action.icon" :class="action.icon"></i>
                              <span v-else>{{ action.label }}</span>
                            </b-button>
                          </td>
                          <td style="text-align:center;vertical-align:middle;white-space:nowrap">
                            <span v-if="scrapTestResults[row.id]" class="badge" :class="statutSmdClass(scrapTestResults[row.id].statut_smd)" style="font-size:0.7em">{{ scrapTestResults[row.id].statut_smd || '?' }}</span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
                <div v-if="scrapTestSelectedProduct" class="border rounded p-2 mt-2">
                  <div class="mb-2 d-flex align-items-center" style="gap:8px">
                    <strong>Test unitaire</strong>
                    <span class="badge badge-primary">{{ scrapTestSelectedProduct.id }}</span>
                    <span>{{ getNom(scrapTestSelectedProduct) }}</span>
                    <b-button size="sm" variant="link" class="text-danger p-0 ml-auto" @click="scrapTestSelectedProduct = null; scrapTestStatus = ''"><i class="fas fa-times"></i></b-button>
                  </div>
                  <small class="text-muted d-block mb-2">Lance le scraper sur ce seul produit et affiche le résultat brut ci-dessous.</small>
                  <div class="d-flex align-items-center flex-wrap mb-2" style="gap:8px">
                    <label class="mb-0">URL (optionnel) :</label>
                    <b-form-input v-model="scrapTestUrl" size="sm" style="min-width:260px;flex:1" placeholder="Laisser vide pour lancer la recherche" />
                  </div>
                  <div class="d-flex align-items-center flex-wrap" style="gap:8px">
                    <b-button v-if="!scrapTestLaunching" size="sm" variant="warning" :disabled="!configForm.domain || !scenarioUid" @click="launchScrapTest">
                      <i class="fas fa-flask"></i> Lancer le test
                    </b-button>
                    <span v-if="scrapTestLaunching" class="text-info"><b-spinner small class="mr-1" /> En cours...</span>
                    <span v-if="scrapTestStatus === 'ok'" class="badge badge-success"><i class="fas fa-check"></i> Terminé ✓</span>
                    <span v-if="scrapTestStatus === 'ko'" class="badge badge-danger"><i class="fas fa-times"></i> Erreur ✗</span>
                    <span v-if="scrapTestStatus === 'error'" class="badge badge-danger"><i class="fas fa-exclamation-triangle"></i> Erreur</span>
                  </div>
                  <div v-if="scrapTestCurrentResult" class="mt-3 border rounded p-2" style="background:#f8f9fa">
                    <table class="table table-sm table-bordered mb-2">
                      <tbody>
                        <tr>
                          <th style="width:160px">URL</th>
                          <td>
                            <a v-if="scrapTestCurrentResult.url" :href="scrapTestCurrentResult.url" target="_blank" rel="noopener" style="word-break:break-all;font-size:0.85em">{{ scrapTestCurrentResult.url }}</a>
                            <span v-else class="text-muted">—</span>
                          </td>
                        </tr>
                        <tr>
                          <th>Statut SMD</th>
                          <td><span class="badge" :class="statutSmdClass(scrapTestCurrentResult.statut_smd)">{{ scrapTestCurrentResult.statut_smd || '—' }}</span></td>
                        </tr>
                        <tr>
                          <th>Identification</th>
                          <td><small>{{ scrapTestCurrentResult.identification || '—' }}</small></td>
                        </tr>
                        <tr>
                          <th>Résultat identification</th>
                          <td><small>{{ scrapTestCurrentResult.identification_result || '—' }}</small></td>
                        </tr>
                      </tbody>
                    </table>
                    <template v-if="scrapTestCurrentResult.result">
                      <strong class="d-block mb-1" style="font-size:0.9em">Résultat</strong>
                      <pre class="p-2 bg-white border rounded mb-0" style="max-height:250px;overflow:auto;font-size:0.78em;white-space:pre-wrap">{{ formatResult(scrapTestCurrentResult.result) }}</pre>
                    </template>
                    <div class="mt-2 d-flex justify-content-end">
                      <b-button size="sm" variant="success" :disabled="scrapTestValidatingId === scrapTestSelectedProduct.id" @click="validateSingleResult(scrapTestSelectedProduct)">
                        <b-spinner v-if="scrapTestValidatingId === scrapTestSelectedProduct.id" small class="mr-1" />
                        <i v-else class="fas fa-check mr-1"></i> Valider — enregistrer en base
                      </b-button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Navigation du wizard -->
          <div class="d-flex justify-content-between mt-4">
            <b-button v-if="wizardStep > 1" variant="outline-secondary" @click="wizardPrev">
              <i class="fas fa-arrow-left"></i> Précédent
            </b-button>
            <div v-else></div>
            <div class="d-flex" style="gap:8px">
              <b-button variant="outline-secondary" @click="configForm.id ? exitWizardToAdvanced() : wizardCancel()">
                {{ configForm.id ? 'Mode avancé' : 'Retour au menu' }}
              </b-button>
              <b-button v-if="wizardStep < wizardTotalSteps" variant="primary" @click="wizardNext" :disabled="!wizardStepValid">
                Suivant <i class="fas fa-arrow-right"></i>
              </b-button>
              <template v-else>
                <b-button variant="success" @click="configForm.id ? saveConfig() : saveConfigAndContinue()" :disabled="configSaving">
                  <b-spinner v-if="configSaving" small class="mr-1" />
                  <i v-else class="fas fa-save mr-1"></i> {{ configForm.id ? 'Sauvegarder' : 'Créer le scraper' }}
                </b-button>
                <b-button v-if="siblingConfigs.length > 1" variant="outline-info" :disabled="syncingSiblings || !configForm.id" @click="syncToSiblings" v-b-tooltip.hover title="Synchroniser schéma, prompt et projet sur toutes les configs du même domaine/groupe">
                  <b-spinner v-if="syncingSiblings" small class="mr-1" /><i v-else class="fas fa-link mr-1"></i>Sync configs
                </b-button>
                <b-dropdown v-if="configForm.id" size="sm" variant="outline-secondary" right :disabled="configSaving">
                  <template #button-content><i class="fas fa-code-branch"></i></template>
                  <b-dropdown-item @click="versionSaveModal = true"><i class="fas fa-tag mr-1"></i>Nouvelle version</b-dropdown-item>
                  <b-dropdown-divider v-if="!configForm.production" />
                  <b-dropdown-item v-if="!configForm.production" @click="promoteToProduction"><i class="fas fa-rocket mr-1 text-success"></i>{{ configForm.version === -1 ? 'Passer en production (nouvelle version)' : 'Passer en production' }}</b-dropdown-item>
                </b-dropdown>
              </template>
            </div>
          </div>
        </div>
        <!-- Fin wizard -->

        <!-- Formulaire complet d'édition (modification ou après wizard) -->
        <div v-if="!wizardActive">
        <div class="row">
          <div class="col-md-4">
            <b-form-group label="Domaine *" label-size="sm">
              <b-form-input v-model="configForm.domain" size="sm" placeholder="ex: monsite.fr" />
            </b-form-group>
          </div>
          <div class="col-md-4">
            <b-form-group label="Groupe" label-size="sm" description="Optionnel — générique si vide">
              <b-form-input v-model="configForm.groupe" size="sm" :list="'adv-groupes-list'" placeholder="ex: high-tech, vêtements" autocomplete="off" />
              <datalist :id="'adv-groupes-list'">
                <option v-for="g in groupesList" :key="g" :value="g" />
              </datalist>
            </b-form-group>
          </div>
          <div class="col-md-4">
            <b-form-group label="Projet" label-size="sm">
              <b-form-input v-model="configForm.projet" size="sm" list="scraping-projects-list" placeholder="ex: Scraping - search" autocomplete="off" />
              <datalist id="scraping-projects-list">
                <option v-for="p in projectsList" :key="p.libelle" :value="p.libelle" />
              </datalist>
            </b-form-group>
          </div>
        </div>
        <div class="row">
          <div class="col-md-4">
            <b-form-group label="Conditions (label de routage)" label-size="sm" description="Label identifiant la stratégie — laisser vide pour config par défaut.">
              <b-form-input v-model="configForm.conditions" size="sm" placeholder="ex: ean, code fabricant, nom" />
            </b-form-group>
          </div>
          <div v-if="siblingConfigs.length > 1" class="col-md-2">
            <b-form-group label="Priorité" label-size="sm" description="Modifiable par glisser-déposer dans le listing des configs">
              <b-form-input :value="(configForm.priority || 0) + 1" type="number" size="sm" readonly plaintext />
            </b-form-group>
          </div>
        </div>
        <div class="row mb-2">
          <div class="col-md-6 d-flex align-items-center" style="padding-top:0.25rem; gap:16px">
            <b-form-checkbox v-model="configForm.active" :value="1" :unchecked-value="0">
              Actif
            </b-form-checkbox>
            <b-form-checkbox v-if="ds.showVeilleTarifaire !== false" v-model="configForm.veille_tarifaire" :value="1" :unchecked-value="0">
              Veille tarifaire
            </b-form-checkbox>
          </div>
        </div>
        <div class="section-header mt-2 mb-3">
          <h6 class="mb-0"><i class="fas fa-bolt text-warning mr-2"></i>Conditions de déclenchement</h6>
          <small class="text-muted">Évaluées <strong>au tout début du scénario</strong> — retourner <strong>true</strong> pour lancer le scraping, <strong>false</strong> pour ignorer ce produit</small>
        </div>
        <b-form-group label-size="sm">
          <div class="d-flex align-items-center mb-2" style="gap:8px">
            <b-form-radio-group v-model="visualRulesModeKey" buttons button-variant="outline-secondary" size="sm">
              <b-form-radio value="visual"><i class="fas fa-sliders-h mr-1"></i>Mode visuel</b-form-radio>
              <b-form-radio value="code"><i class="fas fa-code mr-1"></i>Mode code</b-form-radio>
            </b-form-radio-group>
          </div>
          
          <!-- Mode visuel -->
          <div v-if="visualRulesMode">
            <div class="query-builder border rounded p-2" style="background:#fff">
              <div v-if="!visualRuleGroups.length" class="text-center text-muted py-2" style="font-size:0.9em">
                <i class="fas fa-info-circle mr-1"></i> Aucune condition — tous les résultats seront traités.
                <b-button size="sm" variant="outline-primary" class="ml-2" @click="addGroup"><i class="fas fa-plus mr-1"></i>Ajouter une condition</b-button>
              </div>
              <div v-for="(group, groupIdx) in visualRuleGroups" :key="'group-' + groupIdx">
                <query-builder-group
                  :group="group"
                  :fields="queryBuilderFields"
                  :categories-map="categoriesMap"
                  :marques-list="marquesList"
                  :can-remove="true"
                  @remove="removeGroup(groupIdx)"
                  @open-category="openCategoryForRule($event)"
                />
                <div v-if="groupIdx < visualRuleGroups.length - 1" class="text-center my-2">
                  <span class="badge badge-secondary">OU</span>
                </div>
              </div>
            </div>
            <small class="d-block mt-3 text-muted">
              <strong>Logique :</strong> Les conditions dans un même groupe sont combinées avec <strong>ET</strong>.
              Les groupes sont combinés avec <strong>OU</strong>.
            </small>
          </div>
          
          <div v-else>
            <!-- Mode code -->
            <ace_editor ref="rulesEditor" :height="120" mode="javascript" />
            <small class="text-muted">Ce code JavaScript doit retourner <strong>true</strong> pour poursuivre le scraping, sinon <strong>false</strong>.</small>
          </div>
          <!-- Test conditions -->
          <div class="mt-3 border rounded">
            <div class="p-2 d-flex align-items-center" style="cursor:pointer;gap:8px;background:#f8f9fa" @click="conditionTestOpen = !conditionTestOpen">
              <i :class="conditionTestOpen ? 'fas fa-chevron-down' : 'fas fa-chevron-right'"></i>
              <strong>Tester les conditions</strong>
            </div>
            <div v-if="conditionTestOpen" class="p-2">
              <div :id="testFilterBuilderId" class="mb-2"></div>
              <div class="d-flex align-items-center mb-2 flex-wrap" style="gap:8px">
                <label class="mb-0">Lignes / page :</label>
                <b-form-input v-model.number="conditionTestLimit" type="number" size="sm" style="width:70px" min="1" max="500" />
                <b-button size="sm" variant="outline-secondary" :disabled="conditionTestLoading" @click="conditionTestWhere = ''; initTestFilterBuilder()" v-b-tooltip.hover title="Vider les filtres de test"><i class="fas fa-times"></i></b-button>
                <b-button size="sm" variant="primary" :disabled="conditionTestLoading" @click="runConditionTest(1)">
                  <b-spinner v-if="conditionTestLoading" small /><span v-else><i class="fas fa-play"></i> Tester</span>
                </b-button>
                <b-dropdown size="sm" variant="outline-secondary" no-caret right>
                  <template #button-content><i class="fas fa-columns mr-1"></i>Colonnes<span v-if="testColsVisible.length" class="badge badge-primary ml-1" style="font-size:0.75em">{{ visibleConditionTestCols.length }}</span></template>
                  <b-dropdown-form style="min-width:200px">
                    <div class="d-flex mb-2" style="gap:6px">
                      <b-button size="sm" variant="link" class="p-0 text-primary" style="font-size:0.8em" @click.stop="testColsVisible = allConditionTestCols.map(c => c.key)">Tout</b-button>
                      <b-button size="sm" variant="link" class="p-0 text-secondary" style="font-size:0.8em" @click.stop="testColsVisible = []">Aucun</b-button>
                    </div>
                    <b-form-checkbox v-for="col in allConditionTestCols" :key="col.key" :value="col.key" v-model="testColsVisible" class="mb-1" style="font-size:0.85em">{{ col.label }}</b-form-checkbox>
                  </b-dropdown-form>
                </b-dropdown>
                <span v-if="conditionTestWhere" class="badge badge-info ml-1" v-b-tooltip.hover :title="conditionTestWhere"><i class="fas fa-filter"></i> Filtre actif</span>
                <template v-if="conditionTestRows.length">
                  <div class="ml-2 d-flex align-items-center" style="gap:4px">
                    <b-button size="sm" variant="outline-secondary" :disabled="conditionTestPage <= 1 || conditionTestLoading" @click="runConditionTest(conditionTestPage - 1)"><i class="fas fa-chevron-left"></i></b-button>
                    <small class="text-muted">Page {{ conditionTestPage }} / {{ Math.ceil(conditionTestTotal / conditionTestLimit) }}</small>
                    <b-button size="sm" variant="outline-secondary" :disabled="conditionTestPage >= Math.ceil(conditionTestTotal / conditionTestLimit) || conditionTestLoading" @click="runConditionTest(conditionTestPage + 1)"><i class="fas fa-chevron-right"></i></b-button>
                    <small class="text-muted ml-2">{{ conditionTestRows.filter(r => r._passes).length }} / {{ conditionTestRows.length }} passent</small>
                  </div>
                </template>
              </div>
              <div v-if="conditionTestRows.length" class="table-responsive mt-2">
                <table class="table table-sm table-bordered table-hover">
                  <thead class="thead-light">
                    <tr>
                      <th>Résultat</th><th>id</th><th>Nom</th>
                      <th v-for="col in visibleConditionTestCols" :key="col.key">{{ col.label }}</th>
                      <th v-if="ds.showDataKeys" style="width:80px;text-align:center">Données</th>
                      <th v-if="ds.rowActions && ds.rowActions.length" style="width:50px;text-align:center">Données</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="row in conditionTestRows" :key="row.id" :class="row._passes ? 'table-success' : 'table-danger'">
                      <td style="text-align:center">
                        <span v-if="row._passes" class="badge badge-success">✓ Passe</span>
                        <span v-else class="badge badge-danger">✗ Exclu</span>
                      </td>
                      <td>{{ row.id }}</td>
                      <td>{{ getNom(row) }}</td>
                      <td v-for="col in visibleConditionTestCols" :key="col.key">
                        <template v-if="col.fromData">{{ parseDataField(row.data, col.fromData) }}</template>
                        <template v-else-if="col.key === 'categories'"><span v-for="catId in (typeof row.categories === 'string' ? JSON.parse(row.categories || '[]') : (row.categories || []))" :key="catId" class="badge badge-secondary mr-1" style="font-size:0.75em">{{ categoriesMap[catId] || catId }}</span></template>
                        <template v-else-if="col.key === 'categorie_principale'">{{ categoriesMap[row.categorie_principale] || row.categorie_principale || '—' }}</template>
                        <template v-else-if="col.key === 'default_category'">{{ categoriesMap[row.default_category] || row.default_category || '—' }}</template>
                        <template v-else-if="col.key === 'nom'">{{ getNom(row) }}</template>
                        <template v-else-if="col.key === 'data'"><small class="text-muted" style="font-size:0.75em">{{ row.data ? '...' : '—' }}</small></template>
                        <template v-else>{{ row[col.key] }}</template>
                      </td>
                      <td v-if="ds.showDataKeys" style="text-align:center;vertical-align:middle">
                        <b-button v-if="row.data" size="sm" variant="outline-info" title="Voir les données" @click="conditionTestDataRow = row; conditionTestDataVisible = true"><i class="fas fa-table"></i></b-button>
                        <span v-else class="text-muted">—</span>
                      </td>
                      <td v-if="ds.rowActions && ds.rowActions.length" @click.stop style="text-align:center;vertical-align:middle;white-space:nowrap">
                        <b-button v-for="action in ds.rowActions" :key="action.key" size="sm" :variant="action.variant || 'outline-secondary'" :title="action.label" @click="action.onClick(row)"><i v-if="action.icon" :class="action.icon"></i><span v-else>{{ action.label }}</span></b-button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </b-form-group>
        <div class="section-header mt-4 mb-3">
          <h6 class="mb-0"><i class="fas fa-code text-secondary mr-2"></i>Avant la recherche</h6>
          <small class="text-muted">Code exécuté avant la recherche — modifier <code>data</code> si nécessaire</small>
        </div>
        <b-form-group label-size="sm">
          <ace_editor ref="beforeCodeEditor" :height="120" mode="javascript" />
        </b-form-group>
        <div class="section-header mt-4 mb-3">
          <h6 class="mb-0"><i class="fas fa-search text-primary mr-2"></i>Recherche</h6>
          <small class="text-muted">Mode de recherche et query / URL utilisée pour trouver la page produit</small>
        </div>
        <div class="row mb-3">
          <div class="col-md-12">
            <b-form-group label="Mode de recherche" label-size="sm">
              <div class="d-flex" style="gap:12px">
                <div class="wizard-choice-card flex-fill" :class="configForm.search_mode !== 'site_search' ? 'selected' : ''" @click="configForm.search_mode = 'query'" style="cursor:pointer; padding:10px 14px">
                  <i class="fas fa-globe text-primary mr-2"></i><strong>Recherche web</strong>
                  <span class="text-muted ml-2" style="font-size:0.85rem">Query paramétrée avec les données du produit</span>
                </div>
                <div class="wizard-choice-card flex-fill" :class="configForm.search_mode === 'site_search' ? 'selected' : ''" @click="configForm.search_mode = 'site_search'" style="cursor:pointer; padding:10px 14px">
                  <i class="fas fa-search text-info mr-2"></i><strong>Moteur du site</strong>
                  <span class="text-muted ml-2" style="font-size:0.85rem">URL paramétrée vers le moteur interne du site</span>
                </div>
              </div>
            </b-form-group>
          </div>
        </div>
        <b-form-group v-if="configForm.search_mode === 'site_search'" label="URL de recherche *" label-size="sm" description="Ex: https://example.com/search?q=${data.nom} — les variables seront remplacées par les données produit">
          <div class="d-flex align-items-start" style="gap:8px">
            <b-form-textarea
              v-model="configForm.search_url"
              size="sm"
              rows="2"
              placeholder="ex: https://example.com/search?q=${data.nom}&ref=${data.reference}"
              style="flex:1;font-family:monospace;font-size:0.88em"
            />
            <b-dropdown size="sm" variant="outline-primary" right no-caret>
              <template #button-content><i class="fas fa-code mr-1"></i>+ Variable</template>
              <b-dropdown-item v-for="opt in queryVariableOptions" :key="'asuo-' + (opt.value || opt.column + '_' + opt.lang)" @click="configForm.search_url += (opt.segType === 'caracteristique' ? '' : '${data.' + opt.value + '}')">
                <span :class="opt.segType === 'caracteristique' ? 'text-secondary' : 'text-primary'" style="font-family:monospace;font-size:0.88em">{{ opt.segType === 'caracteristique' ? '📋 ' + opt.column + ' (' + opt.lang + ')' : '${data.' + opt.value + '}' }}</span>
              </b-dropdown-item>
              <b-dropdown-divider v-if="queryVariableOptions.length" />
              <b-dropdown-item v-if="ds.showDataKeys" @click="openQueryDataKeyModal">🗂 Autre clé data...</b-dropdown-item>
            </b-dropdown>
          </div>
        </b-form-group>
        <b-form-group v-if="configForm.search_mode !== 'site_search'" label="Query *" label-size="sm">
          <div class="d-flex align-items-start" style="gap:8px">
            <b-form-textarea
              v-model="configForm.query"
              ref="queryInputAdv"
              size="sm"
              rows="2"
              placeholder="ex: site:example.com ${data.nom} ${data.reference}"
              style="flex:1;font-family:monospace;font-size:0.88em"
              @blur="queryCursorPos = $event.target.selectionStart"
              @click="queryCursorPos = $event.target.selectionStart"
              @keyup="queryCursorPos = $event.target.selectionStart"
            />
            <div class="d-flex flex-column" style="gap:4px">
              <b-dropdown size="sm" variant="outline-primary" right no-caret>
                <template #button-content><i class="fas fa-code mr-1"></i>+ Variable</template>
                <b-dropdown-item
                  v-for="opt in queryVariableOptions"
                  :key="'aqvo-' + (opt.value || opt.column + '_' + opt.lang)"
                  @click="insertQueryVariable(opt)"
                >
                  <span :class="opt.segType === 'caracteristique' ? 'text-secondary' : 'text-primary'" style="font-family:monospace;font-size:0.88em">
                    {{ opt.segType === 'caracteristique' ? '📋 ' + opt.column + ' (' + opt.lang + ')' : '${data.' + opt.value + '}' }}
                  </span>
                </b-dropdown-item>
                <b-dropdown-divider v-if="queryVariableOptions.length" />
                <b-dropdown-item v-if="ds.showCaracteristiques !== false" @click="openQueryCaractModal">📋 Autre caractéristique...</b-dropdown-item>
                <b-dropdown-item v-if="ds.showDataKeys" @click="openQueryDataKeyModal">🗂 Autre clé data...</b-dropdown-item>
              </b-dropdown>
              <b-button size="sm" variant="outline-info" v-b-tooltip.hover title="Générer depuis domaine et identification" @click="generateQuery">
                <i class="fas fa-magic"></i>
              </b-button>
            </div>
          </div>
        </b-form-group>
        <div class="section-header mt-4 mb-3">
          <h6 class="mb-0"><i class="fas fa-spider text-info mr-2"></i>Extraction</h6>
          <small class="text-muted">Schéma des données à extraire et instructions pour l'IA</small>
        </div>
        <b-form-group label="Schema result (JSON Schema)" label-size="sm">
          <json_schema_editor ref="schemaEditor" :show_required="false" :hide_list="true" />
        </b-form-group>
        <b-form-group label="Prompt" label-size="sm" description="Instruction transmise à l'IA pour guider l'extraction des données.">
          <b-form-textarea v-model="configForm.prompt" size="sm" rows="3" placeholder="ex: Extraire uniquement le prix soldé si présent, sinon le prix normal." />
        </b-form-group>
        <div class="section-header mt-4 mb-3">
          <h6 class="mb-0"><i class="fas fa-code text-secondary mr-2"></i>Après le scraping</h6>
          <small class="text-muted">Code exécuté après le scraping — modifier <code>data</code> si nécessaire</small>
        </div>
        <b-form-group label-size="sm">
          <ace_editor ref="afterCodeEditor" :height="120" mode="javascript" />
        </b-form-group>
        <div class="section-header mt-4 mb-3">
          <h6 class="mb-0"><i class="fas fa-tag text-success mr-2"></i>Attribution du statut</h6>
          <small class="text-muted">Comparer les colonnes produit avec le résultat scraping, ou utiliser du code JS</small>
        </div>
        <div class="d-flex align-items-center mb-2" style="gap:8px">
          <span class="text-muted" style="font-size:0.9em">Mode identification :</span>
          <b-form-radio-group v-model="statusIdentMode" buttons button-variant="outline-secondary" size="sm">
            <b-form-radio value="standard">Champs standard</b-form-radio>
            <b-form-radio value="code">Code JS</b-form-radio>
          </b-form-radio-group>
        </div>
        <div v-show="statusIdentMode === 'code'" class="mb-3">
          <b-form-group label="Code JS de statut" label-size="sm" description="Variable disponible : data (le résultat scraping est dans data.result en JSON). Retourner une string : 'validé', 'à vérifier', 'à vérifier - grande différence', 'non trouvé'">
            <ace_editor ref="statusCodeEditor" :height="150" mode="javascript" />
          </b-form-group>
        </div>
        <div class="row">
          <div class="col-md-6">
            <b-form-group label="Identification *" label-size="sm">
              <div v-for="(field, idx) in identificationFields" :key="'id-field-adv-' + idx" class="d-flex align-items-center mb-2" style="gap:8px">
                <template v-if="field && typeof field === 'object' && field.type === 'caracteristique'">
                  <span class="badge badge-secondary" style="flex:0 0 auto; font-size:0.85em; padding:5px 7px">📋</span>
                  <b-form-input
                    :value="field.column"
                    @input="updateCaracteristiqueObject(idx, 'column', $event)"
                    size="sm"
                    placeholder="Nom caract. (ex: Auteur)"
                    style="flex:1"
                  />
                  <b-form-select
                    :value="field.lang"
                    @change="updateCaracteristiqueObject(idx, 'lang', $event)"
                    size="sm"
                    :options="(client && client.prestashop && client.prestashop.languages || ['fr']).map(l => ({value: l, text: l.toUpperCase()}))"
                    style="flex:0 0 auto; width:80px"
                  />
                </template>
                <template v-else-if="field && typeof field === 'object' && field.type === 'data_key'">
                  <span class="badge badge-info" style="flex:0 0 auto; font-size:0.85em; padding:5px 7px">🗂</span>
                  <b-form-input
                    :value="field.key"
                    @input="updateDataKeyObject(idx, $event)"
                    size="sm"
                    placeholder="Clé (ex: reference)"
                    style="flex:1"
                    list="import-data-key-suggestions"
                  />
                </template>
                <template v-else>
                  <b-form-select
                    :value="field"
                    @change="onIdentificationTypeChange(idx, $event)"
                    size="sm"
                    :options="[
                      {value:'', text:'— Sélectionner un champ —'},
                      ...identificationOptions.map(o => ({value: o.value, text: o.text})),
                      ...(ds.predefinedDataKeys || []).map(k => ({value: 'data_preset::' + k, text: '🗂 ' + k})),
                      ...(ds.showCaracteristiques !== false ? [{value:'caracteristiques_custom', text:'📋 Caractéristique...'}] : []),
                      ...(ds.showDataKeys ? [{value:'data_custom', text:'🗂 Clé data...'}] : []),
                    ]"
                    style="flex:1"
                  />
                </template>
                <b-button size="sm" variant="link" @click="removeIdentificationField(idx)">
                  <i class="fas fa-trash text-danger"></i>
                </b-button>
              </div>
              <b-button size="sm" variant="outline-primary" @click="addIdentificationField">
                <i class="fas fa-plus"></i> Ajouter un champ
              </b-button>
              <small class="text-muted d-block mt-2">Champs produit utilisés pour identifier le produit lors de la recherche</small>
            </b-form-group>
          </div>
          <div class="col-md-6">
            <b-form-group label="Identification résultat" label-size="sm">
              <div v-for="(field, idx) in identificationResultFields" :key="'id-result-field-adv-' + idx" class="d-flex align-items-center mb-2" style="gap:8px">
                <b-form-select
                  v-model="identificationResultFields[idx]"
                  size="sm"
                  :options="[{value: '', text: '— Sélectionner un champ —'}, ...identificationSchemaProperties.map(p => ({value: p, text: p}))]"
                  style="flex:1"
                  @mousedown.native="refreshSchemaProperties"
                />
                <b-button size="sm" variant="link" @click="removeIdentificationResultField(idx)">
                  <i class="fas fa-trash text-danger"></i>
                </b-button>
              </div>
              <b-button size="sm" variant="outline-primary" @click="addIdentificationResultField">
                <i class="fas fa-plus"></i> Ajouter un champ
              </b-button>
              <small class="text-muted d-block mt-2">Champs du résultat scraping à comparer avec l'identification</small>
            </b-form-group>
          </div>
        </div>
        <!-- Tester le scraping -->
        <div class="mt-3 border rounded">
          <div class="p-2 d-flex align-items-center" style="cursor:pointer;gap:8px;background:#f8f9fa" @click="scrapTestOpen = !scrapTestOpen">
            <i :class="scrapTestOpen ? 'fas fa-chevron-down' : 'fas fa-chevron-right'"></i>
            <strong>Tester le scraping</strong>
            <span v-if="!scenarioUid" class="badge badge-warning ml-1">Scénario introuvable</span>
            <small class="text-muted ml-2"><i class="fas fa-info-circle"></i> La configuration est automatiquement enregistrée en <span class="badge badge-warning">test</span> avant chaque test</small>
          </div>
          <div v-if="scrapTestOpen" class="p-2">
            <div :id="scrapTestFilterBuilderId" class="mb-2"></div>
            <div class="d-flex align-items-center mb-2 flex-wrap" style="gap:8px">
              <label class="mb-0">Lignes / page :</label>
              <b-form-input v-model.number="scrapTestLimit" type="number" size="sm" style="width:70px" min="1" max="100" />
              <b-button size="sm" variant="outline-secondary" :disabled="scrapTestLoading" @click="scrapTestWhere = ''; initScrapTestFilterBuilder()" v-b-tooltip.hover title="Vider les filtres"><i class="fas fa-times"></i></b-button>
              <b-dropdown size="sm" variant="outline-secondary" no-caret right>
                <template #button-content><i class="fas fa-columns mr-1"></i>Colonnes<span v-if="testColsVisible.length" class="badge badge-primary ml-1" style="font-size:0.75em">{{ visibleConditionTestCols.length }}</span></template>
                <b-dropdown-form style="min-width:180px">
                  <b-button size="sm" variant="link" class="p-0 text-primary" style="font-size:0.8em" @click.stop="testColsVisible = allConditionTestCols.map(c => c.key)">Tout</b-button>
                  <b-button size="sm" variant="link" class="p-0 text-secondary" style="font-size:0.8em" @click.stop="testColsVisible = []">Aucun</b-button>
                  <b-form-checkbox v-for="col in allConditionTestCols" :key="col.key" :value="col.key" v-model="testColsVisible" class="mb-1" style="font-size:0.85em">{{ col.label }}</b-form-checkbox>
                </b-dropdown-form>
              </b-dropdown>
              <b-button size="sm" variant="secondary" :disabled="scrapTestLoading" @click="runScrapTestSearch(1)">
                <b-spinner v-if="scrapTestLoading" small /><span v-else><i class="fas fa-search"></i> Chercher</span>
              </b-button>
              <span v-if="scrapTestWhere" class="badge badge-info ml-1" v-b-tooltip.hover :title="scrapTestWhere"><i class="fas fa-filter"></i> Filtre actif</span>
              <template v-if="scrapTestRows.length">
                <div class="ml-2 d-flex align-items-center" style="gap:4px">
                  <b-button size="sm" variant="outline-secondary" :disabled="scrapTestPage <= 1 || scrapTestLoading" @click="runScrapTestSearch(scrapTestPage - 1)"><i class="fas fa-chevron-left"></i></b-button>
                  <small class="text-muted">Page {{ scrapTestPage }} / {{ Math.ceil(scrapTestTotal / scrapTestLimit) }}</small>
                  <b-button size="sm" variant="outline-secondary" :disabled="scrapTestPage >= Math.ceil(scrapTestTotal / scrapTestLimit) || scrapTestLoading" @click="runScrapTestSearch(scrapTestPage + 1)"><i class="fas fa-chevron-right"></i></b-button>
                </div>
              </template>
            </div>
            <div v-if="scrapTestRows.length" class="mb-2">
              <!-- Toolbar multiselect -->
              <div class="d-flex align-items-center mb-2 flex-wrap" style="gap:8px">
                <b-form-checkbox
                  :checked="scrapTestApplicableRows.length > 0 && scrapTestApplicableRows.every(r => scrapTestSelectedIds.includes(r.id))"
                  :indeterminate="scrapTestSelectedIds.length > 0 && !scrapTestApplicableRows.every(r => scrapTestSelectedIds.includes(r.id))"
                  @change="toggleScrapTestAll"
                />
                <span class="text-muted" style="font-size:0.9em">{{ scrapTestSelectedIds.length }} sélectionné(s) · {{ scrapTestApplicableRows.length }} applicable(s) sur {{ scrapTestRows.length }}</span>
                <b-button size="sm" variant="warning" class="ml-auto" :disabled="!scrapTestSelectedApplicable.length || scrapTestRunningBulk || !scenarioUid" @click="launchBulkTest">
                  <b-spinner v-if="scrapTestRunningBulk" small class="mr-1" />
                  <i v-else class="fas fa-flask mr-1"></i> Tester la sélection ({{ scrapTestSelectedApplicable.length }})
                </b-button>
              </div>
              <!-- Table -->
              <div class="table-responsive mb-2">
                <table class="table table-sm table-bordered table-hover">
                  <thead class="thead-light">
                    <tr>
                      <th style="width:32px"></th>
                      <th style="width:80px;text-align:center">Applicable</th>
                      <th>id</th>
                      <th v-for="col in (ds.listingColumns || [{key:'id_cms',label:'id_cms'},{key:'status',label:'Statut'}])" :key="col.key">{{ col.label }}</th>
                      <th v-if="ds.showNomColumn !== false">Nom</th>
                      <th v-for="col in visibleConditionTestCols" :key="'st-'+col.key">{{ col.label }}</th>
                      <th v-if="globalGroupFn || ds.configType === 'scraping_file'">Groupe</th>
                      <th v-if="ds.rowActions && ds.rowActions.length" style="width:50px;text-align:center">Données</th>
                      <th style="width:120px;text-align:center">Résultat</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="row in scrapTestRows" :key="row.id"
                        :class="[!scrapTestPassesCurrentConfig(row) ? 'text-muted' : '', scrapTestSelectedProduct && scrapTestSelectedProduct.id === row.id ? 'scrap-row-active' : scrapTestSelectedIds.includes(row.id) ? 'scrap-row-selected' : '']"
                        style="cursor:pointer"
                        @click="scrapTestSelectedProduct = row; scrapTestStatus = ''; scrapTestResult = null">
                      <td @click.stop style="text-align:center;vertical-align:middle">
                        <b-form-checkbox :value="row.id" v-model="scrapTestSelectedIds" :disabled="!scrapTestPassesCurrentConfig(row)" />
                      </td>
                      <td style="text-align:center;vertical-align:middle">
                        <span v-if="scrapTestPassesCurrentConfig(row)" class="badge badge-success" style="font-size:0.75em">✓ Oui</span>
                        <span v-else class="badge badge-secondary" style="font-size:0.75em;opacity:0.7">✗ Non</span>
                      </td>
                      <td>{{ row.id }}</td>
                      <td v-for="col in (ds.listingColumns || [{key:'id_cms',label:'id_cms'},{key:'status',label:'Statut'}])" :key="col.key" style="vertical-align:middle">
                        <template v-if="col.key === 'groupe'">
                          <span v-if="row[col.key]" class="badge badge-info" style="font-size:0.8em">{{ row[col.key] }}</span>
                          <span v-else class="text-muted">—</span>
                        </template>
                        <template v-else>{{ col.render ? col.render(row[col.key]) : row[col.key] }}</template>
                      </td>
                      <td v-if="ds.showNomColumn !== false" style="vertical-align:middle">{{ getNom(row) }}</td>
                      <td v-for="col in visibleConditionTestCols" :key="'st-'+col.key" style="vertical-align:middle">
                        <template v-if="col.key === 'categories'"><span v-for="catId in (typeof row.categories === 'string' ? JSON.parse(row.categories || '[]') : (row.categories || []))" :key="catId" class="badge badge-secondary mr-1" style="font-size:0.75em">{{ categoriesMap[catId] || catId }}</span></template>
                        <template v-else-if="col.key === 'categorie_principale'">{{ categoriesMap[row.categorie_principale] || row.categorie_principale || '—' }}</template>
                        <template v-else-if="col.fromData">{{ parseDataField(row.data, col.fromData) }}</template>
                        <template v-else>{{ row[col.key] }}</template>
                      </td>
                      <td v-if="globalGroupFn || ds.configType === 'scraping_file'" style="vertical-align:middle;text-align:center">
                        <span v-if="row.groupe" class="badge badge-info" style="font-size:0.8em">{{ row.groupe }}</span>
                        <span v-else class="text-muted" style="font-size:0.75em">—</span>
                      </td>
                      <td v-if="ds.rowActions && ds.rowActions.length" @click.stop style="text-align:center;vertical-align:middle;white-space:nowrap">
                        <b-button v-for="action in ds.rowActions" :key="action.key" size="sm" :variant="action.variant || 'outline-secondary'" :title="action.label" @click="action.onClick(row)">
                          <i v-if="action.icon" :class="action.icon"></i>
                          <span v-else>{{ action.label }}</span>
                        </b-button>
                      </td>
                      <td style="text-align:center;vertical-align:middle;white-space:nowrap">
                        <span v-if="scrapTestResults[row.id]" class="badge" :class="statutSmdClass(scrapTestResults[row.id].statut_smd)" style="font-size:0.7em">{{ scrapTestResults[row.id].statut_smd || '?' }}</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div v-if="scrapTestSelectedProduct" class="border rounded p-2 mt-2">
              <div class="mb-2 d-flex align-items-center" style="gap:8px">
                <strong>Test unitaire</strong>
                <span class="badge badge-primary">{{ scrapTestSelectedProduct.id }}</span>
                <span>{{ getNom(scrapTestSelectedProduct) }}</span>
                <b-button size="sm" variant="link" class="text-danger p-0 ml-auto" @click="scrapTestSelectedProduct = null; scrapTestStatus = ''"><i class="fas fa-times"></i></b-button>
              </div>
              <small class="text-muted d-block mb-2">Lance le scraper sur ce seul produit et affiche le résultat brut ci-dessous.</small>
              <div class="d-flex align-items-center flex-wrap mb-2" style="gap:8px">
                <label class="mb-0">URL (optionnel) :</label>
                <b-form-input v-model="scrapTestUrl" size="sm" style="min-width:260px;flex:1" placeholder="Laisser vide pour lancer la recherche" />
              </div>
              <div class="d-flex align-items-center flex-wrap" style="gap:8px">
                <b-button v-if="!scrapTestLaunching" size="sm" variant="warning" :disabled="!configForm.domain || !scenarioUid" @click="launchScrapTest">
                  <i class="fas fa-flask"></i> Lancer le test
                </b-button>
                <span v-if="scrapTestLaunching" class="text-info"><b-spinner small class="mr-1" /> En cours...</span>
                <span v-if="scrapTestStatus === 'ok'" class="badge badge-success"><i class="fas fa-check"></i> Terminé ✓</span>
                <span v-if="scrapTestStatus === 'ko'" class="badge badge-danger"><i class="fas fa-times"></i> Erreur ✗</span>
                <span v-if="scrapTestStatus === 'error'" class="badge badge-danger"><i class="fas fa-exclamation-triangle"></i> Erreur</span>
              </div>
              <div v-if="scrapTestCurrentResult" class="mt-3 border rounded p-2" style="background:#f8f9fa">
                <table class="table table-sm table-bordered mb-2">
                  <tbody>
                    <tr><th style="width:160px">URL</th><td><a v-if="scrapTestCurrentResult.url" :href="scrapTestCurrentResult.url" target="_blank" rel="noopener" style="word-break:break-all;font-size:0.85em">{{ scrapTestCurrentResult.url }}</a><span v-else class="text-muted">—</span></td></tr>
                    <tr><th>Statut SMD</th><td><span class="badge" :class="statutSmdClass(scrapTestCurrentResult.statut_smd)">{{ scrapTestCurrentResult.statut_smd || '—' }}</span></td></tr>
                    <tr><th>Identification</th><td><small>{{ scrapTestCurrentResult.identification || '—' }}</small></td></tr>
                    <tr><th>Résultat identification</th><td><small>{{ scrapTestCurrentResult.identification_result || '—' }}</small></td></tr>
                  </tbody>
                </table>
                <template v-if="scrapTestCurrentResult.result">
                  <strong class="d-block mb-1" style="font-size:0.9em">Résultat</strong>
                  <pre class="p-2 bg-white border rounded mb-0" style="max-height:250px;overflow:auto;font-size:0.78em;white-space:pre-wrap">{{ formatResult(scrapTestCurrentResult.result) }}</pre>
                </template>
                <div class="mt-2 d-flex justify-content-end">
                  <b-button size="sm" variant="success" :disabled="scrapTestValidatingId === scrapTestSelectedProduct.id" @click="validateSingleResult(scrapTestSelectedProduct)">
                    <b-spinner v-if="scrapTestValidatingId === scrapTestSelectedProduct.id" small class="mr-1" />
                    <i v-else class="fas fa-check mr-1"></i> Valider — enregistrer en base
                  </b-button>
                </div>
              </div>
            </div>
          </div>
        </div><!-- fin tester le scraping -->
        <div class="d-flex align-items-center mt-3 flex-wrap" style="gap:8px">
          <!-- Navigation versions -->
          <div v-if="versionsList.length > 1" class="d-flex align-items-center" style="gap:6px">
            <small class="text-muted">Version :</small>
            <b-form-select
              size="sm"
              style="width:auto"
              :value="configForm.id"
              @change="v => switchVersion(versionsList.find(x => x.id === v))"
            >
              <b-form-select-option v-for="v in versionsList" :key="v.id" :value="v.id">
                {{ v.version === -1 ? 'test' : 'v' + v.version }}{{ v.production ? ' ★' : '' }}{{ v.description_version ? ' — ' + v.description_version : '' }}{{ v.updated_at ? '  (' + v.updated_at.substring(0,10) + ')' : '' }}
              </b-form-select-option>
            </b-form-select>
            <span v-if="configForm.production" class="badge badge-success">PROD</span>
            <span v-if="configForm.version === -1" class="badge badge-warning">test</span>
          </div>
          <!-- Boutons save -->
          <div class="ml-auto d-flex" style="gap:8px">
            <b-button variant="primary" :disabled="configSaving" @click="saveConfig">
              <b-spinner v-if="configSaving" small /><span v-else><i class="fas fa-save"></i> Sauvegarder</span>
            </b-button>
            <b-button v-if="siblingConfigs.length > 1" variant="outline-info" :disabled="syncingSiblings || !configForm.id" @click="syncToSiblings" v-b-tooltip.hover title="Synchroniser schéma, prompt et projet sur toutes les configs du même domaine/groupe">
              <b-spinner v-if="syncingSiblings" small class="mr-1" /><i v-else class="fas fa-link mr-1"></i>Sync configs
            </b-button>
            <b-button variant="warning" :disabled="configSaving" @click="saveAsTestOnly()" v-b-tooltip.hover title="Enregistrer dans la version test (sans changer la version courante)">
              <i class="fas fa-flask mr-1"></i>Sauvegarder en test
            </b-button>
            <b-button variant="outline-secondary" @click="versionSaveModal = true" :disabled="configSaving" v-b-tooltip.hover title="Enregistrer dans une nouvelle version">
              <i class="fas fa-plus mr-1"></i>Nouvelle version
            </b-button>
            <b-button v-if="!configForm.production" variant="outline-success" @click="promoteToProduction" :disabled="configSaving" v-b-tooltip.hover :title="configForm.version === -1 ? 'Passer en production (crée une nouvelle version)' : 'Passer en production'">
              <i class="fas fa-rocket"></i>
            </b-button>
            <b-button variant="outline-secondary" @click="backToConfigList"><i class="fas fa-arrow-left mr-1"></i>Retour au menu</b-button>
          </div>
        </div>
        </div><!-- fin v-if="!wizardActive" -->
      </div>
      <!-- Liste -->
      <div v-else>
        <!-- Panel test de routage (affiché à la place du tableau quand actif) -->
        <div v-if="routingTestActive">
          <div class="d-flex align-items-center mb-3" style="gap:8px">
            <b-button size="sm" variant="outline-secondary" @click="closeRoutingTest">
              <i class="fas fa-arrow-left"></i> Retour
            </b-button>
            <strong>Test de routage —</strong>
            <code>{{ routingTestDomain }}</code>
            <span v-if="routingTestGroupe" class="badge badge-info">{{ routingTestGroupe }}</span>
          </div>
          <!-- Résumé des configs actives -->
          <div class="mb-3 p-2 border rounded" style="background:#f8f9fa">
            <small class="text-muted d-block mb-1"><i class="fas fa-info-circle"></i> Configs en production pour ce domaine/groupe (par priorité) :</small>
            <span v-if="!routingTestConfigs.length" class="text-muted small">Aucune configuration en production trouvée.</span>
            <span v-for="(cfg, idx) in routingTestConfigs" :key="cfg.id" class="badge badge-pill mr-1 mb-1" :class="cfg.conditions ? 'badge-secondary' : 'badge-primary'">
              {{ idx + 1 }}. {{ cfg.conditions || 'Défaut' }}
            </span>
          </div>
          <!-- Filtre -->
          <div :id="routingTestFilterBuilderId" class="mb-2"></div>
          <div class="d-flex align-items-center mb-2 flex-wrap" style="gap:8px">
            <label class="mb-0">Lignes / page :</label>
            <b-form-input v-model.number="routingTestLimit" type="number" size="sm" style="width:70px" min="1" max="100" />
            <b-button size="sm" variant="outline-secondary" :disabled="routingTestLoading" @click="routingTestWhere = ''; initRoutingTestFilterBuilder()" v-b-tooltip.hover title="Vider les filtres"><i class="fas fa-times"></i></b-button>
            <b-button size="sm" variant="secondary" :disabled="routingTestLoading" @click="runRoutingTestSearch(1)">
              <b-spinner v-if="routingTestLoading" small /><span v-else><i class="fas fa-search"></i> Chercher</span>
            </b-button>
            <template v-if="routingTestRows.length">
              <div class="ml-2 d-flex align-items-center" style="gap:4px">
                <b-button size="sm" variant="outline-secondary" :disabled="routingTestPage <= 1 || routingTestLoading" @click="runRoutingTestSearch(routingTestPage - 1)"><i class="fas fa-chevron-left"></i></b-button>
                <small class="text-muted">Page {{ routingTestPage }} / {{ Math.ceil(routingTestTotal / routingTestLimit) }}</small>
                <b-button size="sm" variant="outline-secondary" :disabled="routingTestPage >= Math.ceil(routingTestTotal / routingTestLimit) || routingTestLoading" @click="runRoutingTestSearch(routingTestPage + 1)"><i class="fas fa-chevron-right"></i></b-button>
              </div>
            </template>
          </div>
          <!-- Boutons d'action -->
          <div class="d-flex align-items-center mb-3 flex-wrap" style="gap:8px">
            <b-button size="sm" variant="info" :disabled="!routingTestRows.length || routingResolutionLoading" @click="runRoutingResolution">
              <b-spinner v-if="routingResolutionLoading" small class="mr-1" />
              <i v-else class="fas fa-map-signs mr-1"></i> Bouton 1 : Prévisualiser le routage
            </b-button>
            <b-button size="sm" variant="warning" :disabled="!routingTestSelectedIds.length || routingTestRunning || !scenarioUid" @click="launchRoutingTest">
              <b-spinner v-if="routingTestRunning" small class="mr-1" />
              <i v-else class="fas fa-flask mr-1"></i> Bouton 2 : Lancer le traitement test ({{ routingTestSelectedIds.length }})
            </b-button>
            <b-dropdown size="sm" variant="outline-secondary" no-caret right>
              <template #button-content><i class="fas fa-columns mr-1"></i>Colonnes<span v-if="testColsVisible.length" class="badge badge-primary ml-1" style="font-size:0.75em">{{ visibleRoutingTestCols.length }}</span></template>
              <b-dropdown-form style="min-width:200px">
                <div class="d-flex mb-2" style="gap:6px">
                  <b-button size="sm" variant="link" class="p-0 text-primary" style="font-size:0.8em" @click.stop="testColsVisible = allRoutingTestCols.map(c => c.key)">Tout</b-button>
                  <b-button size="sm" variant="link" class="p-0 text-secondary" style="font-size:0.8em" @click.stop="testColsVisible = []">Aucun</b-button>
                </div>
                <b-form-checkbox v-for="col in allRoutingTestCols" :key="col.key" :value="col.key" v-model="testColsVisible" class="mb-1" style="font-size:0.85em">{{ col.label }}</b-form-checkbox>
              </b-dropdown-form>
            </b-dropdown>
            <span v-if="routingTestRunStatus === 'ok'" class="badge badge-success"><i class="fas fa-check"></i> Traitement terminé ✓</span>
            <span v-if="routingTestRunStatus === 'ko'" class="badge badge-danger"><i class="fas fa-times"></i> Traitement KO ✗</span>
            <span v-if="routingTestRunStatus === 'error'" class="badge badge-danger"><i class="fas fa-exclamation-triangle"></i> Erreur</span>
            <span v-if="!scenarioUid" class="badge badge-warning"><i class="fas fa-exclamation-triangle mr-1"></i>Scénario introuvable</span>
          </div>
          <!-- Tableau résultats -->
          <div v-if="routingTestRows.length" class="table-responsive">
            <table class="table table-sm table-bordered table-hover">
              <thead class="thead-light">
                <tr>
                  <th style="width:32px">
                    <b-form-checkbox
                      :checked="routingTestRows.length > 0 && routingTestRows.every(r => routingTestSelectedIds.includes(r.id))"
                      :indeterminate="routingTestSelectedIds.length > 0 && !routingTestRows.every(r => routingTestSelectedIds.includes(r.id))"
                      @change="v => { routingTestSelectedIds = v ? routingTestRows.map(r => r.id) : []; }"
                    />
                  </th>
                  <th>id</th>
                  <th v-for="col in visibleRoutingTestCols" :key="col.key">{{ col.label }}</th>
                  <th v-if="ds.showDataKeys" style="width:80px;text-align:center">Données</th>
                  <th v-if="!ds.showDataKeys && ds.rowActions && ds.rowActions.length" style="width:80px;text-align:center">Données</th>
                  <th style="min-width:130px;text-align:center">Config choisie</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in routingTestRows" :key="row.id">
                  <td style="text-align:center;vertical-align:middle">
                    <b-form-checkbox :value="row.id" v-model="routingTestSelectedIds" />
                  </td>
                  <td style="vertical-align:middle">{{ row.id }}</td>
                  <td v-for="col in visibleRoutingTestCols" :key="col.key" style="vertical-align:middle">
                    <template v-if="col.fromData">{{ parseDataField(row.data, col.fromData) }}</template>
                    <template v-else-if="col.key === 'nom'">{{ getNom(row) }}</template>
                    <template v-else-if="col.key === 'categories'">
                      <span v-for="catId in (typeof row.categories === 'string' ? JSON.parse(row.categories || '[]') : (row.categories || []))" :key="catId" class="badge badge-secondary mr-1" style="font-size:0.75em">{{ categoriesMap[catId] || catId }}</span>
                    </template>
                    <template v-else-if="col.key === 'categorie_principale'">{{ categoriesMap[row.categorie_principale] || row.categorie_principale || '—' }}</template>
                    <template v-else-if="col.key === 'default_category'">{{ categoriesMap[row.default_category] || row.default_category || '—' }}</template>
                    <template v-else-if="col.key === 'data'"><small class="text-muted" style="font-size:0.75em">{{ row.data ? '...' : '—' }}</small></template>
                    <template v-else>{{ row[col.key] }}</template>
                  </td>
                  <td v-if="ds.showDataKeys" style="text-align:center;vertical-align:middle">
                    <b-button v-if="row.data" size="sm" variant="outline-info" title="Voir les données" @click="conditionTestDataRow = row; conditionTestDataVisible = true">
                      <i class="fas fa-table"></i>
                    </b-button>
                    <span v-else class="text-muted">—</span>
                  </td>
                  <td v-if="!ds.showDataKeys && ds.rowActions && ds.rowActions.length" @click.stop style="text-align:center;vertical-align:middle;white-space:nowrap">
                    <b-button v-for="action in ds.rowActions" :key="action.key" size="sm" :variant="action.variant || 'outline-secondary'" :title="action.label" @click="action.onClick(row)"><i v-if="action.icon" :class="action.icon"></i><span v-else>{{ action.label }}</span></b-button>
                  </td>
                  <td style="text-align:center;vertical-align:middle">
                    <template v-if="routingTestResolutions[row.id] !== undefined">
                      <span v-if="routingTestResolutions[row.id]" class="badge badge-success">{{ routingTestResolutions[row.id].conditions }}</span>
                      <span v-else class="badge badge-danger">Aucune</span>
                    </template>
                    <span v-else class="text-muted" style="font-size:0.8em">—</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else-if="!routingTestLoading" class="text-center text-muted py-3">Aucun résultat. Utilisez le filtre ou cliquez sur Chercher.</div>
        </div>
        <!-- Listing normal -->
        <template v-else>
        <div class="d-flex justify-content-between align-items-center mb-2">
          <b-button size="sm" variant="outline-secondary" @click="closeConfigView">
            <i class="fas fa-arrow-left"></i> Retour au listing produits
          </b-button>
          <div class="d-flex" style="gap:8px">
            <b-button v-if="ds.showVeilleTarifaire !== false && ds.configType !== 'scraping_file'" size="sm" variant="outline-primary" @click="openVeilleTarifairePage">
              <i class="fas fa-percentage"></i> Règles de veille tarifaire
            </b-button>
            <b-button size="sm" variant="success" @click="newConfigForm">
              <i class="fas fa-plus"></i> Nouveau
            </b-button>
          </div>
        </div>
        <div v-if="configLoading" class="text-center py-4"><b-spinner variant="primary" /></div>
        <table v-else class="table table-sm table-bordered table-hover">
          <thead class="thead-light">
            <tr>
              <th style="width:30px"></th>
              <th>Domaine</th>
              <th>Version</th>
              <th>Projet</th>
              <th style="width:110px">Groupe</th>
              <th style="width:120px">Conditions</th>
              <th style="width:80px;text-align:center">Actif</th>
              <th v-if="ds.showVeilleTarifaire !== false" style="width:100px;text-align:center">Veille tarif.</th>
              <th style="width:80px;text-align:center"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="groupedConfigList.length === 0">
              <td :colspan="ds.showVeilleTarifaire !== false ? 9 : 8" class="text-center text-muted py-3">Aucune configuration</td>
            </tr>
            <template v-for="group in groupedConfigList">
              <!-- Groupe avec plusieurs configs : ligne d'en-tête repliable -->
              <tr v-if="group.configs.length > 1" :key="'h-' + group.key"
                @click="toggleExpandGroup(group.key)"
                style="cursor:pointer; background:#e9ecef; user-select:none"
              >
                <td style="text-align:center;vertical-align:middle">
                  <i :class="expandedGroups[group.key] !== false ? 'fas fa-chevron-down' : 'fas fa-chevron-right'" class="text-muted"></i>
                </td>
                <td style="vertical-align:middle">
                  <strong>{{ group.domain }}</strong>
                  <span class="badge badge-secondary ml-2">{{ group.configs.length }} configurations</span>
                  <small v-if="prioritySaving && dragSourceGroupKey === group.key" class="text-muted ml-2"><b-spinner small /> Sauvegarde...</small>
                </td>
                <td colspan="2" style="vertical-align:middle"></td>
                <td style="vertical-align:middle">
                  <span v-if="group.groupe" class="badge badge-info">{{ group.groupe }}</span>
                  <small v-else class="text-muted">—</small>
                  <b-button size="sm" variant="link" class="p-0 ml-1" @click.stop="openRenameGroupModal(group)" title="Modifier le groupe"><i class="fas fa-pencil-alt text-secondary" style="font-size:0.8em"></i></b-button>
                </td>
                <td></td>
                <td v-if="ds.showVeilleTarifaire !== false"></td>
                <td style="text-align:center;vertical-align:middle">
                  <b-button size="sm" variant="outline-info" @click.stop="openRoutingTest(group.domain, group.groupe)" v-b-tooltip.hover title="Tester le routage entre les configs">
                    <i class="fas fa-map-signs"></i>
                  </b-button>
                </td>
              </tr>
              <!-- Sous-lignes pour groupe multi (dépliable + draggable) -->
              <template v-if="group.configs.length > 1 && expandedGroups[group.key] !== false">
                <tr v-for="cfg in group.configs" :key="cfg.id"
                  draggable="true"
                  @dragstart="onDragStart(cfg, group.key)"
                  @dragover.prevent="onDragOver(cfg, group.key)"
                  @drop.prevent="onDrop(group)"
                  @dragend="onDragEnd"
                  :style="{ background: dragOverCfgId === cfg.id && dragSourceGroupKey === group.key ? '#fff3cd' : '#fafafa', borderLeft: dragOverCfgId === cfg.id && dragSourceGroupKey === group.key ? '3px solid #ffc107' : '' }"
                >
                  <td style="text-align:center;vertical-align:middle;cursor:grab"><i class="fas fa-grip-vertical text-muted"></i></td>
                  <td style="vertical-align:middle;padding-left:24px"><small class="text-muted">{{ cfg.domain }}</small></td>
                  <td style="vertical-align:middle;white-space:nowrap">
                    <span v-if="cfg.version === -1" class="text-warning font-weight-bold">test</span>
                    <span v-else :class="cfg.production ? 'text-success font-weight-bold' : 'text-muted'">v{{ cfg.version }}</span>
                    <small v-if="cfg.description_version" class="text-muted ml-1">— {{ cfg.description_version }}</small>
                  </td>
                  <td style="vertical-align:middle"><small>{{ cfg.projet }}</small></td>
                  <td style="vertical-align:middle">
                    <span v-if="cfg.groupe" class="badge badge-info">{{ cfg.groupe }}</span>
                    <small v-else class="text-muted">—</small>
                  </td>
                  <td style="vertical-align:middle">
                    <span v-if="cfg.conditions" class="badge badge-secondary">{{ cfg.conditions }}</span>
                    <small v-else class="text-muted">—</small>
                  </td>
                  <td style="text-align:center;vertical-align:middle">
                    <b-form-checkbox :checked="!!cfg.active" switch @change="toggleConfigActive(cfg)" />
                  </td>
                  <td v-if="ds.showVeilleTarifaire !== false" style="text-align:center;vertical-align:middle">
                    <b-form-checkbox :checked="!!cfg.veille_tarifaire" switch @change="toggleConfigVeilleTarifaire(cfg)" />
                  </td>
                  <td style="text-align:center;vertical-align:middle">
                    <b-button size="sm" variant="outline-primary" @click.stop="editConfigForm(cfg)"><i class="fas fa-edit"></i></b-button>
                  </td>
                </tr>
              </template>
              <!-- Ligne simple (1 seule config pour ce domaine/groupe) -->
              <tr v-if="group.configs.length === 1" :key="group.configs[0].id">
                <td></td>
                <td style="vertical-align:middle">{{ group.configs[0].domain }}</td>
                <td style="vertical-align:middle;white-space:nowrap">
                  <span v-if="group.configs[0].version === -1" class="text-warning font-weight-bold">test</span>
                  <span v-else :class="group.configs[0].production ? 'text-success font-weight-bold' : 'text-muted'">v{{ group.configs[0].version }}</span>
                  <small v-if="group.configs[0].description_version" class="text-muted ml-1">— {{ group.configs[0].description_version }}</small>
                </td>
                <td style="vertical-align:middle"><small>{{ group.configs[0].projet }}</small></td>
                <td style="vertical-align:middle">
                  <span v-if="group.configs[0].groupe" class="badge badge-info">{{ group.configs[0].groupe }}</span>
                  <small v-else class="text-muted">—</small>
                  <b-button size="sm" variant="link" class="p-0 ml-1" @click.stop="openRenameGroupModal(group)" title="Modifier le groupe"><i class="fas fa-pencil-alt text-secondary" style="font-size:0.8em"></i></b-button>
                </td>
                <td style="vertical-align:middle">
                  <span v-if="group.configs[0].conditions" class="badge badge-secondary">{{ group.configs[0].conditions }}</span>
                  <small v-else class="text-muted">—</small>
                </td>
                <td style="text-align:center;vertical-align:middle">
                  <b-form-checkbox :checked="!!group.configs[0].active" switch @change="toggleConfigActive(group.configs[0])" />
                </td>
                <td v-if="ds.showVeilleTarifaire !== false" style="text-align:center;vertical-align:middle">
                  <b-form-checkbox :checked="!!group.configs[0].veille_tarifaire" switch @change="toggleConfigVeilleTarifaire(group.configs[0])" />
                </td>
                <td style="text-align:center;vertical-align:middle">
                  <b-button size="sm" variant="outline-primary" @click="editConfigForm(group.configs[0])"><i class="fas fa-edit"></i></b-button>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
        </template><!-- /v-else listing normal -->
      </div>
    </div><!-- /config view -->

    <!-- Modal renommage groupe -->
    <b-modal v-model="renameGroupModal" title="Modifier le groupe" size="sm" hide-footer>
      <div class="form-group">
        <label class="font-weight-bold">Groupe <small class="text-muted font-weight-normal">(laisser vide pour supprimer)</small></label>
        <input v-model="renameGroupValue" type="text" class="form-control form-control-sm" placeholder="Nom du groupe…" :list="'rename-groupes-list'" autocomplete="off" @keydown.enter="saveGroupRename" />
        <datalist id="rename-groupes-list">
          <option v-for="g in groupesList" :key="g" :value="g" />
        </datalist>
        <small v-if="renameGroupTarget" class="text-muted">
          Affecte {{ renameGroupTarget.configs.length }} scraper(s) : <em>{{ renameGroupTarget.domain }}</em>
        </small>
      </div>
      <div class="d-flex justify-content-end" style="gap:8px">
        <b-button size="sm" variant="outline-secondary" @click="renameGroupModal = false">Annuler</b-button>
        <b-button size="sm" variant="primary" :disabled="renameGroupSaving" @click="saveGroupRename">
          <b-spinner v-if="renameGroupSaving" small /> Sauvegarder
        </b-button>
      </div>
    </b-modal>

    <!-- Modal nouvelle version -->
    <b-modal v-model="versionSaveModal" title="Nouvelle version" @ok="saveConfigNewVersion" ok-title="Créer la version" cancel-title="Annuler">
      <b-form-group label="Description (optionnelle)" description="Ex: Ajout règle EAN, Correction identification...">
        <b-form-input v-model="versionSaveDescription" placeholder="ex: Ajout règle EAN" autofocus />
      </b-form-group>
      <b-form-group>
        <b-form-checkbox v-model="versionSaveProd" switch>
          Mettre directement en production
        </b-form-checkbox>
      </b-form-group>
    </b-modal>

    <!-- Modal détail scraping -->
    <b-modal v-model="modalVisible" :title="modalTitle" hide-footer size="xl">
      <div class="d-flex justify-content-between mb-2">
        <b-button size="sm" variant="outline-secondary" :disabled="!modalHasPrev" @click="navigateModal(-1)">&#8592; Précédent</b-button>
        <b-button size="sm" variant="outline-secondary" :disabled="!modalHasNext" @click="navigateModal(1)">Suivant &#8594;</b-button>
      </div>
      <div v-if="modalRow">
        <div v-if="modalProduct" class="mb-3 p-2 border rounded bg-light">
          <strong>{{ getNom(modalProduct) }}</strong>
          <span class="ml-3 text-muted">Prix TTC : <b>{{ modalProduct.prix_ttc || '—' }}</b></span>
          <span v-if="modalProduct[ds.promoField || 'prix_promo_ttc']" class="ml-3 text-danger">Promo : <b>{{ modalProduct[ds.promoField || 'prix_promo_ttc'] }}</b></span>
          <template v-if="hasModalProductData">
            <hr class="my-2" />
            <div>
              <a href="#" @click.prevent="modalDataExpanded = !modalDataExpanded" style="font-size:0.82em; text-decoration:none">
                <i :class="modalDataExpanded ? 'fas fa-chevron-down' : 'fas fa-chevron-right'" class="mr-1"></i>
                <span class="text-muted">Données ({{ Object.keys(modalProductData).length }} champs)</span>
              </a>
              <table v-if="modalDataExpanded" class="table table-sm table-bordered mt-1 mb-0" style="font-size:0.8em">
                <tbody>
                  <tr v-for="(val, key) in modalProductData" :key="key">
                    <th style="width:35%; background:#f8f9fa; white-space:nowrap">{{ key }}</th>
                    <td style="word-break:break-word">{{ val }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </template>
        </div>
        <table class="table table-sm">
          <tbody>
            <tr>
              <th style="width:140px">URL</th>
              <td>
                <div class="d-flex align-items-center" style="gap:8px">
                  <b-form-input
                    v-model="modalEditUrl"
                    size="sm"
                    placeholder="https://..."
                    style="font-size:0.85em"
                    @keyup.enter="saveUrl"
                  />
                  <b-button size="sm" variant="primary" :disabled="modalSaving" @click="saveUrl">
                    <b-spinner v-if="modalSaving" small /><span v-else>Sauvegarder</span>
                  </b-button>
                  <a v-if="modalRow.url" :href="modalRow.url" target="_blank" rel="noopener" title="Ouvrir">
                    <i class="fas fa-external-link-alt"></i>
                  </a>
                </div>
                <small v-if="modalLaunchStatus" class="text-muted d-block mt-1">{{ modalLaunchStatus }}</small>
              </td>
            </tr>
            <tr>
              <th>Statut SMD</th>
              <td>
                <span class="badge" :class="statutSmdClass(modalRow.statut_smd)">
                  {{ modalRow.statut_smd || '—' }}
                </span>
              </td>
            </tr>
            <tr>
              <th>Statut</th>
              <td>{{ modalRow.statut || '—' }}</td>
            </tr>
            <tr>
              <th>Date recherche</th>
              <td>{{ modalRow.date_recherche || '—' }}</td>
            </tr>
            <tr>
              <th>Identification</th>
              <td><small>{{ modalRow.identification || '—' }}</small></td>
            </tr>
            <tr>
              <th>Résultat identification</th>
              <td><small>{{ modalRow.identification_result || '—' }}</small></td>
            </tr>
            <tr v-if="ds.showVeilleTarifaire !== false && modalCfg && modalCfg.veille_tarifaire">
              <th>Veille tarifaire</th>
              <td>
                <div class="d-flex align-items-center" style="gap:8px">
                  <b-form-checkbox
                    v-model="modalEditVeilleTarifaire"
                    :value="1"
                    :unchecked-value="0"
                    switch
                  >
                    {{ modalEditVeilleTarifaire ? 'Activée' : 'Désactivée' }}
                  </b-form-checkbox>
                  <b-button size="sm" variant="primary" :disabled="modalSaving" @click="saveVeilleTarifaire">
                    <b-spinner v-if="modalSaving" small /><span v-else>Sauvegarder</span>
                  </b-button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Résultat parsé -->
        <div v-if="modalRow.result" class="mt-3">
          <strong class="d-block mb-1">Résultat</strong>
          <pre class="p-2 bg-light rounded" style="max-height:200px;overflow:auto;font-size:0.78em;white-space:pre-wrap">{{ formatResult(modalRow.result) }}</pre>
        </div>

        <!-- Modifier statut -->
        <div v-if="modalRow" class="mt-3">
          <strong class="d-block mb-1">Statut</strong>
          <div class="d-flex" style="gap:8px">
            <b-form-select v-model="modalEditStatut" :options="statutOptions" size="sm" style="max-width:200px" />
            <b-button size="sm" variant="primary" :disabled="modalSaving" @click="saveStatut">
              <b-spinner v-if="modalSaving" small /><span v-else>Sauvegarder</span>
            </b-button>
          </div>
        </div>

        <!-- Retraiter le résultat -->
        <div v-if="showReprocessButton" class="mt-3">
          <b-button
            variant="outline-primary"
            class="d-inline-flex align-items-center"
            :disabled="modalLaunching || !scenarioUid"
            :title="scenarioUid ? 'Réappliquer le projet pour retraiter le résultat' : 'Projet Scraping - search introuvable'"
            @click="launchScraping"
          >
            <b-spinner v-if="modalLaunching" small />
            <span v-else><i class="fas fa-sync-alt mr-1"></i>Relancer le scraper sur cette URL</span>
          </b-button>
          <small v-if="modalLaunchStatus" class="text-muted d-block mt-1">{{ modalLaunchStatus }}</small>
        </div>

      </div>
      <div v-else-if="modalProduct && modalCfg && passesRules(modalProduct, modalCfg)" class="py-3">
        <p class="text-muted mb-3">Ce produit n'a pas encore été scrappé sur ce domaine.</p>
        <div class="mb-3">
          <label class="font-weight-bold mb-1">URL à scrapper</label>
          <div class="d-flex align-items-center" style="gap:8px">
            <b-form-input
              v-model="modalEditUrl"
              size="sm"
              placeholder="https://..."
              style="font-size:0.85em"
              @keyup.enter="saveUrl"
            />
            <b-button size="sm" variant="primary" :disabled="modalSaving" @click="saveUrl">
              <b-spinner v-if="modalSaving" small /><span v-else>Sauvegarder</span>
            </b-button>
          </div>
          <small v-if="modalLaunchStatus" class="text-muted d-block mt-1">{{ modalLaunchStatus }}</small>
        </div>
        <b-button
          variant="outline-warning"
          class="d-inline-flex align-items-center"
          :disabled="modalLaunching || !scenarioUid"
          :title="scenarioUid ? 'Lancer le scraping sur ce domaine pour ce produit' : 'Projet Scraping - search introuvable'"
          @click="launchScraping"
        >
          <b-spinner v-if="modalLaunching" small class="mr-1" />
          <span v-else><i class="fas fa-search mr-1"></i>Lancer la recherche</span>
        </b-button>
      </div>
      <div v-else class="text-center py-3 text-muted">
        Ce domaine n'est pas applicable à ce produit (ne correspond pas aux filtres).
      </div>
    </b-modal>

    <!-- Veille tarifaire view -->
    <div v-if="veilleView">
      <div class="d-flex align-items-center mb-3" style="gap:8px">
        <b-button size="sm" variant="outline-secondary" @click="closeVeilleTarifairePage">
          <i class="fas fa-arrow-left"></i> Retour aux configurations
        </b-button>
        <h5 class="mb-0">Règles de veille tarifaire</h5>
        <b-button variant="primary" :disabled="veilleTarifaireSaving" class="ml-auto" @click="saveVeilleTarifaireRegles">
          <b-spinner v-if="veilleTarifaireSaving" small class="mr-1" />
          <i v-else class="fas fa-save mr-1"></i> Enregistrer
        </b-button>
      </div>
      <div v-if="veilleTarifaireLoading" class="text-center py-4"><b-spinner variant="primary" /></div>
      <div v-else>
        <div class="p-2 mb-2 bg-light rounded border" style="font-size:0.82em">
          <strong>Priorité :</strong> Le champ Priorité départage : <strong>valeur plus élevée = prioritaire</strong>. Fallback par défaut : <strong>-5%</strong>.
          Faites glisser les lignes (<i class="fas fa-grip-vertical"></i>) pour réordonner.
        </div>
        <table class="table table-sm table-bordered" style="font-size:0.88em">
          <thead class="thead-light">
            <tr>
              <th style="width:24px"></th>
              <th style="width:140px">Nom *</th>
              <th style="width:145px">
                Réduction
                <span id="vtr-tip-reduction" style="cursor:pointer;color:#6c757d"><i class="fas fa-info-circle"></i></span>
                <b-tooltip target="vtr-tip-reduction" triggers="hover" placement="top">Prix scrapé − % ou montant</b-tooltip>
              </th>
              <th style="width:145px">
                Prix max
                <span id="vtr-tip-prix-max" style="cursor:pointer;color:#6c757d"><i class="fas fa-info-circle"></i></span>
                <b-tooltip target="vtr-tip-prix-max" triggers="hover" placement="top">Réduction sur prix_ttc de base — cap sur le prix calculé</b-tooltip>
              </th>
              <th style="width:145px">
                Prix min
                <span id="vtr-tip-prix-min" style="cursor:pointer;color:#6c757d"><i class="fas fa-info-circle"></i></span>
                <b-tooltip target="vtr-tip-prix-min" triggers="hover" placement="top">Marge minimale sur prix_achat (TTC)</b-tooltip>
              </th>
              <th style="width:70px">Priorité</th>
              <th style="width:50px;text-align:center">Actif</th>
              <th style="width:130px">Marque</th>
              <th v-if="ds.showSourceInVeilleTarifaire" style="width:120px">Source</th>
              <th style="width:150px">Catégorie</th>
              <th style="width:260px">Caractéristiques</th>
              <th style="width:36px"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="!veilleTarifaireRegles.length">
              <td :colspan="ds.showSourceInVeilleTarifaire ? 12 : 11" class="text-center text-muted py-2">
                Aucune règle. Fallback par défaut : <strong>-5%</strong>.
              </td>
            </tr>
          </tbody>
          <tbody v-for="(rule, idx) in veilleTarifaireRegles" :key="idx">
            <tr
              :class="{ 'table-info': vtrDragOver === idx }"
              @dragover.prevent="vtrDragOver = idx"
              @dragleave="vtrDragOver = null"
              @drop.prevent="vtrDrop(idx)"
              @dragend="vtrDragOver = null"
            >
              <!-- Poignée -->
              <td
                class="text-center align-middle"
                style="cursor:grab;color:#aaa"
                draggable="true"
                @dragstart="vtrDragStart(idx, $event)"
              ><i class="fas fa-grip-vertical"></i></td>
              <!-- Nom -->
              <td><b-form-input v-model="rule.nom" size="sm" placeholder="Nom *" :state="rule.nom && rule.nom.trim() ? null : false" /></td>
              <!-- Réduction sur prix concurrent scrapé -->
              <td>
                <div class="d-flex" style="gap:3px">
                  <b-form-input v-model.number="rule.reduction" type="number" min="0" step="0.01" size="sm" style="width:75px" @blur="rule.reduction = Math.round(rule.reduction * 100) / 100" />
                  <b-form-select v-model="rule.type" :options="[{value:'%',text:'%'},{value:'€',text:'€'}]" size="sm" style="width:58px" />
                </div>
              </td>
              <!-- Prix max : réduction sur prix_ttc en base -->
              <td>
                <div class="d-flex" style="gap:3px">
                  <b-form-input v-model.number="rule.prix_max" type="number" min="0" step="0.01" size="sm" style="width:75px" @blur="rule.prix_max = Math.round(rule.prix_max * 100) / 100" />
                  <b-form-select v-model="rule.type_max" :options="[{value:'%',text:'%'},{value:'€',text:'€'}]" size="sm" style="width:58px" />
                </div>
              </td>
              <!-- Prix min : marge minimale sur prix_achat -->
              <td>
                <div class="d-flex" style="gap:3px">
                  <b-form-input v-model.number="rule.prix_min" type="number" min="0" step="0.01" size="sm" style="width:75px" @blur="rule.prix_min = Math.round(rule.prix_min * 100) / 100" />
                  <b-form-select v-model="rule.type_min" :options="[{value:'%',text:'%'},{value:'€',text:'€'}]" size="sm" style="width:58px" />
                </div>
              </td>
              <!-- Priorité -->
              <td><b-form-input v-model.number="rule.priority" type="number" size="sm" /></td>
              <!-- Actif -->
              <td class="text-center align-middle">
                <b-form-checkbox v-model="rule.active" :value="1" :unchecked-value="0" class="mb-0" />
              </td>
              <!-- Marque -->
              <td><b-form-input v-model="rule._marque" size="sm" placeholder="Toutes" list="vtr-marques-list" /></td>
              <!-- Source -->
              <td v-if="ds.showSourceInVeilleTarifaire"><b-form-input v-model="rule._source" size="sm" placeholder="Toutes" list="vtr-sources-list" /></td>
              <!-- Catégorie -->
              <td>
                <div class="d-flex align-items-center" style="gap:3px">
                  <span v-if="rule._categorie" class="badge badge-light border" style="max-width:110px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">{{ categoriesMap[rule._categorie] || '#'+rule._categorie }}</span>
                  <span v-else class="text-muted" style="font-size:0.85em">Aucune</span>
                  <b-button size="sm" variant="outline-secondary" @click="openCategoryForVtr(idx, '_categorie')"><i class="fas fa-folder-open"></i></b-button>
                  <b-button v-if="rule._categorie" size="sm" variant="outline-danger" @click="rule._categorie = null"><i class="fas fa-times"></i></b-button>
                </div>
              </td>
              <!-- Caractéristiques -->
              <td>
                <div class="d-flex flex-wrap align-items-center" style="gap:3px">
                  <div v-for="(c, ci) in rule._caracteristiques" :key="ci" class="d-flex align-items-center" style="gap:3px">
                    <b-form-input v-model="c.key" size="sm" placeholder="Clé" style="width:90px" />
                    <b-form-input v-model="c.value" size="sm" placeholder="Valeur" style="width:90px" />
                    <b-button size="sm" variant="outline-danger" @click="rule._caracteristiques.splice(ci, 1)"><i class="fas fa-times"></i></b-button>
                  </div>
                  <b-button size="sm" variant="outline-secondary" @click="rule._caracteristiques.push({key:'',value:''})"><i class="fas fa-plus"></i></b-button>
                </div>
              </td>
              <!-- Supprimer -->
              <td class="text-center align-middle" style="white-space:nowrap">
                <b-button size="sm" :variant="rule.code && rule.code.trim() ? 'success' : 'outline-secondary'" class="mr-1" @click="rule._open = !rule._open" :title="rule.code && rule.code.trim() ? 'Code personnalisé (actif)' : 'Code personnalisé (vide)'" style="position:relative">
                  <i class="fas fa-code"></i>
                  <span v-if="rule.code && rule.code.trim()" style="position:absolute;top:-4px;right:-4px;width:9px;height:9px;background:#fd7e14;border-radius:50%;border:2px solid #fff"></span>
                </b-button>
                <b-button size="sm" variant="outline-danger" @click="veilleTarifaireRegles.splice(idx, 1); veilleTarifaireDirty = true"><i class="fas fa-trash"></i></b-button>
              </td>
            </tr>
            <!-- Ligne expandable : code personnalisé -->
            <tr v-if="rule._open">
              <td :colspan="ds.showSourceInVeilleTarifaire ? 13 : 12" class="p-2" style="background:#f8f9fa">
                <div class="mb-1 d-flex align-items-center" style="gap:8px">
                  <strong style="font-size:0.85em">Code personnalisé</strong>
                  <span class="text-muted" style="font-size:0.8em">Exécuté après le calcul des bornes, appliqué avant la comparaison prix min / prix max.</span>
                  <span :id="'vtr-tip-code-' + idx" style="cursor:pointer;color:#6c757d"><i class="fas fa-info-circle"></i></span>
                  <b-tooltip :target="'vtr-tip-code-' + idx" triggers="hover" placement="top">
                    Variables modifiables : <strong>prix_min</strong> (plancher TTC), <strong>prix_max</strong> (plafond TTC).<br>
                    Variables disponibles : <strong>prix_achat</strong>.
                  </b-tooltip>
                </div>
                <div class="d-flex" style="gap:8px; align-items:stretch">
                  <div style="flex:1;min-width:0;display:flex;flex-direction:column">
                    <ace_editor :ref="'vtrCodeEditor_' + idx" :height="200" mode="javascript" :code="rule.code || ''" @update="rule.code = $event; veilleTarifaireDirty = true" style="flex:1" />
                  </div>
                  <div style="flex:0 0 180px; font-size:0.82em">
                    <div class="border rounded p-2 h-100" style="background:#fff">
                      <div class="font-weight-bold mb-2" style="font-size:0.9em">Tester</div>
                      <div class="mb-1">
                        <label class="mb-0 text-muted" style="font-size:0.85em">prix_min</label>
                        <b-form-input v-model.number="rule._test_prix_min" type="number" size="sm" step="0.01" placeholder="ex: 10" />
                      </div>
                      <div class="mb-1">
                        <label class="mb-0 text-muted" style="font-size:0.85em">prix_max</label>
                        <b-form-input v-model.number="rule._test_prix_max" type="number" size="sm" step="0.01" placeholder="ex: 50" />
                      </div>
                      <div class="mb-2">
                        <label class="mb-0 text-muted" style="font-size:0.85em">prix_achat <span class="badge badge-secondary" style="font-size:0.75em">lecture</span></label>
                        <b-form-input v-model.number="rule._test_prix_achat" type="number" size="sm" step="0.01" placeholder="ex: 8" />
                      </div>
                      <b-button size="sm" variant="primary" block @click="vtrRunCodeTest(rule)">
                        <i class="fas fa-play"></i> Exécuter
                      </b-button>
                      <div v-if="rule._test_result !== undefined" class="mt-2 p-2 rounded" style="background:#f0f4ff; font-size:0.95em">
                        <div class="mb-1"><span class="text-muted">prix_min →</span> <strong :class="rule._test_result.error ? 'text-danger' : 'text-success'" style="font-size:1.05em">{{ rule._test_result.error ? '!' : rule._test_result.prix_min }}</strong></div>
                        <div><span class="text-muted">prix_max →</span> <strong :class="rule._test_result.error ? 'text-danger' : 'text-success'" style="font-size:1.05em">{{ rule._test_result.error ? rule._test_result.error : rule._test_result.prix_max }}</strong></div>
                      </div>
                    </div>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
        <b-button variant="outline-primary" size="sm" @click="addVeilleTarifaireRegle">
          <i class="fas fa-plus"></i> Ajouter une règle
        </b-button>
      </div>
      <datalist id="vtr-marques-list">
        <option v-for="m in marquesList" :key="m" :value="m" />
      </datalist>
      <datalist id="vtr-sources-list">
        <option v-for="s in veilleTarifaireSourcesList" :key="s" :value="s" />
      </datalist>
    </div>

    <!-- Modal sélection catégorie -->
    <b-modal v-model="categoryModalVisible" title="Sélectionner une catégorie" size="lg" no-enforce-focus @ok="saveCategorySelection">
      <div v-if="categoriesTree.length" style="max-height: 500px; overflow-y: auto; border: 1px solid #ddd; border-radius: 4px; padding: 5px">
        <category-tree-item 
          v-for="node in categoriesTree" 
          :key="node.uniqueId"
          :node="node"
          :selected-id="selectedCategoryId"
          @select="selectedCategoryId = $event"
        />
      </div>
      <div v-else class="text-center py-3 text-muted">
        Chargement des catégories...
      </div>
    </b-modal>

    <!-- Modal ajout caractéristique dans le query builder -->
    <b-modal v-model="showQueryCaractModal" title="Ajouter une caractéristique" size="sm" ok-title="Ajouter" @ok="confirmAddCaractQuery">
      <b-form-group label="Nom de la caractéristique">
        <b-form-input v-model="queryCaractColumn" size="sm" placeholder="ex: Auteur" />
      </b-form-group>
      <b-form-group label="Langue">
        <b-form-select v-model="queryCaractLang" size="sm" :options="languageOptions" />
      </b-form-group>
    </b-modal>

    <!-- Modal règles de groupe -->
    <b-modal
      v-model="groupRulesModalShow"
      title="Règles de groupe"
      size="xl"
      hide-footer
      @shown="onGroupRulesModalShown"
    >
      <rules_editor
        ref="groupRulesEditor"
        :show_group_mapping="true"
        :qb-fields="queryBuilderFields"
        :categories-map="categoriesMap"
        :marques-list="marquesList"
        :groupes-list="groupesList"
        @open-category="openCategoryForRule"
      />

      <!-- Section test sur les produits chargés -->
      <div class="mt-3 border rounded">
        <div
          class="p-2 d-flex align-items-center"
          style="cursor:pointer; gap:8px; background:#f0f4ff"
          @click="groupRulesTestOpen = !groupRulesTestOpen"
        >
          <i :class="groupRulesTestOpen ? 'fas fa-chevron-down' : 'fas fa-chevron-right'"></i>
          <strong>Tester sur les produits chargés</strong>
          <small class="text-muted ml-2">({{ products.length }} produits)</small>
        </div>
        <div v-if="groupRulesTestOpen" class="p-2">
          <b-button size="sm" variant="primary" class="mb-2" @click="runGroupRulesTest">
            <i class="fas fa-play"></i> Tester
          </b-button>
          <table v-if="groupRulesTestResults.length" class="table table-sm table-bordered" style="font-size:0.82em">
            <thead class="thead-light">
              <tr><th>Nom</th><th>Groupe attribué</th></tr>
            </thead>
            <tbody>
              <tr v-for="(r, i) in groupRulesTestResults" :key="i">
                <td>{{ r.nom }}</td>
                <td><span class="badge" :class="r.groupe ? 'badge-info' : 'badge-secondary'">{{ r.groupe || '(aucun)' }}</span></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="d-flex justify-content-end mt-3" style="gap:8px">
        <b-button variant="secondary" @click="groupRulesModalShow = false">Annuler</b-button>
        <b-button variant="primary" :disabled="groupRulesSaving" @click="saveGroupRules">
          <b-spinner v-if="groupRulesSaving" small class="mr-1" />
          <i v-else class="fas fa-save mr-1"></i> Sauvegarder
        </b-button>
      </div>
    </b-modal>

    <!-- Modal règles de veille tarifaire supprimé, remplacé par veilleView -->

    <!-- Modal résultats veille tarifaire -->
    <b-modal v-model="veilleResultModalVisible" size="xl" title="Résultats de veille tarifaire" hide-footer scrollable>
      <div v-if="veilleResultLoading" class="text-center py-5">
        <b-spinner variant="primary" /><br><small class="text-muted mt-2">Chargement...</small>
      </div>
      <template v-else>
        <div v-if="veilleResultProduct" class="mb-3 d-flex align-items-center" style="gap:8px">
          <span class="badge badge-primary">id {{ veilleResultProduct.id }}</span>
          <strong>{{ getNom(veilleResultProduct) }}</strong>
        </div>
        <b-tabs>
          <b-tab title="Historique" :title-link-class="veilleHistorique.length ? '' : 'text-muted'">
            <div v-if="!veilleHistorique.length" class="text-center text-muted py-4">Aucun historique pour ce produit</div>
            <div v-else class="table-responsive mt-2">
              <table class="table table-sm table-bordered table-hover">
                <thead class="thead-light">
                  <tr>
                    <th>Date</th>
                    <th>Prix extrait</th>
                    <th>Prix TTC</th>
                    <th>Prix promo</th>
                    <th>Nouveau prix</th>
                    <th>Règle</th>
                    <th>Source prix</th>
                    <th>URL</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in veilleHistorique" :key="row.id">
                    <td style="white-space:nowrap;font-size:0.82em">{{ row.date }}</td>
                    <td>{{ row.prix_extrait != null ? Number(row.prix_extrait).toFixed(2) + ' €' : '—' }}</td>
                    <td>{{ row.prix_ttc != null ? Number(row.prix_ttc).toFixed(2) + ' €' : '—' }}</td>
                    <td>{{ row.prix_promo != null ? Number(row.prix_promo).toFixed(2) + ' €' : '—' }}</td>
                    <td>
                      <span v-if="row.nouveau_prix != null" class="badge badge-success">{{ Number(row.nouveau_prix).toFixed(2) }} €</span>
                      <span v-else class="text-muted">—</span>
                    </td>
                    <td style="max-width:180px"><small class="text-truncate d-block" :title="row.regle">{{ row.regle || '—' }}</small></td>
                    <td><span v-if="row.source_prix" class="badge badge-info">{{ row.source_prix }}</span><span v-else class="text-muted">—</span></td>
                    <td style="text-align:center">
                      <a v-if="row.url" :href="row.url" target="_blank" rel="noopener" style="font-size:0.85em"><i class="fas fa-external-link-alt"></i></a>
                      <span v-else class="text-muted">—</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </b-tab>
          <b-tab :title="'Anomalies' + (veilleAnomalies.length ? ' (' + veilleAnomalies.length + ')' : '')" :title-link-class="veilleAnomalies.length ? 'text-danger' : 'text-muted'">
            <div v-if="!veilleAnomalies.length" class="text-center text-muted py-4">Aucune anomalie pour ce produit</div>
            <div v-else class="table-responsive mt-2">
              <table class="table table-sm table-bordered table-hover">
                <thead class="thead-light">
                  <tr>
                    <th>Date détection</th>
                    <th>Prix extrait</th>
                    <th>Prix TTC</th>
                    <th>Prix promo</th>
                    <th>Ratio</th>
                    <th>Raison</th>
                    <th>Traité</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in veilleAnomalies" :key="row.id" :class="row.raison === 'trop_bas' ? 'table-danger' : 'table-warning'">
                    <td style="white-space:nowrap;font-size:0.82em">{{ row.date_detection }}</td>
                    <td>{{ row.prix_extrait != null ? Number(row.prix_extrait).toFixed(2) + ' €' : '—' }}</td>
                    <td>{{ row.prix_ttc != null ? Number(row.prix_ttc).toFixed(2) + ' €' : '—' }}</td>
                    <td>{{ row.prix_promo != null ? Number(row.prix_promo).toFixed(2) + ' €' : '—' }}</td>
                    <td><strong>{{ row.ratio != null ? Number(row.ratio).toFixed(3) : '—' }}</strong></td>
                    <td><span class="badge" :class="row.raison === 'trop_bas' ? 'badge-danger' : 'badge-warning'">{{ row.raison }}</span></td>
                    <td style="text-align:center">
                      <span class="badge" :class="row.process ? 'badge-success' : 'badge-secondary'">{{ row.process ? 'Oui' : 'Non' }}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </b-tab>
        </b-tabs>
      </template>
    </b-modal>

    <!-- datalist suggestions depuis le premier produit chargé -->
    <datalist id="import-data-key-suggestions">
      <option v-for="col in importDataKeySuggestions" :key="col" :value="col" />
    </datalist>

    <!-- Modal ajout clé data dans le query builder -->
    <b-modal v-model="showQueryDataKeyModal" title="Ajouter une clé data" size="sm" ok-title="Ajouter" :ok-disabled="!queryDataKey.trim()" @ok="confirmAddDataKeyQuery">
      <b-form-group label="Clé dans data" description="La variable insérée sera ${data.data_<clé>}">
        <b-form-input v-model="queryDataKey" size="sm" placeholder="ex: auteur" list="import-data-key-suggestions" />
      </b-form-group>
    </b-modal>

    <b-modal v-model="conditionTestDataVisible" title="Données" size="lg" hide-footer @hide="conditionTestDataVisible = false; conditionTestDataRow = null">
      <table v-if="conditionTestDataRow" class="table table-sm table-bordered">
        <tbody>
          <tr v-for="(val, key) in (typeof conditionTestDataRow.data === 'string' ? JSON.parse(conditionTestDataRow.data || '{}') : (conditionTestDataRow.data || {}))" :key="key">
            <th style="width:35%; background:#f8f9fa; white-space:nowrap">{{ key }}</th>
            <td style="word-break:break-word">{{ val }}</td>
          </tr>
        </tbody>
      </table>
    </b-modal>

  </div>
</template>

<script>
import QueryBuilderGroup from './QueryBuilderGroup.vue';
import ace_editor from './ace_editor.vue';
import json_schema_editor from './json_schema_editor.vue';
import library from '../services/library.js';
import rules_editor from './rules_editor.vue';

// Composant récursif pour l'arbre de catégories
const CategoryTreeItem = {
  name: 'CategoryTreeItem',
  props: ['node', 'selectedId'],
  data() {
    return {
      expanded: false
    };
  },
  methods: {
    toggleExpand() {
      if (this.node.nodes && this.node.nodes.length > 0) {
        this.expanded = !this.expanded;
      }
    },
    selectNode() {
      this.$emit('select', this.node.id);
    }
  },
  render(h) {
    const hasChildren = this.node.nodes && this.node.nodes.length > 0;
    
    return h('div', { class: 'category-tree-node' }, [
      // Ligne de la catégorie
      h('div', { 
        class: {
          'category-tree-row': true,
          'selected': this.selectedId === this.node.id
        }
      }, [
        // Triangle expand/collapse
        h('span', {
          class: 'category-toggle',
          style: {
            visibility: hasChildren ? 'visible' : 'hidden'
          },
          on: {
            click: (e) => {
              e.stopPropagation();
              this.toggleExpand();
            }
          }
        }, [
          h('i', {
            class: this.expanded ? 'fas fa-caret-down' : 'fas fa-caret-right'
          })
        ]),
        // Checkbox
        h('input', {
          attrs: { type: 'checkbox' },
          domProps: { checked: this.selectedId === this.node.id },
          class: 'category-checkbox',
          on: {
            click: () => {
              this.selectNode();
            }
          }
        }),
        // Texte
        h('span', {
          class: 'category-text',
          on: {
            click: () => {
              this.selectNode();
            }
          }
        }, this.node.text)
      ]),
      // Enfants (si étendu)
      this.expanded && hasChildren ? h('div', { class: 'category-tree-children' },
        this.node.nodes.map(child => 
          h('category-tree-item', {
            key: child.uniqueId,
            props: {
              node: child,
              selectedId: this.selectedId
            },
            on: {
              select: (id) => this.$emit('select', id)
            }
          })
        )
      ) : null
    ].filter(Boolean));
  }
};

export default {
  builder_info: 'Interface de suivi scraping produits: filtres, configuration des scrapers, lancement de recherche URL et validation des résultats par domaine/type.',
  components: {
    CategoryTreeItem,
    QueryBuilderGroup,
    ace_editor,
    json_schema_editor,
    rules_editor,
  },
  inject: {
    database:   { from: '$database',   default: null },
    treatments: { from: '$treatments', default: null },
    json2csv:   { from: '$json2csv',   default: null },
    $:          { from: '$jquery',     default: null },
    workers:    { from: '$workers',    default: null },
    library:    { from: '$library',    default: null },
    projects:   { from: '$projects',   default: null },
    Chart:      { from: '$Chart',      default: null },
    clone:      { from: '$clone',      default: null },
  },
  props: ['format', 'data', 'client', 'datasource'],
  data() {
    return {
      builderId: 'qb-scraping-' + Math.random().toString(36).substr(2, 8),
      filtersOpen: true,
      whereClause: '',
      statusDomainFilter: { domain: '', statuses: [] },
      loading: true,
      products: [],
      total: 0,
      page: 1,
      pageSize: 10,
      pageSizeOptions: [10, 20, 50, 100, 200, 500],
      selectedIds: [],
      categoriesMap: {},
      scrapingConfigs: [],
      scrapingMap: {},
      defaultLang: 'fr',
      modalVisible: false,
      modalProduct: null,
      modalDataExpanded: false,
      modalProductIndex: -1,
      modalCfg: null,
      modalRow: null,
      modalEditUrl: '',
      modalEditStatut: '',
      modalEditVeilleTarifaire: 0,
      modalSaving: false,
      modalLaunching: false,
      modalLaunchStatus: '',
      scenarioUid: null,
      veilleScenarioUid: null,
      inlineEditKey: null,
      inlineEditUrl: '',
      inlineSaving: false,
      inlineLaunchKeys: {},
      scrapingQueue: [],
      queueProcessing: false,
      queueAborted: false,
      sequentialMode: false,
      bulkScope: 'selected',
      bulkCfgKeys: [],
      bulkAction: 'scraping',
      bulkRunning: false,
      bulkDone: 0,
      bulkTotal: 0,
      bulkVeilleRunning: false,
      bulkVeilleDone: 0,
      bulkVeilleTotal: 0,
      bulkVeilleDropdownOpen: false,
      veilleInlineRunning: {},
      veilleQueue: [],
      veilleQueueProcessing: false,
      veilleQueueAborted: false,
      veilleResultModalVisible: false,
      veilleResultProduct: null,
      veilleResultLoading: false,
      veilleHistorique: [],
      veilleAnomalies: [],
      scrapingFilterStartTime: null,
      csvExporting: false,
      csvProgress: 0,
      statutOptions: [
        { value: '', text: '— (vide)' },
        { value: 'validé', text: 'Validé' },
        { value: 'refusé', text: 'Refusé' },
      ],
      // Config modal
      configView: false,
      veilleView: false,
      veilleTarifaireDirty: false,
      vtrDragIndex: null,
      vtrDragOver: null,
      configLoading: false,
      configList: [],
      groupesList: [],
      projectsList: [],
      productColumns: [],
      configEditing: null,
      // Renommage de groupe inline
      renameGroupModal: false,
      renameGroupTarget: null, // { domain, groupe, type }
      renameGroupValue: '',
      renameGroupSaving: false,
      configForm: {
        id: null, domain: '', type: 'scraping', rules: '', query: '',
        schema_result: '', identification: [], identification_result: '[]',
        prompt: '', projet: '', active: 1, veille_tarifaire: 0, before_code: '', after_code: '', render: '', groupe: '', conditions: '', priority: 0,
        version: -1, production: 0, description_version: '',
      },
      versionsList: [],
      versionSaveModal: false,
      versionSaveDescription: '',
      versionSaveProd: false,
      identificationFields: [],
      identificationResultFields: [],
      configSaving: false,
      syncingSiblings: false,
      schemaPropertiesList: [],

      // Wizard
      wizardActive: false,
      wizardStep: 1,
      wizardSkipConditions: false,

      visualRulesMode: false,
      visualRuleGroups: [],

      // Fonction globale de détermination du groupe d'un produit (chargée depuis la bibliothèque)
      globalGroupFn: null,
      groupRulesModalShow: false,
      groupRulesSaving: false,
      groupRulesTestOpen: false,
      groupRulesTestResults: [],
      veilleTarifaireModalShow: false,
      veilleTarifaireSaving: false,
      veilleTarifaireLoading: false,
      veilleTarifaireRegles: [],
      veilleTarifaireSourcesList: [],
      veilleTarifaireLoadedIds: [],

      categoriesMap: {},
      categoriesTree: [],
      selectedCategoryId: null,
      marquesList: [],
      categoryModalVisible: false,
      categoryTargetRule: null,
      categoryTargetVtr: null,
      queryBuilderFields: [
        { value: 'marque', label: 'Marque', type: 'text', useMarquesList: true },
        { value: 'prix_ht', label: 'Prix HT', type: 'number' },
        { value: 'prix_ttc', label: 'Prix TTC', type: 'number' },
        { value: 'categorie', label: 'Catégorie principale', type: 'categorie' },
        { value: 'categories', label: 'Catégories', type: 'categories' },
      ],
      // Query builder visuel
      queryCursorPos: 0,
      showQueryCaractModal: false,
      queryCaractColumn: '',
      queryCaractLang: 'fr',
      showQueryDataKeyModal: false,
      queryDataKey: '',
      // Test conditions
      conditionTestOpen: false,
      conditionTestRows: [],
      conditionTestLoading: false,
      conditionTestLimit: 20,
      conditionTestPage: 1,
      conditionTestTotal: 0,
      conditionTestDataRow: null,
      conditionTestDataVisible: false,
      testFilterBuilderId: 'qb-test-' + Math.random().toString(36).substr(2, 8),
      conditionTestWhere: '',
      // Scraping test panel
      statusIdentMode: 'standard',
      scrapTestOpen: false,
      scrapTestFilterBuilderId: 'qb-scrap-test-' + Math.random().toString(36).substr(2, 8),
      scrapTestWhere: '',
      scrapTestRows: [],
      scrapTestLoading: false,
      scrapTestLimit: 10,
      scrapTestPage: 1,
      scrapTestTotal: 0,
      scrapTestSelectedProduct: null,
      scrapTestSelectedIds: [],
      scrapTestUrl: '',
      scrapTestLaunching: false,
      scrapTestRunningBulk: false,
      scrapTestStatus: '',
      scrapTestResults: {},
      scrapTestValidatingId: null,
      scrapTestResultFiles: [],
      scrapTestResult: null,
      // Config list grouping & drag-and-drop
      expandedGroups: {},
      draggingCfgId: null,
      dragOverCfgId: null,
      dragSourceGroupKey: null,
      prioritySaving: false,
      // Sync champs entre configs du même domaine/groupe
      syncFields: { projet: false, schema: false, prompt: false },
      // Test de routage (depuis le listing)
      routingTestActive: false,
      routingTestDomain: null,
      routingTestGroupe: null,
      routingTestRows: [],
      routingTestLoading: false,
      routingTestTotal: 0,
      routingTestPage: 1,
      routingTestLimit: 20,
      routingTestResolutions: {},
      routingResolutionLoading: false,
      routingTestRunning: false,
      routingTestRunStatus: '',
      routingTestFilterBuilderId: 'qb-routing-' + Math.random().toString(36).substr(2, 8),
      routingTestWhere: '',
      routingTestSelectedIds: [],
      configFormDirty: false,
      testColsVisible: [], // tableau de clés visibles ([] = aucune, rempli après chargement)
      listingExtraCols: [], // colonnes supplémentaires visibles dans le listing principal
      conditionTestRowKeys: [], // clés détectées depuis les lignes chargées
      conditionTestDataKeys: [], // clés détectées dans le JSON data
      routingTestRowKeys: [],   // idem pour le test de routage
      routingTestDataKeys: [],
    };
  },
  computed: {
    scrapingQueueKeySet() {
      return new Set(this.scrapingQueue.map(q => q.key));
    },
    veilleQueueIdSet() {
      return new Set(this.veilleQueue.map(q => q.product.id));
    },
    hasVeilleTarifaireConfig() {
      return this.ds.showVeilleTarifaire !== false &&
        this.scrapingConfigs.some(c => c.veille_tarifaire);
    },
    scrapTestPseudoCfg() {
      let fn = null;
      const rules = this.configForm.rules;
      if (rules && rules.trim() && rules.trim() !== 'return true;') {
        try { fn = new Function('data', rules); } catch(e) {}
      }
      return { groupe: this.configForm.groupe || null, fn, domain: this.configForm.domain };
    },
    scrapTestApplicableRows() {
      return this.scrapTestRows.filter(r => this.scrapTestPassesCurrentConfig(r));
    },
    scrapTestSelectedApplicable() {
      return this.scrapTestRows.filter(r => this.scrapTestSelectedIds.includes(r.id) && this.scrapTestPassesCurrentConfig(r));
    },
    scrapTestCurrentResult() {
      if (!this.scrapTestSelectedProduct) return null;
      return this.scrapTestResults[this.scrapTestSelectedProduct.id] || this.scrapTestResult || null;
    },
    listingDataKeys() {
      const keys = new Set();
      for (const p of (this.products || [])) {
        if (p.data) {
          try {
            const obj = typeof p.data === 'string' ? JSON.parse(p.data) : p.data;
            for (const k of Object.keys(obj || {})) keys.add(k);
          } catch {}
        }
      }
      return [...keys];
    },
    allListingExtraCols() {
      const alreadyShown = new Set(['id', ...(this.ds.listingColumns || []).map(c => c.key)]);
      const cols = [];
      for (const f of (this.ds.qbMainFilters || [])) {
        if (!alreadyShown.has(f.id)) cols.push({ key: f.id, label: f.label });
      }
      for (const key of this.listingDataKeys) {
        cols.push({ key: `data.${key}`, label: `🗂 ${key}`, fromData: key });
      }
      return cols;
    },
    visibleListingExtraCols() {
      return this.allListingExtraCols.filter(c => this.listingExtraCols.includes(c.key));
    },
    visibleGroupes() {
      const gs = new Set();
      // Groupe fixe fourni par le datasource (ex: scraping_file avec groupe de session)
      if (this.ds.fixedGroupe) gs.add(this.ds.fixedGroupe);
      for (const p of (Array.isArray(this.products) ? this.products : [])) {
        if (p.groupe) gs.add(p.groupe);
      }
      return gs;
    },
    allConditionTestCols() {
      // Colonnes fixes définies par le datasource ou par défaut
      const ALWAYS_SKIP = new Set(['id', 'nom', 'groupe', '_passes']);
      const labelFor = (key) => {
        if (this.ds.conditionTestColumns) {
          const found = this.ds.conditionTestColumns.find(c => c.key === key);
          if (found) return found.label;
        }
        const LABELS = { id_cms: 'Clé externe', marque: 'Marque', source: 'Source', prix_ht: 'Prix HT', prix_ttc: 'Prix TTC', reference: 'Référence', ean13: 'EAN13', upc: 'UPC', nom_fr: 'Nom FR', nom_es: 'Nom ES', id_source: 'id_source', urls_image: 'URLs images', categories: 'Catégories', categorie_principale: 'Catégorie principale', default_category: 'Catégorie principale' };
        return LABELS[key] || key;
      };
      const cols = [];
      const seen = new Set();
      // 1. Colonnes définies explicitement par le datasource (dans l'ordre demandé)
      if (this.ds.conditionTestColumns && this.ds.conditionTestColumns.length) {
        for (const c of this.ds.conditionTestColumns) {
          if (!seen.has(c.key)) { cols.push(c); seen.add(c.key); }
        }
      }
      // 2. Colonnes détectées depuis les lignes chargées
      for (const key of this.conditionTestRowKeys) {
        if (!seen.has(key) && !ALWAYS_SKIP.has(key)) {
          cols.push({ key, label: labelFor(key) });
          seen.add(key);
        }
      }
      // 3. Colonnes extraites du JSON data (si showDataKeys)
      for (const key of this.conditionTestDataKeys) {
        const dkey = `data.${key}`;
        if (!seen.has(dkey)) {
          cols.push({ key: dkey, label: key, fromData: key });
          seen.add(dkey);
        }
      }
      // 4. Si rien du tout, fallback
      if (!cols.length) {
        cols.push({ key: 'marque', label: 'Marque' });
        if (this.ds.conditionTestSourceCol) cols.push({ key: 'source', label: 'Source' });
        cols.push({ key: 'prix_ht', label: 'Prix HT' }, { key: 'prix_ttc', label: 'Prix TTC' }, { key: 'categories', label: 'Catégories' });
      }
      return cols;
    },
    allRoutingTestCols() {
      const ALWAYS_SKIP = new Set(['id', 'groupe', '_passes']);
      const labelFor = (key) => {
        if (this.ds.conditionTestColumns) {
          const found = this.ds.conditionTestColumns.find(c => c.key === key);
          if (found) return found.label;
        }
        const LABELS = { id_cms: 'Clé externe', nom: 'Nom', nom_fr: 'Nom FR', nom_es: 'Nom ES', marque: 'Marque', source: 'Source', prix_ht: 'Prix HT', prix_ttc: 'Prix TTC', reference: 'Référence', ean13: 'EAN13', upc: 'UPC', id_source: 'id_source', urls_image: 'URLs images', categories: 'Catégories', categorie_principale: 'Catégorie principale', default_category: 'Catégorie principale' };
        return LABELS[key] || key;
      };
      const cols = [];
      const seen = new Set();
      if (this.ds.conditionTestColumns && this.ds.conditionTestColumns.length) {
        for (const c of this.ds.conditionTestColumns) {
          if (!seen.has(c.key)) { cols.push(c); seen.add(c.key); }
        }
      }
      for (const key of this.routingTestRowKeys) {
        if (!seen.has(key) && !ALWAYS_SKIP.has(key)) {
          cols.push({ key, label: labelFor(key) });
          seen.add(key);
        }
      }
      for (const key of this.routingTestDataKeys) {
        const dkey = `data.${key}`;
        if (!seen.has(dkey)) {
          cols.push({ key: dkey, label: key, fromData: key });
          seen.add(dkey);
        }
      }
      if (!cols.length) {
        cols.push({ key: 'nom', label: 'Nom' }, { key: 'marque', label: 'Marque' });
        if (this.ds.conditionTestSourceCol) cols.push({ key: 'source', label: 'Source' });
        cols.push({ key: 'prix_ht', label: 'Prix HT' });
      }
      return cols;
    },
    visibleConditionTestCols() {
      return this.allConditionTestCols.filter(c => this.testColsVisible.includes(c.key));
    },
    visibleRoutingTestCols() {
      return this.allRoutingTestCols.filter(c => this.testColsVisible.includes(c.key));
    },
    visibleScrapingConfigs() {
      const seen = new Set();
      return this.scrapingConfigs.filter(cfg => {
        if (cfg.groupe && !this.visibleGroupes.has(cfg.groupe)) return false;
        const key = `${cfg.domain}__${cfg.groupe || ''}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    },
    groupedConfigList() {
      const groups = new Map();
      for (const cfg of this.configList) {
        const key = `${cfg.domain}__${cfg.groupe || ''}`;
        if (!groups.has(key)) {
          groups.set(key, { key, domain: cfg.domain, groupe: cfg.groupe || null, configs: [] });
        }
        groups.get(key).configs.push(cfg);
      }
      for (const group of groups.values()) {
        group.configs.sort((a, b) => (a.priority || 0) - (b.priority || 0));
      }
      return Array.from(groups.values());
    },
    siblingConfigs() {
      if (!this.configForm || !this.configForm.domain) return [];
      const domain = (this.configForm.domain || '').trim();
      const groupe = this.configForm.groupe && this.configForm.groupe.trim() ? this.configForm.groupe.trim() : null;
      return this.configList.filter(c => {
        const cDomain = (c.domain || '').trim();
        const cGroupe = c.groupe && c.groupe.trim() ? c.groupe.trim() : null;
        return cDomain === domain && cGroupe === groupe;
      }).sort((a, b) => (a.priority || 0) - (b.priority || 0));
    },
    routingTestConfigs() {
      if (!this.routingTestDomain) return [];
      const domain = this.routingTestDomain;
      const groupe = this.routingTestGroupe;
      return this.scrapingConfigs
        .filter(c => c.domain === domain && c.groupe === groupe)
        .sort((a, b) => (a.priority || 0) - (b.priority || 0));
    },
    modalProductData() {
      if (!this.modalProduct) return {};
      if (this.ds.getModalProductData) return this.ds.getModalProductData(this.modalProduct, this.categoriesMap);
      if (!this.modalProduct.data) return {};
      try { return JSON.parse(this.modalProduct.data); } catch (e) { return {}; }
    },
    hasModalProductData() {
      return this.modalProduct && (this.ds.getModalProductData || this.modalProduct.data);
    },
    importDataKeySuggestions() {
      const first = this.products && this.products[0];
      if (!first) return [];
      try {
        const d = typeof first.data === 'string' ? JSON.parse(first.data) : (first.data || {});
        return Object.keys(d);
      } catch (e) { return []; }
    },
    selectAll() {
      return this.products.length > 0 && this.products.every(p => this.selectedIds.includes(p.id));
    },
    selectIndeterminate() {
      return this.selectedIds.length > 0 && !this.selectAll;
    },
    modalTitle() {
      if (!this.modalProduct || !this.modalCfg) return 'Détail scraping';
      const pos = this.modalProductIndex >= 0 ? ` (${this.modalProductIndex + 1}/${this.products.length})` : '';
      return `${this.getNom(this.modalProduct)} — ${this.modalCfg.domain}${pos}`;
    },
    modalHasPrev() { return this.modalProductIndex > 0; },
    modalHasNext() { return this.modalProductIndex >= 0 && this.modalProductIndex < this.products.length - 1; },
    showReprocessButton() {
      if (!this.modalRow) return false;
      const smdValide = (this.modalRow.statut_smd || '').toLowerCase().includes('valid');
      const statutValide = (this.modalRow.statut || '').toLowerCase().includes('valid');
      const statutRefuse = (this.modalRow.statut || '').toLowerCase().includes('refus');
      // Ne pas montrer si statut_smd validé ET statut refusé
      if (smdValide && statutRefuse) return false;
      // Montrer si statut_smd validé OU statut validé
      return smdValide || statutValide;
    },
    wizardTotalSteps() {
      return this.ds.showVeilleTarifaire === false ? 5 : 6;
    },
    identificationOptions() {
      // Override direct depuis le datasource
      if (this.ds.identificationOptions) return this.ds.identificationOptions;
      const options = [];
      // Colonnes JSON qui nécessitent une langue
      const jsonColumns = this.ds.jsonColumns !== undefined
        ? this.ds.jsonColumns
        : ['nom', 'description', 'meta_title', 'meta_description', 'description_courte', 'lien_rewrite'];
      const languages = this.client?.prestashop?.languages || ['fr'];
      
      // Filtre pour ne garder que les champs pertinents pour l'identification
      const filteredColumns = this.productColumns.filter(col => {
        const colLower = col.toLowerCase();
        
        // Whitelist : codes identifiants uniques
        const isIdentifier = /^(ean|isbn|gtin|upc|asin|mpn|sku|ref|reference)(_|$|\d)/i.test(col);
        
        // Whitelist : noms/titres (sans meta/description courte) — autorise suffixes _fr, _en, etc.
        const isName = /^(nom|name|title|titre|libelle)(_[a-z]{2,5})?$/i.test(col);
        
        // Whitelist : modèle
        const isModel = /^(model|modele)$/i.test(col);
        
        // Whitelist pour autoriser marque / code fabricant
        const isBrand = /^(marque|brand|manufacturer|fabricant)$/i.test(col) ||
          /^manufacturer_code$/i.test(col);
        
        // Blacklist stricte
        const isBlacklisted = 
          /prix|price|tarif|cost|montant|promo|achat|ht|ttc|devise/i.test(col) ||
          /^id_|_id$|^id$/i.test(col) ||
          /description_courte|desc_courte|meta_|caracteristique|spec/i.test(col) ||
          /image|img|photo|picture|thumbnail|media/i.test(col) ||
          /stock|quantite|quantity|qty|disponibilite/i.test(col) ||
          /^url|link|lien_rewrite|href/i.test(col) ||
          /date|created|updated|modified|timestamp/i.test(col) ||
          /categor|category|default_category/i.test(col) ||
          /poids|weight|hauteur|largeur|profondeur|dimension/i.test(col) ||
          /taxe|tax|status|statut|condition|show_/i.test(col) ||
          /auteur|author|editeur|publisher|artiste|artist/i.test(col);
        
        return (isIdentifier || isName || isModel || isBrand) && !isBlacklisted;
      });
      
      for (const col of filteredColumns) {
        if (jsonColumns.includes(col)) {
          // Ajouter une option par langue
          for (const lang of languages) {
            options.push({ value: `${col}_${lang}`, text: `${col} (${lang})`, isJson: true, column: col, lang });
          }
        } else {
          // Colonne simple
          options.push({ value: col, text: col, isJson: false });
        }
      }
      return options;
    },
    schemaProperties() {
      // Utiliser la liste mise à jour
      return this.schemaPropertiesList;
    },
    identificationSchemaProperties() {
      // Pas de filtre ici : l'utilisateur peut nommer ses champs scrapés comme il veut
      return this.schemaPropertiesList;
    },
    languageOptions() {
      return (this.client?.prestashop?.languages || ['fr']).map(l => ({ value: l, text: l.toUpperCase() }));
    },
    queryVariableOptions() {
      const options = [];
      const seen = new Set();
      // En premier : les champs d'identification déjà configurés
      this.identificationFields.forEach(f => {
        if (typeof f === 'string' && f.trim() && f !== 'caracteristiques_custom' && f !== 'data_custom') {
          if (!seen.has(f)) {
            seen.add(f);
            const label = (this.identificationOptions.find(o => o.value === f) || {}).text || f;
            options.push({ value: f, text: label, segType: 'variable' });
          }
        } else if (typeof f === 'object' && f.type === 'caracteristique' && f.column && f.column.trim()) {
          const key = `carac_${this._toSafeKey(f.column)}_${f.lang}`;
          if (!seen.has(key)) {
            seen.add(key);
            options.push({ text: `📋 ${f.column} (${f.lang})`, segType: 'caracteristique', column: f.column, lang: f.lang });
          }
        } else if (typeof f === 'object' && f.type === 'data_key' && f.key && f.key.trim()) {
          const safeKey = this._toSafeKey(f.key);
          const k = `data_${safeKey}`;
          if (!seen.has(k)) {
            seen.add(k);
            options.push({ value: `data_${safeKey}`, text: `🗂 ${f.key}`, segType: 'data_key' });
          }
        }
      });
      // Ensuite : les autres champs disponibles (non déjà listés)
      this.identificationOptions.forEach(opt => {
        if (!seen.has(opt.value)) {
          seen.add(opt.value);
          options.push({ value: opt.value, text: opt.text, segType: 'variable' });
        }
      });
      return options;
    },
    previewCleanDomain() {
      return this.performCleanDomain(this.configForm.domain);
    },
    visualRulesModeKey: {
      get() { return this.visualRulesMode ? 'visual' : 'code'; },
      set(val) { this.toggleRulesMode(val === 'visual'); },
    },
    ds() {
      return this.datasource || {};
    },
    wizardStepValid() {
      if (this.wizardStep === 1) return this.configForm.domain.trim().length > 0;
      if (this.wizardStep === 2) return true; // conditions de déclenchement optionnelles
      if (this.wizardStep === 3) return true; // schema optionnel
      if (this.wizardStep === 4) {
        // Au moins un champ valide (non vide et non custom incomplet)
        return this.identificationFields.length > 0 && this.identificationFields.some(f => {
          if (!f) return false;
          if (typeof f === 'object') return (
            (f.type === 'caracteristique' && f.column && f.column.trim()) ||
            (f.type === 'data_key' && f.key && f.key.trim())
          );
          return f.trim() && f !== 'caracteristiques_custom' && f !== 'data_custom';
        });
      }
      return true;
    },
  },
  watch: {
    hasVeilleTarifaireConfig(val) {
      if (!val && this.bulkAction === 'veille') this.bulkAction = 'scraping';
    },
    veilleTarifaireRegles: {
      deep: true,
      handler() {
        if (this.veilleView && !this.veilleTarifaireLoading) this.veilleTarifaireDirty = true;
      },
    },
    statusDomainFilter: {
      deep: true,
      async handler() {
        this.selectedIds = [];
        this.page = 1;
        await this._refreshServerTime();
        this.loadPage();
      }
    },
    selectedIds(val) {
      if (val.length === 0 && this.bulkScope === 'selected') this.bulkScope = 'page';
    },
    identificationFields: {
      deep: true,
      handler() {
        this.updateIdentificationFromFields();
      }
    },
    identificationResultFields: {
      deep: true,
      handler() {
        this.updateIdentificationResultFromFields();
      }
    },
    'configForm.query': {
      handler() {
        this.generateBeforeCode();
      }
    },
    visualRuleGroups: {
      deep: true,
      handler() {
        if (this.visualRulesMode) {
          this.visualRulesToCode();
        }
      }
    },
    visualRulesMode(newVal, oldVal) {
      // Quand on passe en mode code (false)
      if (!newVal && oldVal) {
        setTimeout(() => {
          if (this.$refs.rulesEditor) {
            this.$refs.rulesEditor.load(this.configForm.rules || 'return true;');
          }
          if (this.$refs.wizardRulesEditor) {
            this.$refs.wizardRulesEditor.load(this.configForm.rules || 'return true;');
          }
        }, 100);
      }
    },
    conditionTestOpen(val) {
      if (val) this.$nextTick(() => this.initTestFilterBuilder());
    },
    scrapTestOpen(val) {
      if (val) this.$nextTick(() => { this.initScrapTestFilterBuilder(); this.runScrapTestSearch(1); });
    },
    routingTestActive(val) {
      if (val) this.$nextTick(() => { this.initRoutingTestFilterBuilder(); this.runRoutingTestSearch(1); });
    },
    configForm: {
      deep: true,
      handler() {
        if (this._suppressDirty) return;
        this.configFormDirty = true;
      },
    },
  },
  async mounted() {
    try {
      const lang = this.client?.prestashop?.languages?.[0];
      if (lang) this.defaultLang = lang;
    } catch (e) {}

    if (this.ds.qbFields) {
      this.queryBuilderFields = this.ds.qbFields;
    }

    await Promise.all([
      this.loadCategories(),
      this.loadScrapingConfigs(),
      this.fetchScenarioUid(),
      this.loadGroupRules(),
    ]);

    this.$nextTick(() => this.initQueryBuilder());
    await this.loadPage();
    this._closeVeilleDropdown = () => { this.bulkVeilleDropdownOpen = false; };
    document.addEventListener('click', this._closeVeilleDropdown);
  },
  beforeDestroy() {
    if (this._closeVeilleDropdown) document.removeEventListener('click', this._closeVeilleDropdown);
    try { this.$('#' + this.builderId).queryBuilder('destroy'); } catch (e) {}
    try { this.$('#' + this.testFilterBuilderId).queryBuilder('destroy'); } catch (e) {}
    try { this.$('#' + this.scrapTestFilterBuilderId).queryBuilder('destroy'); } catch (e) {}
    try { this.$('#' + this.routingTestFilterBuilderId).queryBuilder('destroy'); } catch (e) {}
  },
  methods: {
    initTestFilterBuilder() {
      const self = this;
      const filters = this.ds.qbTestFilters || [
        { id: 'id',               label: 'id',               type: 'integer' },
        { id: 'id_cms',           label: 'id_cms',           type: 'integer' },
        { id: 'reference',        label: 'Référence',        type: 'string'  },
        { id: 'nom',              label: 'Nom',              type: 'string'  },
        { id: 'marque',           label: 'Marque',              type: 'string'  },
        { id: 'default_category', label: 'Catégorie principale', type: 'integer' },
        { id: 'prix_ht',          label: 'Prix HT',             type: 'double'  },
        { id: 'prix_ttc',         label: 'Prix TTC',            type: 'double'  },
        { id: 'categorie_nom',    label: 'Dans les catégories (nom)',  type: 'string'  },
      ];
      const operators = [
        { type: 'equal' }, { type: 'not_equal' },
        { type: 'less' },  { type: 'less_or_equal' },
        { type: 'greater' }, { type: 'greater_or_equal' },
        { type: 'contains' }, { type: 'not_contains' },
        { type: 'begins_with' }, { type: 'ends_with' },
        { type: 'is_empty',     apply_to: ['string','number','datetime','boolean'] },
        { type: 'is_not_empty', apply_to: ['string','number','datetime','boolean'] },
      ];
      try { this.$('#' + this.testFilterBuilderId).queryBuilder('destroy'); } catch (e) {}
      this.$('#' + this.testFilterBuilderId).queryBuilder({ filters, operators });
      this.$('#' + this.testFilterBuilderId).on('change afterDeleteRule.queryBuilder afterDeleteGroup.queryBuilder', function () {
        try {
          let sql = self.$('#' + self.testFilterBuilderId).queryBuilder('getSQL', false).sql;
          self.conditionTestWhere = self.postProcessSQL(sql);
        } catch (e) {
          self.conditionTestWhere = '';
        }
      });
    },
    initQueryBuilder() {
      const self = this;
      const filters = this.ds.qbMainFilters || [
        { id: 'id',               label: 'id',               type: 'integer' },
        { id: 'id_cms',           label: 'id_cms',           type: 'integer' },
        { id: 'reference',        label: 'Référence',        type: 'string'  },
        { id: 'nom',              label: 'Nom',              type: 'string'  },
        { id: 'marque',           label: 'Marque',           type: 'string'  },
        { id: 'status',           label: 'Statut',           type: 'string'  },
        { id: 'ean13',            label: 'EAN13',            type: 'string'  },
        { id: 'default_category', label: 'Catégorie principale', type: 'integer' },
        { id: 'prix_ht',          label: 'Prix HT',             type: 'double'  },
        { id: 'prix_ttc',         label: 'Prix TTC',            type: 'double'  },
        { id: 'quantite',         label: 'Quantité',           type: 'integer' },
        { id: 'categorie_nom',    label: 'Dans les catégories (nom)',  type: 'string'  },
      ];
      const operators = [
        { type: 'equal' }, { type: 'not_equal' },
        { type: 'less' },  { type: 'less_or_equal' },
        { type: 'greater' }, { type: 'greater_or_equal' },
        { type: 'contains' }, { type: 'not_contains' },
        { type: 'begins_with' }, { type: 'ends_with' },
        { type: 'is_empty',     apply_to: ['string','number','datetime','boolean'] },
        { type: 'is_not_empty', apply_to: ['string','number','datetime','boolean'] },
      ];

      try { this.$('#' + this.builderId).queryBuilder('destroy'); } catch (e) {}
      this.$('#' + this.builderId).queryBuilder({ filters, operators });

      this.$('#' + this.builderId).on('change afterDeleteRule.queryBuilder afterDeleteGroup.queryBuilder', function () {
        try {
          let sql = self.$('#' + self.builderId).queryBuilder('getSQL', false).sql;
          self.whereClause = self.postProcessSQL(sql);
        } catch (e) {
          self.whereClause = '';
        }
      });
    },

    async applyFilters() {
      this.selectedIds = [];
      this.page = 1;
      await this._refreshServerTime();
      this.loadPage();
    },

    async _refreshServerTime() {
      if (this.statusDomainFilter.domain && this.statusDomainFilter.statuses.length) {
        try {
          this.scrapingFilterStartTime = await this.database.getServerTime();
        } catch (e) {
          this.scrapingFilterStartTime = null;
        }
      } else {
        this.scrapingFilterStartTime = null;
      }
    },

    resetFilters() {
      try { this.$('#' + this.builderId).queryBuilder('reset'); } catch (e) {}
      this.whereClause = '';
      this.statusDomainFilter = { domain: '', statuses: [] };
      this.page = 1;
      this.loadPage();
    },

    postProcessSQL(sql) {
      if (!sql) return '';
      // data.X filter IDs (e.g. id: 'data.manufacturer_code') → JSON_UNQUOTE(JSON_EXTRACT)
      // Handle: `data`.`key` (QB splits on dot), `data.key` (single backtick), data.key (no backtick)
      sql = sql.replace(/`data`\.`([^`]+)`/gi, (_, key) => `JSON_UNQUOTE(JSON_EXTRACT(\`data\`, '$.${key}'))`);
      sql = sql.replace(/`data\.([^`]+)`/gi,    (_, key) => `JSON_UNQUOTE(JSON_EXTRACT(\`data\`, '$.${key}'))`);
      sql = sql.replace(/(?<![$.`\w])data\.([a-zA-Z_\u00C0-\u024F][a-zA-Z0-9_\u00C0-\u024F]*)/g, (_, key) => `JSON_UNQUOTE(JSON_EXTRACT(\`data\`, '$.${key}'))`);
      if (this.ds.nomSqlTransform !== false) {
        // nom → JSON_EXTRACT pour la langue par défaut
        sql = sql.replace(/`nom`(\s+NOT)?\s+LIKE\s+('[^']*')/gi,
          (_, not, val) => `JSON_EXTRACT(\`nom\`, '$.${this.defaultLang}')${not || ''} LIKE ${val}`);
        sql = sql.replace(/`nom`\s*(=|!=|<>)\s*('[^']*')/gi,
          (_, op, val) => `JSON_EXTRACT(\`nom\`, '$.${this.defaultLang}') ${op} ${val}`);
      }
      // categorie_nom → résoudre les noms en JSON_CONTAINS sur categories
      // QueryBuilder peut générer LIKE '%val%' ou LIKE('%val%') et sans backticks → \(? \)?
      sql = sql.replace(/`?categorie_nom`?\s+(NOT\s+LIKE|LIKE)\s*\(?'([^']*)'\)?/gi, (_, notlike, pattern) => {
        return this._catPatternToJsonContains(pattern, notlike.toUpperCase().startsWith('NOT'));
      });
      sql = sql.replace(/`?categorie_nom`?\s*(=|!=)\s*\(?'([^']*)'\)?/gi, (_, op, val) => {
        return this._catPatternToJsonContains(val, op === '!=', 'equal');
      });
      // Nettoyer les résidus categorie_nom (is_empty, is_not_empty, etc.)
      sql = sql.replace(/\(`?categorie_nom`?[^)]*\)/gi, '1=1');
      sql = sql.replace(/`?categorie_nom`?\s*\S+\s*\(?'[^']*'\)?/gi, '1=1');
      sql = sql.replace(/`?categorie_nom`?[^\s,)]*/gi, '1=1');
      // is_empty / is_not_empty pour les autres champs
      sql = sql.replace(/`([^`]*)` = ''/gi,  "(`$1` = '' or `$1` is NULL)");
      sql = sql.replace(/`([^`]*)` != ''/gi, "(`$1` != '' and `$1` is not NULL)");
      return sql;
    },

    _catPatternToJsonContains(pattern, negated, mode) {
      const lval = pattern.replace(/%/g, '').toLowerCase();
      const isContains = !mode && pattern.startsWith('%') && pattern.endsWith('%');
      const isBegins   = !mode && !pattern.startsWith('%') && pattern.endsWith('%');
      const isEnds     = !mode && pattern.startsWith('%') && !pattern.endsWith('%');
      let matchingIds;
      if (mode === 'equal' || (!isContains && !isBegins && !isEnds)) {
        matchingIds = Object.entries(this.categoriesMap)
          .filter(([, name]) => name.toLowerCase() === lval).map(([id]) => id);
      } else if (isContains) {
        matchingIds = Object.entries(this.categoriesMap)
          .filter(([, name]) => name.toLowerCase().includes(lval)).map(([id]) => id);
      } else if (isBegins) {
        matchingIds = Object.entries(this.categoriesMap)
          .filter(([, name]) => name.toLowerCase().startsWith(lval)).map(([id]) => id);
      } else {
        matchingIds = Object.entries(this.categoriesMap)
          .filter(([, name]) => name.toLowerCase().endsWith(lval)).map(([id]) => id);
      }
      if (!matchingIds.length) return negated ? '1=1' : '1=0';
      if (negated) return '(' + matchingIds.map(id => `NOT JSON_CONTAINS(\`categories\`, '${id}', '$')`).join(' AND ') + ')';
      return '(' + matchingIds.map(id => `JSON_CONTAINS(\`categories\`, '${id}', '$')`).join(' OR ') + ')';
    },

    async loadCategories() {
      const _catSelect = this.ds.categoriesSelect || 'id_cms, nom';
      const _catIsJson = this.ds.categoriesIsJson !== false;
      const rows = await this.database.get('select', 'categories', {
        select: _catSelect,
        end: 'LIMIT 10000',
      });
      const map = {};
      for (const row of rows || []) {
        if (this.datasource && this.datasource.mapCategoryRow) {
          const { id, name } = this.datasource.mapCategoryRow(row, this.defaultLang);
          map[id] = name;
        } else if (_catIsJson) {
          try {
            const nom = typeof row.nom === 'string' ? JSON.parse(row.nom) : row.nom;
            map[row.id_cms] = nom?.[this.defaultLang] || nom?.fr || String(row.id_cms);
          } catch { map[row.id_cms] = String(row.id_cms); }
        } else {
          map[row.id] = row.name_fr || String(row.id);
        }
      }
      this.categoriesMap = map;
    },
    async loadScrapingConfigs() {
      const configType = this.ds.configType || 'scraping';
      const rows = await this.database.get('select', 'scraping_config', {
        select: 'domain, type, veille_tarifaire, rules, before_code, groupe, conditions, priority, search_mode, search_url',
        where: `active = 1 AND type = '${configType}' AND production = 1`,
        end: 'ORDER BY priority ASC',
      });
      const seen = new Set();
      const configs = [];
      for (const row of rows || []) {
        const groupe = (row.groupe && row.groupe !== 'null') ? row.groupe : null;
        const conditions = (row.conditions && row.conditions !== 'null') ? row.conditions : null;
        const key = `${row.domain}__${row.type}__${groupe || ''}__${conditions || ''}`;
        if (!seen.has(key)) {
          seen.add(key);
          let fn = null;
          if (row.rules && row.rules.trim()) {
            try { fn = new Function('data', row.rules); } catch (e) { fn = null; }
          }
          const DIRECT_FIELDS = new Set(['nom', 'cle_externe']);
          const requiredDataCols = [];
          if (row.before_code) {
            for (const m of [...row.before_code.matchAll(/_dataJson\['([^']+)'\]/g)]) {
              if (!DIRECT_FIELDS.has(m[1])) requiredDataCols.push(m[1]);
            }
          }
          configs.push({ domain: row.domain, type: row.type, veille_tarifaire: row.veille_tarifaire, groupe, conditions, priority: row.priority ?? 0, key, fn, requiredDataCols, search_mode: row.search_mode || 'query', search_url: row.search_url || '' });
        }
      }
      // Trier : génériques (groupe null) en premier, puis par groupe alphabétique, puis par domain, puis par priority
      configs.sort((a, b) => {
        if (!a.groupe && b.groupe) return -1;
        if (a.groupe && !b.groupe) return 1;
        if (a.groupe !== b.groupe) return (a.groupe || '').localeCompare(b.groupe || '');
        if (a.domain !== b.domain) return a.domain.localeCompare(b.domain);
        return (a.priority || 0) - (b.priority || 0);
      });
      this.scrapingConfigs = configs;
    },
    async loadGroupRules() {
      try {
        const data = await library.load('scraping_group_rules', null, 'rules');
        if (!data || !data.group_mapping || !data.group_mapping.length) {
          this.globalGroupFn = null;
          return;
        }
        this.globalGroupFn = this._buildGroupFnFromRules(data.group_mapping);
      } catch (e) {
        this.globalGroupFn = null;
      }
    },
    /** Construit la globalGroupFn à partir du tableau de règles group_mapping */
    _generateGroupMappingCode(groupMapping) {
      if (!groupMapping || !groupMapping.length) return '';
      const lines = ['// [Généré par les règles de groupe scraping]'];
      lines.push('var _checkCond = function(cond, data) {');
      lines.push('  if (cond.field === "categories") {');
      lines.push('    var catIds; try { var _p=JSON.parse(data.categories||"[]"); catIds=Array.isArray(_p)?_p.map(String):[]; } catch(e){ catIds=String(data.categories||"").replace(/[\\[\\]]/g,"").split(",").map(function(s){return s.trim();}).filter(Boolean); }');
      lines.push('    var v = String(cond.value);');
      lines.push('    if (cond.operator === "==" || cond.operator === "LIKE") return catIds.indexOf(v) !== -1;');
      lines.push('    if (cond.operator === "!=" || cond.operator === "NOT LIKE") return catIds.indexOf(v) === -1;');
      lines.push('    return false;');
      lines.push('  }');
      lines.push('  if (cond.field === "categorie") {');
      lines.push('    var dc = String(data.categorie_principale || data.default_category || ""); var v = String(cond.value);');
      lines.push('    if (cond.operator === "==" || cond.operator === "LIKE") return dc === v;');
      lines.push('    if (cond.operator === "!=" || cond.operator === "NOT LIKE") return dc !== v;');
      lines.push('    return false;');
      lines.push('  }');
      lines.push('  var fv = data[cond.field] == null ? "" : String(data[cond.field]);');
      lines.push('  var cv = String(cond.value);');
      lines.push('  if (cond.operator === "==") return fv === cv;');
      lines.push('  if (cond.operator === "!=") return fv !== cv;');
      lines.push('  if (cond.operator === "includes") return fv.indexOf(cv) !== -1;');
      lines.push('  if (cond.operator === "!includes") return fv.indexOf(cv) === -1;');
      lines.push('  if (cond.operator === ">") return parseFloat(fv) > parseFloat(cv);');
      lines.push('  if (cond.operator === "<") return parseFloat(fv) < parseFloat(cv);');
      lines.push('  return false;');
      lines.push('};');
      lines.push('var _checkGroup = function(group, data) {');
      lines.push('  var conds = group.conditions || [];');
      lines.push('  if (!conds.length) return false;');
      lines.push('  if (group.operator === "OU") return conds.some(function(c){return _checkCond(c, data);});');
      lines.push('  return conds.every(function(c){return _checkCond(c, data);});');
      lines.push('};');
      lines.push('var _rules = ' + JSON.stringify(groupMapping) + ';');
      lines.push('data.groupe = null;');
      lines.push('for (var _i = 0; _i < _rules.length; _i++) {');
      lines.push('  var _rule = _rules[_i];');
      lines.push('  if (!_rule.groupe || !_rule.groupe.trim()) continue;');
      lines.push('  var _vrg = _rule.visualRuleGroups || [];');
      lines.push('  if (!_vrg.length) continue;');
      lines.push('  var _match = false;');
      lines.push('  for (var _j = 0; _j < _vrg.length; _j++) { if (_checkGroup(_vrg[_j], data)) { _match = true; break; } }');
      lines.push('  if (_match) { data.groupe = _rule.groupe; break; }');
      lines.push('}');
      return lines.join('\n');
    },
    _buildGroupFnFromRules(groupMapping) {
      const checkCond = (cond, product) => {
        if (cond.field === 'categories') {
          let catIds = [];
          try {
            const parsed = JSON.parse(product.categories || '[]');
            catIds = Array.isArray(parsed) ? parsed.map(String) : [];
          } catch(e) {
            catIds = String(product.categories || '').replace(/[\[\]]/g, '').split(',').map(s => s.trim()).filter(Boolean);
          }
          const condVal = String(cond.value);
          const result = cond.operator === '==' || cond.operator === 'LIKE' ? catIds.includes(condVal) : !catIds.includes(condVal);
          return result;
        }
        if (cond.field === 'categorie') {
          const defCat = String(product.categorie_principale || product.default_category || '');
          const condVal = String(cond.value);
          const result = cond.operator === '==' || cond.operator === 'LIKE' ? defCat === condVal : defCat !== condVal;
          return result;
        }
        const rawVal = cond.field && cond.field.startsWith('data.') ? (() => {
          const dataKey = cond.field.slice(5);
          let dataObj = {};
          try { dataObj = typeof product.data === 'string' ? JSON.parse(product.data) : (product.data || {}); } catch (e) {}
          return dataObj[dataKey];
        })() : product[cond.field];
        const fieldVal = rawVal == null ? '' : String(rawVal);
        const condVal = String(cond.value);
        switch (cond.operator) {
          case '==':        return fieldVal === condVal;
          case '!=':        return fieldVal !== condVal;
          case 'includes':  return fieldVal.includes(condVal);
          case '!includes': return !fieldVal.includes(condVal);
          case '>':         return parseFloat(fieldVal) > parseFloat(condVal);
          case '<':         return parseFloat(fieldVal) < parseFloat(condVal);
          default:          return false;
        }
      };
      const checkGroup = (group, product) => {
        const conds = group.conditions || [];
        if (!conds.length) return false;
        if (group.operator === 'OU') return conds.some(c => checkCond(c, product));
        return conds.every(c => checkCond(c, product));  // ET par défaut
      };
      return (product) => {
        for (const rule of groupMapping) {
          if (!rule.groupe || !rule.groupe.trim()) continue;
          const vrg = rule.visualRuleGroups || [];
          if (!vrg.length) continue;
          // Les groupes sont reliés par OU (c'est la sémantique QueryBuilder : un groupe = un bloc ET, entre groupes = OU)
          const matched = vrg.some(g => checkGroup(g, product));
          if (matched) return rule.groupe;
        }
        return null;
      };
    },
    async openGroupRulesModal() {
      this.groupRulesTestResults = [];
      this.groupRulesTestOpen = false;
      this.groupRulesModalShow = true;
      // Charger les règles dans rules_editor après que le modal est monté
      await this.$nextTick();
      await this._loadRulesEditorFromLibrary();
    },
    async _loadRulesEditorFromLibrary() {
      if (!this.$refs.groupRulesEditor) return;
      try {
        const data = await library.load('scraping_group_rules', null, 'rules');
        if (data) await this.$refs.groupRulesEditor.load(data);
      } catch (e) { /* pas encore de règles sauvegardées */ }
    },
    async onGroupRulesModalShown() {
      // Fallback timing : si $nextTick dans openGroupRulesModal était trop rapide
      await this._loadRulesEditorFromLibrary();
    },
    async runGroupRulesTest() {
      if (!this.$refs.groupRulesEditor) return;
      const data = await this.$refs.groupRulesEditor.save();
      const groupMapping = data.group_mapping || [];
      const fn = this._buildGroupFnFromRules(groupMapping);
      this.groupRulesTestResults = (this.products || []).map(p => ({
        nom: p.nom || p.name || p.id || '—',
        groupe: fn ? fn(p) : null,
      }));
    },
    async openVeilleTarifairePage() {
      this.veilleView = true;
      this.veilleTarifaireDirty = false;
      this.veilleTarifaireLoading = true;
      try {
        const isGm = this.ds.showSourceInVeilleTarifaire === true;
        const [rows, sources] = await Promise.all([
          this.database.get('select', 'veille_tarifaire_regles', {
            select: '*',
            end: 'ORDER BY priority DESC, id ASC',
          }),
          isGm ? this.database.get('select', this.ds.table || 'produits', {
            select: 'DISTINCT source',
            where: `source IS NOT NULL AND source != ''`,
            end: 'ORDER BY source ASC',
          }) : Promise.resolve([]),
        ]);
        this.veilleTarifaireSourcesList = (sources || []).map(r => r.source).filter(Boolean);
        const filteredRows = isGm ? (rows || []).filter(r => !r.zone) : (rows || []);
        const eligibleRows = filteredRows.filter(r => r.nom);
        this.veilleTarifaireLoadedIds = eligibleRows.map(r => r.id);
        this.veilleTarifaireRegles = eligibleRows.map(r => this._vtrFromDb(r));
      } catch (e) {
        alert('Erreur chargement : ' + e.message);
      } finally {
        this.veilleTarifaireLoading = false;
      }
    },
    closeVeilleTarifairePage() {
      if (this.veilleTarifaireDirty && !confirm('Des modifications non enregistrées seront perdues. Continuer ?')) return;
      this.veilleView = false;
      this.veilleTarifaireDirty = false;
    },
    vtrDragStart(idx, event) {
      this.vtrDragIndex = idx;
      event.dataTransfer.effectAllowed = 'move';
    },
    vtrDrop(toIdx) {
      if (this.vtrDragIndex === null || this.vtrDragIndex === toIdx) return;
      const rules = this.veilleTarifaireRegles;
      const moved = rules.splice(this.vtrDragIndex, 1)[0];
      rules.splice(toIdx, 0, moved);
      // Réassigner les priorités selon la position (premier = plus haute priorité)
      rules.forEach((r, i) => { r.priority = rules.length - i; });
      this.vtrDragIndex = null;
      this.vtrDragOver = null;
      this.veilleTarifaireDirty = true;
    },
    async openVeilleTarifaireModal() {
      // redirect to page
      this.openVeilleTarifairePage();
    },
    _vtrFromDb(r) {
      let criteres = {};
      try { criteres = typeof r.criteres === 'string' ? JSON.parse(r.criteres || '{}') : (r.criteres || {}); } catch {}
      const categorie = criteres.categorie ?? criteres.categorie_principale ?? criteres.categorie_dans_liste ?? null;
      if (r.marque && !criteres.marque) criteres.marque = r.marque;
      return {
        id: r.id,
        nom: r.nom || '',
        reduction: Math.round((r.reduction ?? 5) * 100) / 100,
        type: r.type || '%',
        prix_max: Math.round((r.prix_max ?? 5) * 100) / 100,
        type_max: r.type_max || '%',
        prix_min: Math.round((r.prix_min ?? 5) * 100) / 100,
        type_min: r.type_min || '%',
        priority: r.priority ?? 0,
        active: r.active ?? 1,
        _marque: criteres.marque || '',
        _source: criteres.source || '',
        _categorie: categorie,
        _caracteristiques: Object.entries(criteres.caracteristiques || {}).map(([key, value]) => ({ key, value })),
        _open: false,
        code: r.code || '',
        _test_prix_min: null,
        _test_prix_max: null,
        _test_prix_achat: null,
        _test_result: undefined,
      };
    },
    _vtrToDb(rule) {
      const criteres = {};
      if (rule._marque) criteres.marque = rule._marque;
      if (rule._source) criteres.source = rule._source;
      if (rule._categorie != null) criteres.categorie = rule._categorie;
      if (rule._caracteristiques.length) {
        const c = {};
        for (const { key, value } of rule._caracteristiques) { if (key) c[key] = value; }
        if (Object.keys(c).length) criteres.caracteristiques = c;
      }
      return {
        nom: rule.nom || null,
        criteres: JSON.stringify(criteres),
        reduction: rule.reduction,
        type: rule.type,
        prix_max: rule.prix_max,
        type_max: rule.type_max,
        prix_min: rule.prix_min,
        type_min: rule.type_min,
        priority: rule.priority ?? 0,
        active: rule.active ?? 1,
        code: rule.code || null,
      };
    },
    addVeilleTarifaireRegle() {
      this.veilleTarifaireRegles.push({
        id: null, nom: '', reduction: 5, type: '%', prix_max: 5, type_max: '%', prix_min: 5, type_min: '%', priority: 0, active: 1,
        _marque: '', _source: '', _categorie: null, _caracteristiques: [], _open: false, code: '',
        _test_prix_min: null, _test_prix_max: null, _test_prix_achat: null, _test_result: undefined,
      });
    },
    vtrRunCodeTest(rule) {
      let prix_min = parseFloat(rule._test_prix_min);
      let prix_max = parseFloat(rule._test_prix_max);
      const prix_achat = isNaN(parseFloat(rule._test_prix_achat)) ? 0 : parseFloat(rule._test_prix_achat); // eslint-disable-line no-unused-vars
      if (isNaN(prix_min) || isNaN(prix_max)) {
        this.$set(rule, '_test_result', { error: 'Renseigner prix_min et prix_max' });
        return;
      }
      try {
        // eslint-disable-next-line no-eval
        eval(rule.code);
        this.$set(rule, '_test_result', { prix_min: Math.round(prix_min * 100) / 100, prix_max: Math.round(prix_max * 100) / 100 });
      } catch (e) {
        this.$set(rule, '_test_result', { error: e.message });
      }
    },
    async saveVeilleTarifaireRegles() {
      const unnamed = this.veilleTarifaireRegles.findIndex(r => !r.nom || !r.nom.trim());
      if (unnamed !== -1) {
        alert(`La règle n°${unnamed + 1} n'a pas de nom. Veuillez renseigner un nom pour chaque règle.`);
        return;
      }
      this.veilleTarifaireSaving = true;
      try {
        // Supprimer uniquement les règles qui avaient été chargées (pas les lignes legacy/filtrées)
        const loadedIds = new Set(this.veilleTarifaireLoadedIds);
        const keepIds = new Set(this.veilleTarifaireRegles.filter(r => r.id).map(r => r.id));
        for (const id of loadedIds) {
          if (!keepIds.has(id)) {
            await this.database.get('delete', 'veille_tarifaire_regles', { where: `id = ${id}` });
          }
        }
        // Insérer ou mettre à jour
        for (const rule of this.veilleTarifaireRegles) {
          const data = this._vtrToDb(rule);
          const fields = Object.keys(data);
          if (rule.id) {
            const setParts = fields.map(f => {
              const val = data[f];
              if (val === null || val === undefined) return `\`${f}\` = NULL`;
              return `\`${f}\` = "${String(val).replace(/"/g, '\\"')}"`;
            }).join(', ');
            await this.database.get('update', 'veille_tarifaire_regles', { set: setParts, where: `id = ${rule.id}` });
          } else {
            const res = await this.database.get('insert into', 'veille_tarifaire_regles', { columns: fields, data });
            if (res && res.insertId) rule.id = res.insertId;
          }
        }
        this.veilleTarifaireModalShow = false;
        this.veilleView = false;
        this.veilleTarifaireDirty = false;
      } catch (e) {
        alert('Erreur sauvegarde : ' + e.message);
      } finally {
        this.veilleTarifaireSaving = false;
      }
    },
    async saveGroupRules() {
      if (!this.$refs.groupRulesEditor) return;
      this.groupRulesSaving = true;
      try {
        const data = await this.$refs.groupRulesEditor.save();
        // Générer le code JS exécutable depuis group_mapping
        data.code = this._generateGroupMappingCode(data.group_mapping || []);
        await library.save(
          'scraping_group_rules',
          1,
          data,
          'rules',
          false,
          data.data_db || {},
          true
        );
        this.groupRulesModalShow = false;
        await this.loadGroupRules();
        await this.loadPage();
      } catch (e) {
        alert('Erreur lors de la sauvegarde : ' + e.message);
      } finally {
        this.groupRulesSaving = false;
      }
    },
    toggleStatusDomainFilter(val) {
      const idx = this.statusDomainFilter.statuses.indexOf(val);
      if (idx >= 0) this.statusDomainFilter.statuses.splice(idx, 1);
      else this.statusDomainFilter.statuses.push(val);
    },
    _buildStatusDomainClause() {
      if (!this.statusDomainFilter.domain || !this.statusDomainFilter.statuses.length) return null;
      const cfg = this.scrapingConfigs.find(c => c.key === this.statusDomainFilter.domain);
      if (!cfg) return null;
      const domEsc = cfg.domain.replace(/'/g, "''");
      const typeEsc = cfg.type.replace(/'/g, "''");
      const joinId = this.ds.scrapingJoinId || 'produits.id';
      const isImport = this.ds.configType === 'scraping_file';
      const scrapCol = isImport ? 'scraping.id_import_produit' : 'scraping.id_produit';
      const groupeEsc = (cfg.groupe || '').replace(/'/g, "''");
      const groupeClause = cfg.groupe ? `AND scraping.groupe = '${groupeEsc}'` : `AND (scraping.groupe IS NULL OR scraping.groupe = '')`;
      const onBase = `${scrapCol} = ${joinId} AND scraping.domaine = '${domEsc}' AND scraping.type = '${typeEsc}' ${groupeClause}`;
      const statuses = this.statusDomainFilter.statuses;
      const hasNonFait = statuses.includes('non_fait');
      const others = statuses.filter(s => s !== 'non_fait');
      const otherConds = [];
      if (others.includes('non_trouve')) otherConds.push(`scraping.statut_smd LIKE '%non tr%'`);
      if (others.includes('a_verifier')) otherConds.push(`(scraping.statut NOT LIKE '%valid%' AND (scraping.statut_smd LIKE '%vérif%' OR scraping.statut_smd LIKE '%verif%' OR scraping.statut_smd LIKE '%diff%'))`);
      if (others.includes('valide')) otherConds.push(`(scraping.statut LIKE '%valid%' OR scraping.statut_smd LIKE '%valid%')`);
      const safeStartTime = this.scrapingFilterStartTime ? this.scrapingFilterStartTime.toString().replace(/'/g, "''") : null;
      const recentCond = safeStartTime ? `scraping.date_recherche >= '${safeStartTime}'` : null;
      const nonFaitCond = safeStartTime
        ? `(${scrapCol} IS NULL OR ${recentCond})`
        : `${scrapCol} IS NULL`;
      if (hasNonFait && !others.length) {
        return { join_table: 'scraping', join_type: 'LEFT JOIN', join_end: `ON ${onBase}`, where: nonFaitCond };
      } else if (!hasNonFait) {
        const matchCond = otherConds.join(' OR ');
        const where = recentCond ? `(${matchCond} OR ${recentCond})` : `(${matchCond})`;
        return { join_table: 'scraping', join_type: 'LEFT JOIN', join_end: `ON ${onBase}`, where };
      } else {
        const matchCond = otherConds.join(' OR ');
        const where = recentCond
          ? `(${nonFaitCond} OR (${matchCond}) OR ${recentCond})`
          : `(${nonFaitCond} OR (${matchCond}))`;
        return { join_table: 'scraping', join_type: 'LEFT JOIN', join_end: `ON ${onBase}`, where };
      }
    },
    async loadPage() {
      this.loading = true;
      const _table = this.ds.table || 'produits';
      const _baseWhere = this.ds.baseWhere || '';
      const isImport = this.ds.configType === 'scraping_file';
      const _sfBase = this.ds.selectFields || (isImport ? 'id, import_uid, cle_externe, nom, data, date' : 'id, id_cms, status, reference, nom, marque, prix_ht, prix_ttc, prix_promo_ttc, categories, default_category');
      const _sf = isImport && !_sfBase.includes('groupe') ? _sfBase + ', groupe' : _sfBase;
      const _sfj = this.ds.selectFieldsJoined || (isImport
        ? `${_table}.id, ${_table}.import_uid, ${_table}.cle_externe, ${_table}.nom, ${_table}.data, ${_table}.date, ${_table}.groupe`
        : `${_table}.id, ${_table}.id_cms, ${_table}.status, ${_table}.reference, ${_table}.nom, ${_table}.marque, ${_table}.prix_ht, ${_table}.prix_ttc, ${_table}.prix_promo_ttc, ${_table}.categories, ${_table}.default_category`);
      try {
        const offset = (this.page - 1) * this.pageSize;
        const sdf = this._buildStatusDomainClause();
        const joinParams = sdf ? { join_table: sdf.join_table, join_type: sdf.join_type, join_end: sdf.join_end } : {};
        if (this.ds.extraJoin) joinParams.extra_join = this.ds.extraJoin;
        const parts = [this.whereClause ? `(${this.whereClause})` : null, sdf?.where || null, _baseWhere || null].filter(Boolean);
        const where = parts.join(' AND ');
        const selectFields = sdf ? `DISTINCT ${_sfj}` : _sf;

        const [rows, countRows] = await Promise.all([
          this.database.get('select', _table, {
            select: selectFields,
            where,
            end: `ORDER BY ${_table}.id DESC LIMIT ${this.pageSize} OFFSET ${offset}`,
            ...joinParams,
          }),
          this.database.get('select', _table, {
            select: sdf ? `COUNT(DISTINCT ${_table}.id) AS total` : 'COUNT(*) AS total',
            where,
            ...joinParams,
          }),
        ]);

        this.products = Array.isArray(rows) ? rows : [];
        this.total = Array.isArray(countRows) && countRows[0] ? (parseInt(countRows[0].total) || 0) : 0;

        // Pour les produits natifs (non import), calculer le groupe dynamiquement via globalGroupFn
        if (!isImport && this.globalGroupFn) {
          this.products.forEach((p, i) => { this.$set(this.products, i, { ...p, groupe: this.globalGroupFn(p) }); });
        }

        if (this.products.length) {
          const ids = this.products.map(p => p.id).join(',');
          const isImport = this.ds.configType === 'scraping_file';
          const scrapingRows = await this.database.get('select', 'scraping', {
            select: '*',
            where: isImport ? `id_import_produit IN (${ids})` : `id_produit IN (${ids})`,
          });
          const map = {};
          for (const row of scrapingRows || []) {
            const prodId = isImport ? row.id_import_produit : row.id_produit;
            const key = `${prodId}__${row.domaine}__${row.type}__${row.groupe || ''}`;
            if (!map[key]) map[key] = row; // garder la première occurrence
          }
          this.scrapingMap = map;
        } else {
          this.scrapingMap = {};
        }
      } finally {
        this.loading = false;
      }
    },
    async exportCsvAll() {
      if (this.csvExporting) return;
      this.csvExporting = true;
      this.csvProgress = 0;
      const BATCH = 100;
      const _table = this.ds.table || 'produits';
      const _baseWhere = this.ds.baseWhere || '';
      const isImport = this.ds.configType === 'scraping_file';
      const _sfBase = this.ds.selectFields || (isImport
        ? 'id, import_uid, cle_externe, nom, data, date'
        : 'id, id_cms, status, reference, nom, marque, prix_ht, prix_ttc, prix_promo_ttc, categories, default_category');
      const _sf = isImport && !_sfBase.includes('groupe') ? _sfBase + ', groupe' : _sfBase;
      const _sfj = this.ds.selectFieldsJoined || (isImport
        ? `${_table}.id, ${_table}.import_uid, ${_table}.cle_externe, ${_table}.nom, ${_table}.data, ${_table}.date, ${_table}.groupe`
        : `${_table}.id, ${_table}.id_cms, ${_table}.status, ${_table}.reference, ${_table}.nom, ${_table}.marque, ${_table}.prix_ht, ${_table}.prix_ttc, ${_table}.prix_promo_ttc, ${_table}.categories, ${_table}.default_category`);

      const sdf = this._buildStatusDomainClause();
      const joinParams = sdf ? { join_table: sdf.join_table, join_type: sdf.join_type, join_end: sdf.join_end } : {};
      if (this.ds.extraJoin) joinParams.extra_join = this.ds.extraJoin;
      const parts = [this.whereClause ? `(${this.whereClause})` : null, sdf?.where || null, _baseWhere || null].filter(Boolean);
      const where = parts.join(' AND ');
      const selectFields = sdf ? `DISTINCT ${_sfj}` : _sf;

      const cfgs = this.visibleScrapingConfigs;
      const extraCols = this.visibleListingExtraCols;
      const listingCols = this.ds.listingColumns || [{key:'id_cms',label:'id_cms'},{key:'status',label:'Statut'}];
      const showNom = this.ds.showNomColumn !== false;
      const showCats = this.ds.showCategoriesColumn !== false;
      const showGroupe = !!(this.globalGroupFn && !listingCols.some(c => c.key === 'groupe'));

      const headers = ['id'];
      for (const col of listingCols) headers.push(col.label || col.key);
      if (showNom) headers.push('Nom');
      if (showCats) headers.push('Catégories');
      if (showGroupe) headers.push('Groupe');
      for (const col of extraCols) headers.push(col.label);
      for (const cfg of cfgs) {
        const prefix = cfg.domain + (cfg.groupe ? '_' + cfg.groupe : '');
        headers.push(`${prefix}_statut`);
        headers.push(`${prefix}_statut_gm`);
        headers.push(`${prefix}_url`);
        headers.push(`${prefix}_resultat`);
        headers.push(`${prefix}_identifiant`);
        headers.push(`${prefix}_identifiant_result`);
      }

      let total = 0;
      try {
        const countRows = await this.database.get('select', _table, {
          select: sdf ? `COUNT(DISTINCT ${_table}.id) AS total` : 'COUNT(*) AS total',
          where,
          ...joinParams,
        });
        total = Array.isArray(countRows) && countRows[0] ? (parseInt(countRows[0].total) || 0) : 0;
      } catch (e) {
        alert('Erreur export CSV : ' + e.message);
        this.csvExporting = false;
        return;
      }

      const allRows = [];
      let offset = 0;
      try {
        while (offset < total) {
          const rows = await this.database.get('select', _table, {
            select: selectFields,
            where,
            end: `ORDER BY ${_table}.id DESC LIMIT ${BATCH} OFFSET ${offset}`,
            ...joinParams,
          });
          const products = Array.isArray(rows) ? rows : [];
          if (!isImport && this.globalGroupFn) {
            products.forEach((p, i) => { products[i] = { ...p, groupe: this.globalGroupFn(p) }; });
          }

          let batchScrapingMap = {};
          if (products.length) {
            const ids = products.map(p => p.id).join(',');
            const scrapingRows = await this.database.get('select', 'scraping', {
              select: isImport
                ? 'id_import_produit, domaine, type, groupe, statut, statut_smd, url, result, identification, identification_result, cle_produit'
                : 'id_produit, domaine, type, groupe, statut, statut_smd, url, result, identification, identification_result, cle_produit',
              where: isImport ? `id_import_produit IN (${ids})` : `id_produit IN (${ids})`,
            });
            for (const row of scrapingRows || []) {
              const prodId = isImport ? row.id_import_produit : row.id_produit;
              const key = `${prodId}__${row.domaine}__${row.type}__${row.groupe || ''}`;
              const expectedCle = `${prodId}_${row.type}_${row.groupe || ''}_${row.domaine}`;
              if (!batchScrapingMap[key] || row.cle_produit === expectedCle) batchScrapingMap[key] = row;
            }
          }

          for (const product of products) {
            const obj = { id: product.id };
            for (const col of listingCols) {
              const val = col.fromData ? this.parseDataField(product.data, col.fromData) : (col.render ? col.render(product[col.key]) : product[col.key]);
              obj[col.label || col.key] = val ?? '';
            }
            if (showNom) obj['Nom'] = this.getNom(product);
            if (showCats) obj['Catégories'] = this.getCategories(product).map(id => this.getCatName(id)).join(', ');
            if (showGroupe) obj['Groupe'] = product.groupe || '';
            for (const col of extraCols) {
              obj[col.label] = col.fromData ? this.parseDataField(product.data, col.fromData) : (product[col.key] ?? '');
            }
            for (const cfg of cfgs) {
              const prefix = cfg.domain + (cfg.groupe ? '_' + cfg.groupe : '');
              const mapKey = `${product.id}__${cfg.domain}__${cfg.type}__${cfg.groupe || ''}`;
              const scrapRow = batchScrapingMap[mapKey] || null;
              obj[`${prefix}_statut`] = scrapRow?.statut || '';
              obj[`${prefix}_statut_gm`] = scrapRow?.statut_smd || '';
              obj[`${prefix}_url`] = scrapRow?.url || '';
              obj[`${prefix}_resultat`] = scrapRow?.result || '';
              obj[`${prefix}_identifiant`] = scrapRow?.identification || '';
              obj[`${prefix}_identifiant_result`] = scrapRow?.identification_result || '';
            }
            allRows.push(obj);
          }

          offset += BATCH;
          this.csvProgress = total > 0 ? Math.min(Math.round(offset / total * 100), 100) : 100;
          if (products.length < BATCH) break;
        }
      } catch (e) {
        alert('Erreur export CSV : ' + e.message);
        this.csvExporting = false;
        this.csvProgress = 0;
        return;
      }

      const csv = this.json2csv.parse(allRows, { fields: headers, fieldNames: headers });
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `export_scraping_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
      this.csvExporting = false;
      this.csvProgress = 0;
    },
    async reload() {
      this.selectedIds = [];
      this.page = 1;
      await this._refreshServerTime();
      this.loadPage();
    },
    getNom(product) {
      if (this.datasource && this.datasource.getNom) {
        return this.datasource.getNom(product, this.defaultLang);
      }
      try {
        const nom = typeof product.nom === 'string' ? JSON.parse(product.nom) : product.nom;
        return nom?.[this.defaultLang] || nom?.fr || product.reference || '—';
      } catch { return product.reference || '—'; }
    },
    _getCleProduir(product) {
      if (this.datasource && this.datasource.getCleProduir) {
        return this.datasource.getCleProduir(product);
      }
      return product.id_cms || '';
    },
    getCategories(product) {
      try {
        const cats = typeof product.categories === 'string'
          ? JSON.parse(product.categories)
          : product.categories;
        return Array.isArray(cats) ? cats : [];
      } catch { return []; }
    },
    getCatName(idCms) {
      return this.categoriesMap[idCms] || idCms;
    },
    getScrapingRow(productId, cfg) {
      return this.scrapingMap[`${productId}__${cfg.domain}__${cfg.type}__${cfg.groupe || ''}`] || null;
    },
    parseDataField(dataStr, key) {
      try {
        const obj = typeof dataStr === 'string' ? JSON.parse(dataStr || '{}') : (dataStr || {});
        const v = obj[key];
        return v === null || v === undefined ? '' : v;
      } catch (e) { return ''; }
    },
    passesRules(product, cfg) {
      // Incompatibilité de groupe : le scraper a un groupe, le produit doit avoir le même
      if (cfg.groupe && product.groupe !== cfg.groupe) return false;
      if (!cfg.fn) return true;
      try { return !!cfg.fn(product); } catch (e) { return true; }
    },
    passesAnyConfig(product, repCfg) {
      // Retourne true si le produit satisfait au moins une config du même domain+groupe
      const siblings = this.scrapingConfigs.filter(c => c.domain === repCfg.domain && c.groupe === repCfg.groupe);
      if (!siblings.length) return this.passesRules(product, repCfg);
      return siblings.some(cfg => this.passesRules(product, cfg));
    },
    productMissingDataCols(product, cfg) {
      if (!this.ds.showDataKeys || !cfg.requiredDataCols || !cfg.requiredDataCols.length) return [];
      let dataObj = {};
      try { dataObj = typeof product.data === 'string' ? JSON.parse(product.data) : (product.data || {}); } catch (e) {}
      const normalize = s => this._toSafeKey(s);
      const dataKeys = new Set(Object.keys(dataObj).map(normalize));
      return cfg.requiredDataCols.filter(col => !dataKeys.has(normalize(col)));
    },
    openModal(product, cfg) {
      this.modalProduct = product;
      this.modalDataExpanded = false;
      this.modalProductIndex = this.products.indexOf(product);
      this.modalCfg = cfg;
      this.modalRow = this.getScrapingRow(product.id, cfg);
      this.modalEditUrl = this.modalRow?.url || '';
      this.modalEditStatut = this.modalRow?.statut || '';
      this.modalEditVeilleTarifaire = this.modalRow?.veille_tarifaire ?? 0;
      this.modalLaunchStatus = '';
      this.modalVisible = true;
    },
    navigateModal(delta) {
      const idx = this.modalProductIndex + delta;
      if (idx < 0 || idx >= this.products.length) return;
      this.openModal(this.products[idx], this.modalCfg);
    },
    toggleSelectAll(val) {
      this.selectedIds = val ? this.products.map(p => p.id) : [];
    },
    toggleBulkCfg(key, checked) {
      if (checked) {
        if (!this.bulkCfgKeys.includes(key)) this.bulkCfgKeys.push(key);
      } else {
        this.bulkCfgKeys = this.bulkCfgKeys.filter(k => k !== key);
      }
    },
    async bulkLaunchScraping() {
      if (!this.scenarioUid) return;
      // Étendre les clés sélectionnées (représentantes déduplicées) à tous les siblings du même domain+groupe
      let selectedConfigs;
      if (this.bulkCfgKeys.length) {
        const repConfigs = this.visibleScrapingConfigs.filter(c => this.bulkCfgKeys.includes(c.key));
        selectedConfigs = this.scrapingConfigs.filter(c =>
          repConfigs.some(rep => rep.domain === c.domain && rep.groupe === c.groupe)
        );
      } else {
        selectedConfigs = this.scrapingConfigs;
      }
      if (!selectedConfigs.length) return;

      let products;
      if (this.bulkScope === 'selected') {
        products = this.products.filter(p => this.selectedIds.includes(p.id));
      } else if (this.bulkScope === 'page') {
        products = [...this.products];
      } else {
        const _table = this.ds.table || 'produits';
        const _baseWhere = this.ds.baseWhere || '';
        const _sf = this.ds.selectFields || 'id, id_cms, status, reference, nom, marque, prix_ht, prix_ttc, prix_promo_ttc, categories, default_category';
        const _sfj = this.ds.selectFieldsJoined || `${_table}.id, ${_table}.id_cms, ${_table}.status, ${_table}.reference, ${_table}.nom, ${_table}.marque, ${_table}.prix_ht, ${_table}.prix_ttc, ${_table}.prix_promo_ttc, ${_table}.categories, ${_table}.default_category`;
        const sdf = this._buildStatusDomainClause();
        const joinParams = sdf ? { join_table: sdf.join_table, join_type: sdf.join_type, join_end: sdf.join_end } : {};
        if (this.ds.extraJoin) joinParams.extra_join = this.ds.extraJoin;
        const parts = [this.whereClause ? `(${this.whereClause})` : null, sdf?.where || null, _baseWhere || null].filter(Boolean);
        const where = parts.join(' AND ');
        const selectFields = sdf ? _sfj : _sf;
        const rows = await this.database.get('select', _table, {
          select: selectFields,
          where,
          end: `ORDER BY ${sdf ? `${_table}.` : ''}id DESC LIMIT 1000`,
          ...joinParams,
        });
        products = rows || [];
        if (products.length === 1000) {
          if (!confirm('Plus de 1000 produits correspondent aux filtres. Le traitement sera effectué uniquement sur les 1000 premiers. Continuer ?')) return;
        }
      }

      if (!products.length) return;

      const _catField = this.ds.categoryPrimaryField || 'default_category';
      const allRows = [];
      for (const product of products) {
        // Pour chaque produit, ne garder que les configs applicables (passesRules)
        const applicableConfigs = selectedConfigs.filter(cfg => this.passesRules(product, cfg));
        if (!applicableConfigs.length) continue;
        // Construire le champ domaine : "domain::groupe|domain2" (:: sépare domaine et groupe, | sépare les scrapers)
        // Dédupliquer par domain+groupe (plusieurs conditions peuvent matcher pour le même domaine)
        const seenDomGroupe = new Set();
        const domaineField = applicableConfigs
          .filter(cfg => {
            const dk = `${cfg.domain}__${cfg.groupe || ''}`;
            if (seenDomGroupe.has(dk)) return false;
            seenDomGroupe.add(dk);
            return true;
          })
          .map(cfg => cfg.groupe ? `${cfg.domain}::${cfg.groupe}` : cfg.domain)
          .join('|');
        const baseRow = {
          id_produit: product.id,
          cle_produit: this._getCleProduir(product),
          reference: product.reference || '',
          nom: this.getNom(product),
          marque: product.marque || '',
          categories: product.categories || '',
          categorie_principale: product[_catField] || '',
          prix_ht: product.prix_ht || '',
          prix_ttc: product.prix_ttc || '',
          source: product.source || '',
          domaine: domaineField,
          type: applicableConfigs[0].type,
          groupe: product.groupe || '',
          data: product.data || '',
        };
        allRows.push(baseRow);
      }

      if (!allRows.length) {
        alert('Aucun produit à traiter (conditions non remplies pour la sélection).');
        return;
      }

      // Mode séquentiel : ajouter chaque produit/config dans la file une par une
      // On utilise visibleScrapingConfigs filtrés (déjà dédupliqués par domaine, comme la loupe inline)
      if (this.sequentialMode) {
        const seqConfigs = this.visibleScrapingConfigs.filter(c => this.bulkCfgKeys.includes(c.key));
        for (const product of products) {
          for (const cfg of seqConfigs) {
            if (this.passesAnyConfig(product, cfg)) {
              this.addToQueue(product, cfg);
            }
          }
        }
        return;
      }

      this.bulkRunning = true;
      this.bulkDone = 0;
      this.bulkTotal = allRows.length;
      try {
        const fields = Object.keys(allRows[0]);
        const csv = this.json2csv.parse(allRows, { fields, fieldNames: fields });
        const blob = new Blob([new TextEncoder().encode(csv)], { type: 'text/plain' });
        let t = await this.treatments.create(this.scenarioUid, null, blob, 'data.csv', [], 'import');
        while (t.treatment.resultat !== 'ok' && t.treatment.resultat !== 'ko') {
          await new Promise(r => setTimeout(r, 2000));
          t = await this.treatments.get(t.treatment.uid);
        }
        this.bulkDone = allRows.length;
        await this.loadPage();
      } finally {
        this.bulkRunning = false;
      }
    },
    productHasValidScraping(product) {
      const productId = product.id;
      const isImport = this.ds.configType === 'scraping_file';
      return Object.values(this.scrapingMap).some(row => {
        const pid = isImport ? row.id_import_produit : row.id_produit;
        if (pid != productId) return false;
        if (!row.veille_tarifaire) return false;
        return row.statut === 'validé' || ((!row.statut || row.statut === '') && row.statut_smd === 'validé');
      });
    },
    async bulkLaunchVeilleTarifaire() {
      if (!this.veilleScenarioUid) return;
      let products = [];
      if (this.bulkScope === 'selected') {
        products = this.products.filter(p => this.selectedIds.includes(p.id) && this.productHasValidScraping(p));
      } else if (this.bulkScope === 'page') {
        products = this.products.filter(p => this.productHasValidScraping(p));
      } else {
        // Pour "Tous" : récupérer les IDs éligibles depuis la DB (pas de feedback visuel ligne par ligne)
        const isImport = this.ds.configType === 'scraping_file';
        const idField = isImport ? 'id_import_produit' : 'id_produit';
        const rows = await this.database.get('select', 'scraping', {
          select: `DISTINCT ${idField} AS id_produit`,
          where: `veille_tarifaire = 1 AND (statut = 'validé' OR (statut_smd = 'validé' AND (statut IS NULL OR statut = '')))`,
          end: 'LIMIT 5000',
        });
        products = (rows || []).map(r => ({ id: r.id_produit }));
      }
      if (!products.length) {
        alert('Aucun produit éligible (veille tarifaire activée avec scraping validé).');
        return;
      }
      for (const product of products) {
        this.addToVeilleQueue(product);
      }
    },
    launchVeilleTarifaireInline(product) {
      if (!this.veilleScenarioUid) return;
      this.addToVeilleQueue(product);
    },
    addToVeilleQueue(product) {
      if (this.veilleQueueIdSet.has(product.id)) return;
      if (this.veilleInlineRunning[product.id]) return;
      this.veilleQueue.push({ product });
      this.startVeilleQueueProcessor();
    },
    removeFromVeilleQueue(productId) {
      const idx = this.veilleQueue.findIndex(q => q.product.id === productId);
      if (idx !== -1) this.veilleQueue.splice(idx, 1);
    },
    stopVeilleQueue() {
      this.veilleQueueAborted = true;
      this.veilleQueue = [];
    },
    async startVeilleQueueProcessor() {
      if (this.veilleQueueProcessing) return;
      this.veilleQueueAborted = false;
      this.veilleQueueProcessing = true;
      try {
        while (this.veilleQueue.length > 0 && !this.veilleQueueAborted) {
          const item = this.veilleQueue[0];
          this.veilleQueue.shift();
          const product = item.product;
          this.$set(this.veilleInlineRunning, product.id, true);
          try {
            const csv = this.json2csv.parse([{ id_produit: product.id }], { fields: ['id_produit'], fieldNames: ['id_produit'] });
            const blob = new Blob([new TextEncoder().encode(csv)], { type: 'text/plain' });
            let t = await this.treatments.create(this.veilleScenarioUid, null, blob, 'data.csv', [], 'import');
            while (t.treatment.resultat !== 'ok' && t.treatment.resultat !== 'ko') {
              await new Promise(r => setTimeout(r, 2000));
              t = await this.treatments.get(t.treatment.uid);
            }
          } catch (e) {
            console.error('veilleQueue error', e);
          } finally {
            this.$delete(this.veilleInlineRunning, product.id);
          }
        }
      } finally {
        this.veilleQueueProcessing = false;
      }
    },
    async openVeilleResultModal(product) {
      this.veilleResultProduct = product;
      this.veilleResultModalVisible = true;
      this.veilleResultLoading = true;
      this.veilleHistorique = [];
      this.veilleAnomalies = [];
      try {
        const [historique, anomalies] = await Promise.all([
          this.database.get('select', 'veille_tarifaire_historique', {
            select: '*',
            where: `id_produit = ${product.id}`,
            end: 'ORDER BY date DESC LIMIT 50',
          }),
          this.database.get('select', 'veille_tarifaire_anomalies', {
            select: '*',
            where: `id_produit = ${product.id}`,
            end: 'ORDER BY date_detection DESC LIMIT 50',
          }),
        ]);
        this.veilleHistorique = historique || [];
        this.veilleAnomalies = anomalies || [];
      } finally {
        this.veilleResultLoading = false;
      }
    },
    async saveInlineUrl(product, cfg) {
      if (!this.inlineEditUrl.trim()) return;
      this.inlineSaving = true;
      try {
        const isImport = this.ds.configType === 'scraping_file';
        const groupeVal = cfg.groupe || '';
        const newRow = isImport
          ? { id_import_produit: product.id, cle_produit: `${product.id}_${cfg.type}_${groupeVal}_${cfg.domain}`, domaine: cfg.domain, type: cfg.type, groupe: groupeVal, url: this.inlineEditUrl.trim(), statut: 'validé', veille_tarifaire: cfg.veille_tarifaire ? 1 : 0 }
          : { id_produit: product.id, cle_produit: `${product.id}_${cfg.type}_${groupeVal}_${cfg.domain}`, domaine: cfg.domain, type: cfg.type, groupe: groupeVal, url: this.inlineEditUrl.trim(), statut: 'validé', veille_tarifaire: cfg.veille_tarifaire ? 1 : 0 };
        await this.database.get('insert into', 'scraping', {
          columns: Object.keys(newRow),
          data: newRow,
        });
        // Recharger la ligne depuis la DB pour avoir l'id
        const groupeWhere = groupeVal ? `AND groupe = '${groupeVal.replace(/'/g, "''")}' ` : `AND (groupe IS NULL OR groupe = '') `;
        const rows = await this.database.get('select', 'scraping', {
          select: '*',
          where: isImport
            ? `id_import_produit = ${product.id} AND domaine = '${cfg.domain}' AND type = '${cfg.type}' ${groupeWhere}`
            : `id_produit = ${product.id} AND domaine = '${cfg.domain}' AND type = '${cfg.type}' ${groupeWhere}`,
          end: 'ORDER BY id DESC LIMIT 1',
        });
        if (rows && rows.length) {
          const key = `${product.id}__${cfg.domain}__${cfg.type}__${groupeVal}`;
          this.$set(this.scrapingMap, key, rows[0]);
        }
        this.inlineEditKey = null;
      } finally { this.inlineSaving = false; }
    },
    async launchScrapingInline(product, cfg) {
      if (!this.scenarioUid) return;
      if (this.sequentialMode) {
        this.addToQueue(product, cfg);
        return;
      }
      await this._executeLaunchScraping(product, cfg);
    },
    addToQueue(product, cfg) {
      const cellKey = `${product.id}__${cfg.key}`;
      if (this.scrapingQueueKeySet.has(cellKey)) return;
      if (this.inlineLaunchKeys[cellKey]) return;
      this.scrapingQueue.push({ key: cellKey, product, cfg });
      this.startQueueProcessor();
    },
    removeFromQueue(cellKey) {
      const idx = this.scrapingQueue.findIndex(q => q.key === cellKey);
      if (idx !== -1) this.scrapingQueue.splice(idx, 1);
    },
    stopQueue() {
      this.queueAborted = true;
      this.scrapingQueue = [];
    },
    async startQueueProcessor() {
      if (this.queueProcessing) return;
      this.queueAborted = false;
      this.queueProcessing = true;
      try {
        while (this.scrapingQueue.length > 0 && !this.queueAborted) {
          const item = this.scrapingQueue[0];
          await this._executeLaunchScraping(item.product, item.cfg);
          if (!this.queueAborted) this.scrapingQueue.shift();
        }
      } finally {
        this.queueProcessing = false;
        this.queueAborted = false;
      }
    },
    async _executeLaunchScraping(product, cfg) {
      const cellKey = `${product.id}__${cfg.key}`;
      this.$set(this.inlineLaunchKeys, cellKey, true);
      try {
        const groupeVal = cfg.groupe || '';
        const _catField = this.ds.categoryPrimaryField || 'default_category';
        const row = {
          id_produit: product.id,
          cle_produit: `${product.id}_${cfg.type}_${groupeVal}_${cfg.domain}`,
          reference: product.reference || '',
          nom: this.getNom(product),
          marque: product.marque || '',
          categories: product.categories || '',
          categorie_principale: product[_catField] || '',
          prix_ht: product.prix_ht || '',
          prix_ttc: product.prix_ttc || '',
          source: product.source || '',
          domaine: cfg.groupe ? `${cfg.domain}::${cfg.groupe}` : cfg.domain,
          type: cfg.type,
          groupe: groupeVal,
          data: product.data || '',
        };
        const csv = this.json2csv.parse([row], { fields: Object.keys(row), fieldNames: Object.keys(row) });
        const blob = new Blob([new TextEncoder().encode(csv)], { type: 'text/plain' });
        let t = await this.treatments.create(this.scenarioUid, null, blob, 'data.csv', [], 'import');
        while (t.treatment.resultat !== 'ok' && t.treatment.resultat !== 'ko') {
          await new Promise(r => setTimeout(r, 2000));
          t = await this.treatments.get(t.treatment.uid);
        }
        const isImport = this.ds.configType === 'scraping_file';
        const groupeWhere = groupeVal ? `AND groupe = '${groupeVal.replace(/'/g, "''")}' ` : `AND (groupe IS NULL OR groupe = '') `;
        const rows = await this.database.get('select', 'scraping', {
          select: '*',
          where: isImport
            ? `id_import_produit = ${product.id} AND domaine = '${cfg.domain}' AND type = '${cfg.type}' ${groupeWhere}`
            : `id_produit = ${product.id} AND domaine = '${cfg.domain}' AND type = '${cfg.type}' ${groupeWhere}`,
          end: 'ORDER BY id DESC LIMIT 1',
        });
        if (rows && rows.length) {
          const key = `${product.id}__${cfg.domain}__${cfg.type}__${groupeVal}`;
          this.$set(this.scrapingMap, key, rows[0]);
        }
      } catch (e) {
        console.error('launchScrapingInline error', e);
      } finally {
        this.$set(this.inlineLaunchKeys, cellKey, false);
      }
    },
    async fetchScenarioUid() {
      try {
        const list = await this.projects.getAll(false, false, null, true);
        const project = (list || []).find(p => p.libelle === 'Scraping - search');
        if (project) this.scenarioUid = project.uid;
        const veilleProject = (list || []).find(p => p.libelle === 'Veille tarifaire - produit');
        if (veilleProject) this.veilleScenarioUid = veilleProject.uid;
      } catch (e) { console.warn('fetchScenarioUid error', e); }
    },
    formatResult(result) {
      try {
        const obj = typeof result === 'string' ? JSON.parse(result) : result;
        return JSON.stringify(obj, null, 2);
      } catch (e) { return result; }
    },
    async saveUrl() {
      if (!this.modalRow) {
        // Pas encore de ligne : créer une nouvelle entrée scraping
        if (!this.modalProduct || !this.modalCfg || !this.modalEditUrl.trim()) return;
        this.modalSaving = true;
        try {
          const isImport = this.ds.configType === 'scraping_file';
          const groupeVal = this.modalProduct.groupe || '';
          const newRow = isImport
            ? { id_import_produit: this.modalProduct.id, cle_produit: `${this.modalProduct.id}_${this.modalCfg.type}_${groupeVal}_${this.modalCfg.domain}`, domaine: this.modalCfg.domain, type: this.modalCfg.type, groupe: groupeVal, url: this.modalEditUrl.trim(), statut: 'validé', veille_tarifaire: this.modalCfg.veille_tarifaire ? 1 : 0 }
            : { id_produit: this.modalProduct.id, cle_produit: `${this.modalProduct.id}_${this.modalCfg.type}_${groupeVal}_${this.modalCfg.domain}`, domaine: this.modalCfg.domain, type: this.modalCfg.type, groupe: groupeVal, url: this.modalEditUrl.trim(), statut: 'validé', veille_tarifaire: this.modalCfg.veille_tarifaire ? 1 : 0 };
          await this.database.get('insert into', 'scraping', { columns: Object.keys(newRow), data: newRow });
          const groupeWhere = groupeVal ? `AND groupe = '${groupeVal.replace(/'/g, "''")}' ` : `AND (groupe IS NULL OR groupe = '') `;
          const rows = await this.database.get('select', 'scraping', {
            select: '*',
            where: isImport
              ? `id_import_produit = ${this.modalProduct.id} AND domaine = '${this.modalCfg.domain}' AND type = '${this.modalCfg.type}' ${groupeWhere}`
              : `id_produit = ${this.modalProduct.id} AND domaine = '${this.modalCfg.domain}' AND type = '${this.modalCfg.type}' ${groupeWhere}`,
            end: 'ORDER BY id DESC LIMIT 1',
          });
          if (rows && rows.length) {
            const key = `${this.modalProduct.id}__${this.modalCfg.domain}__${this.modalCfg.type}__${groupeVal}`;
            this.$set(this.scrapingMap, key, rows[0]);
            this.modalRow = rows[0];
            this.modalEditStatut = rows[0].statut || '';
            this.modalEditVeilleTarifaire = rows[0].veille_tarifaire ?? 0;
          }
        } finally { this.modalSaving = false; }
        return;
      }
      this.modalSaving = true;
      try {
        const hasUrl = this.modalEditUrl.trim() !== '';
        const isImportUrl = this.ds.configType === 'scraping_file';
        const prodIdUrl = isImportUrl ? this.modalRow.id_import_produit : this.modalRow.id_produit;
        const key = `${prodIdUrl}__${this.modalRow.domaine}__${this.modalRow.type}__${this.modalRow.groupe || ''}`;
        if (!hasUrl) {
          // Supprimer la ligne scraping pour pouvoir la relancer proprement
          await this.database.get('delete', 'scraping', { where: `id = ${this.modalRow.id}` });
          this.$delete(this.scrapingMap, key);
          this.modalRow = null;
          this.$bvModal.hide('scraping-detail-modal');
        } else {
          const urlEscaped = this.modalEditUrl.replace(/'/g, "\\'");
          await this.database.get('update', 'scraping', {
            set: `url = '${urlEscaped}', statut_smd = NULL, statut = 'validé', identification = '', identification_result = '', result = ''`,
            where: `id = ${this.modalRow.id}`,
          });
          const updated = { ...this.scrapingMap[key],
            url: this.modalEditUrl,
            statut_smd: null,
            statut: 'validé',
            identification: '',
            identification_result: '',
            result: '',
          };
          this.$set(this.scrapingMap, key, updated);
          this.modalRow = updated;
          this.modalEditStatut = 'validé';
          this.modalEditVeilleTarifaire = updated.veille_tarifaire ?? 0;
        }
      } finally { this.modalSaving = false; }
    },
    async saveStatut() {
      if (!this.modalRow) return;
      this.modalSaving = true;
      try {
        const valEscaped = this.modalEditStatut.replace(/'/g, "\\'");
        await this.database.get('update', 'scraping', {
          set: `statut = '${valEscaped}'`,
          where: `id = ${this.modalRow.id}`,
        });
        const isImport = this.ds.configType === 'scraping_file';
        const prodId = isImport ? this.modalRow.id_import_produit : this.modalRow.id_produit;
        const key = `${prodId}__${this.modalRow.domaine}__${this.modalRow.type}__${this.modalRow.groupe || ''}`;
        const updated = { ...this.scrapingMap[key], statut: this.modalEditStatut };
        this.$set(this.scrapingMap, key, updated);
        this.modalRow = updated;
      } finally { this.modalSaving = false; }
    },
    async saveVeilleTarifaire() {
      if (!this.modalRow) return;
      this.modalSaving = true;
      try {
        const val = this.modalEditVeilleTarifaire ? 1 : 0;
        await this.database.get('update', 'scraping', {
          set: `veille_tarifaire = ${val}`,
          where: `id = ${this.modalRow.id}`,
        });
        const isImport = this.ds.configType === 'scraping_file';
        const prodId = isImport ? this.modalRow.id_import_produit : this.modalRow.id_produit;
        const key = `${prodId}__${this.modalRow.domaine}__${this.modalRow.type}__${this.modalRow.groupe || ''}`;
        const updated = { ...this.scrapingMap[key], veille_tarifaire: val };
        this.$set(this.scrapingMap, key, updated);
        this.modalRow = updated;
      } finally { this.modalSaving = false; }
    },
    async launchScraping() {
      if (!this.scenarioUid || !this.modalProduct || !this.modalCfg) return;
      this.modalLaunching = true;
      this.modalLaunchStatus = 'Lancement...';
      try {
        const _catField = this.ds.categoryPrimaryField || 'default_category';
        const row = {
          id_produit: this.modalProduct.id,
          cle_produit: `${this.modalProduct.id}_${this.modalCfg.type}_${this.modalProduct.groupe || ''}_${this.modalCfg.domain}`,
          reference: this.modalProduct.reference || '',
          nom: this.getNom(this.modalProduct),
          marque: this.modalProduct.marque || '',
          categories: this.modalProduct.categories || '',
          categorie_principale: this.modalProduct[_catField] || '',
          prix_ht: this.modalProduct.prix_ht || '',
          prix_ttc: this.modalProduct.prix_ttc || '',
          source: this.modalProduct.source || '',
          domaine: this.modalCfg.groupe ? `${this.modalCfg.domain}::${this.modalCfg.groupe}` : this.modalCfg.domain,
          type: this.modalCfg.type,
          groupe: this.modalProduct.groupe || '',
          data: this.modalProduct.data || '',
        };
        const csv = this.json2csv.parse([row], { fields: Object.keys(row), fieldNames: Object.keys(row) });
        const blob = new Blob([new TextEncoder().encode(csv)], { type: 'text/plain' });
        let t = await this.treatments.create(this.scenarioUid, null, blob, 'data.csv', [], 'import');
        this.modalLaunchStatus = 'En cours...';
        while (t.treatment.resultat !== 'ok' && t.treatment.resultat !== 'ko') {
          await new Promise(r => setTimeout(r, 2000));
          t = await this.treatments.get(t.treatment.uid);
        }
        this.modalLaunchStatus = t.treatment.resultat === 'ok' ? 'Terminé ✓' : 'Erreur ✗';
        await this.loadPage();
        this.modalRow = this.getScrapingRow(this.modalProduct.id, this.modalCfg);
        if (this.modalRow) {
          this.modalEditUrl = this.modalRow.url || '';
          this.modalEditStatut = this.modalRow.statut || '';
          this.modalEditVeilleTarifaire = this.modalRow.veille_tarifaire ?? 0;
        }
      } catch (e) {
        this.modalLaunchStatus = 'Erreur: ' + (e.message || String(e));
      } finally { this.modalLaunching = false; }
    },
    async openConfigModal() {
      this.configEditing = null;
      this.configView = true;
      this.$emit('config-view-change', true);
      await Promise.all([this.loadConfigList(), this.loadGroupesList(), this.loadProjectsList(), this.loadProductColumns(), this.loadMarquesList(), this.loadCategoriesTree(), this.loadGroupRules()]);
    },
    async loadProjectsList() {
      try {
        const list = await this.projects.getAll(false, false, null, true);
        this.projectsList = (list || []).filter(p => p.libelle);
      } catch (e) { console.warn('loadProjectsList error', e); }
    },
    async loadProductColumns() {
      try {
        // Récupérer la structure de la table produits
        const _pcTable = this.ds.productColumnsTable || 'produits';
        const _defCols = this.ds.defaultProductColumns || [
          'id_cms', 'reference', 'nom', 'description', 'description_courte',
          'ean13', 'marque', 'status', 'prix_ht', 'prix_ttc', 'quantite',
          'meta_title', 'meta_description', 'lien_rewrite', 'default_category',
          'categories'
        ];
        const rows = await this.database.get('select', _pcTable, {
          select: '*',
          end: 'LIMIT 1',
        });
        if (rows && rows.length > 0) {
          // Extraire les noms de colonnes
          this.productColumns = Object.keys(rows[0]).filter(col => 
            // Exclure certaines colonnes non pertinentes
            !['id', 'created_at', 'updated_at', 'deleted_at'].includes(col)
          ).sort();
        } else {
          this.productColumns = _defCols;
        }
      } catch (e) {
        console.warn('loadProductColumns error', e);
        this.productColumns = this.ds.defaultProductColumns || [
          'id_cms', 'reference', 'nom', 'description', 'description_courte',
          'ean13', 'marque', 'status', 'prix_ht', 'prix_ttc', 'quantite',
          'meta_title', 'meta_description', 'lien_rewrite', 'default_category',
          'categories'
        ];
      }
    },
    updateBeforeCodeWithGenerated(generatedCode) {
      let currentCode = '';
      if (this.$refs.beforeCodeEditor) {
        currentCode = this.$refs.beforeCodeEditor.save() || '';
      } else {
        currentCode = this.configForm.before_code || '';
      }

      // Migration : supprimer l'ancien bloc query séparé s'il existe encore
      const oldQStart = '/*code generate query */';
      const oldQEnd = '/*code generate query end */';
      const oqs = currentCode.indexOf(oldQStart);
      const oqe = oqs !== -1 ? currentCode.indexOf(oldQEnd, oqs) : -1;
      if (oqs !== -1 && oqe !== -1) {
        currentCode = (currentCode.substring(0, oqs) + currentCode.substring(oqe + oldQEnd.length)).replace(/\n{3,}/g, '\n\n');
      }

      const startMarker = '/*code generate */';
      const endMarker = '/*code generate end */';

      let newCode;
      const startIndex = currentCode.indexOf(startMarker);
      const endIndex = currentCode.indexOf(endMarker, startIndex);

      if (startIndex !== -1 && endIndex !== -1) {
        const before = currentCode.substring(0, startIndex);
        const after = currentCode.substring(endIndex + endMarker.length);
        if (generatedCode.trim()) {
          newCode = before + `${startMarker}\n${generatedCode}\n${endMarker}` + after;
        } else {
          newCode = (before + after).replace(/\n{3,}/g, '\n\n');
        }
      } else {
        if (generatedCode.trim()) {
          const codeToAdd = `${startMarker}\n${generatedCode}\n${endMarker}`;
          newCode = currentCode.trim() ? codeToAdd + '\n\n' + currentCode : codeToAdd;
        } else {
          newCode = currentCode;
        }
      }

      this.configForm.before_code = newCode;
      this.$nextTick(() => {
        if (this.$refs.beforeCodeEditor) {
          this.$refs.beforeCodeEditor.load(newCode);
        }
      });
    },
    /** Force tous les champs de properties dans required (requis pour ChatGPT structured outputs) */
    _forceSchemaRequired(schema) {
      if (!schema || typeof schema !== 'object') return schema;
      const result = { ...schema };
      if (result.properties && typeof result.properties === 'object') {
        result.required = Object.keys(result.properties);
        // Récursion sur les sous-objets et items d'array
        const newProps = {};
        for (const [key, val] of Object.entries(result.properties)) {
          newProps[key] = this._forceSchemaRequired(val);
        }
        result.properties = newProps;
      }
      if (result.items && typeof result.items === 'object') {
        result.items = this._forceSchemaRequired(result.items);
      }
      return result;
    },
    refreshSchemaProperties() {
      // Extraire les propriétés du JSON schema
      try {
        let schema;
        const activeEditor = this.wizardActive ? this.$refs.wizardSchemaEditor : this.$refs.schemaEditor;
        if (activeEditor) {
          schema = activeEditor.save();
        } else if (this.configForm.schema_result) {
          schema = typeof this.configForm.schema_result === 'string' 
            ? JSON.parse(this.configForm.schema_result) 
            : this.configForm.schema_result;
        }
        
        if (schema && schema.properties && typeof schema.properties === 'object') {
          this.schemaPropertiesList = Object.keys(schema.properties);
        } else {
          this.schemaPropertiesList = [];
        }
      } catch (e) {
        console.warn('Error extracting schema properties:', e);
        this.schemaPropertiesList = [];
      }
    },
    generateQuery() {
      if (!this.configForm.domain.trim()) {
        alert('Veuillez d\'abord renseigner le domaine.');
        return;
      }
      const isFieldValid = f => {
        if (!f) return false;
        if (typeof f === 'object') return (
          (f.type === 'caracteristique' && f.column && f.column.trim()) ||
          (f.type === 'data_key' && f.key && f.key.trim())
        );
        return f.trim() && f !== 'caracteristiques_custom' && f !== 'data_custom';
      };
      if (this.identificationFields.length === 0 || !this.identificationFields.some(isFieldValid)) {
        alert('Veuillez d\'abord sélectionner au moins un champ d\'identification.');
        return;
      }
      const fields = this.identificationFields.filter(isFieldValid).map(f => {
        if (typeof f === 'object') {
          if (f.type === 'caracteristique') {
            const safeName = this._toSafeKey(f.column);
            return `\${data.caracteristique_${safeName}_${f.lang}}`;
          }
          if (f.type === 'data_key') {
            const safeKey = this._toSafeKey(f.key);
            return `\${data.data_${safeKey}}`;
          }
        }
        return `\${data.${f}}`;
      }).join(' ');
      this.configForm.query = `site:${this.configForm.domain} ${fields}`;
    },
    toggleRulesMode(visualMode) {
      if (visualMode && !this.visualRulesMode) {
        this.codeToVisualRules();
      }
      this.visualRulesMode = visualMode;
    },
    addGroup() {
      this.visualRuleGroups.push({
        operator: 'ET',
        conditions: [{
          field: 'marque',
          operator: '==',
          value: ''
        }]
      });
    },
    removeGroup(groupIdx) {
      this.visualRuleGroups.splice(groupIdx, 1);
    },
    addRuleToGroup(groupIdx) {
      this.visualRuleGroups[groupIdx].conditions.push({
        field: 'marque',
        operator: '==',
        value: ''
      });
    },
    removeRuleFromGroup(groupIdx, ruleIdx) {
      this.visualRuleGroups[groupIdx].conditions.splice(ruleIdx, 1);
      // Si le groupe devient vide, le supprimer
      if (this.visualRuleGroups[groupIdx].conditions.length === 0) {
        this.removeGroup(groupIdx);
      }
    },
    onFieldChange(rule) {
      // Changer l'opérateur par défaut en fonction du champ
      if (rule.field === 'marque' || rule.field === 'categorie') {
        rule.operator = '==';
      } else if (rule.field === 'prix_ht' || rule.field === 'prix_ttc') {
        rule.operator = '>';
      }
      rule.value = '';
    },
    addGroupInsideGroup(groupIdx) {
      // Ajouter un sous-groupe dans un groupe principal
      this.visualRuleGroups[groupIdx].conditions.push({
        isGroup: true,
        operator: 'ET',
        conditions: [{
          field: 'marque',
          operator: '==',
          value: ''
        }]
      });
    },
    openCategoryForVtr(idx, field) {
      this.categoryTargetVtr = { idx, field };
      this.selectedCategoryId = this.veilleTarifaireRegles[idx][field] || null;
      this.categoryModalVisible = true;
    },
    openCategoryForRule(rule) {
      this.categoryTargetRule = rule;
      this.selectedCategoryId = rule.value || null;
      this.categoryModalVisible = true;
    },
    saveCategorySelection() {
      if (this.selectedCategoryId !== null && this.categoryTargetVtr) {
        const { idx, field } = this.categoryTargetVtr;
        this.$set(this.veilleTarifaireRegles[idx], field, this.selectedCategoryId);
      } else if (this.selectedCategoryId !== null && this.categoryTargetRule) {
        this.$set(this.categoryTargetRule, 'value', this.selectedCategoryId);
      }
      this.categoryModalVisible = false;
      this.selectedCategoryId = null;
      this.categoryTargetRule = null;
      this.categoryTargetVtr = null;
    },
    visualRulesToCode() {
      if (this.visualRuleGroups.length === 0) {
        this.configForm.rules = 'return true;';
        if (this.$refs.rulesEditor) {
          this.$refs.rulesEditor.load('return true;');
        }
        return;
      }

      const convertRuleToCode = (rule) => {
        if (rule.isGroup) {
          // C'est un sous-groupe, traiter récursivement
          const subConditions = rule.conditions.map(subRule => convertRuleToCode(subRule)).filter(c => c);
          const operator = rule.operator === 'OU' ? ' || ' : ' && ';
          return subConditions.length > 1 ? `(${subConditions.join(operator)})` : subConditions[0];
        }

        // C'est une règle simple
        const field = rule.field;
        const rawVal = String(rule.value ?? '').replace(/\\/g, '\\\\').replace(/'/g, "\\'");
        const rawValLower = rawVal.toLowerCase();

        if (field === 'prix_ht' || field === 'prix_ttc') {
          return `data.${field} ${rule.operator} ${rule.value}`;
        } else if (field === 'categories') {
          const id = parseInt(rule.value, 10);
          if (rule.operator === '==' || rule.operator === 'LIKE') {
            return `(function(){ try { var _c=JSON.parse(data.categories||'[]'); return _c.map(String).indexOf('${rule.value}')!==-1; } catch(e){ return false; } })()`;
          } else {
            return `(function(){ try { var _c=JSON.parse(data.categories||'[]'); return _c.map(String).indexOf('${rule.value}')===-1; } catch(e){ return true; } })()`;
          }
        } else if (field === 'categorie') {
          const catField = this.ds.categoryPrimaryField || 'default_category';
          if (rule.operator === '==' || rule.operator === 'LIKE') {
            return `String(data.${catField}||'') === '${rule.value}'`;
          } else {
            return `String(data.${catField}||'') !== '${rule.value}'`;
          }
        } else if (field.startsWith('data.')) {
          // Champ dans la colonne JSON data — utilise _d parsé en début de code
          const dataKey = field.slice(5).replace(/'/g, "\\'");
          if (rule.operator === 'LIKE') {
            return `String(_d['${dataKey}']||'').toLowerCase().includes('${rawValLower}')`;
          } else if (rule.operator === 'NOT LIKE') {
            return `!String(_d['${dataKey}']||'').toLowerCase().includes('${rawValLower}')`;
          } else if (rule.operator === '!=') {
            return `String(_d['${dataKey}']||'') != '${rawVal}'`;
          } else {
            return `String(_d['${dataKey}']||'') == '${rawVal}'`;
          }
        } else {
          // Champ texte générique (nom, cle_externe, marque, reference…)
          if (rule.operator === 'LIKE') {
            return `String(data.${field} || '').toLowerCase().includes('${rawValLower}')`;
          } else if (rule.operator === 'NOT LIKE') {
            return `!String(data.${field} || '').toLowerCase().includes('${rawValLower}')`;
          } else if (rule.operator === '!=') {
            return `String(data.${field} || '') != '${rawVal}'`;
          } else {
            return `String(data.${field} || '') == '${rawVal}'`;
          }
        }
      };

      // Détecte si au moins une règle utilise un champ data.*
      const hasDataField = (groups) => groups.some(g =>
        (g.conditions || []).some(r => r.isGroup ? hasDataField([r]) : (r.field || '').startsWith('data.'))
      );
      const needsDataParse = hasDataField(this.visualRuleGroups);

      const groupConditions = this.visualRuleGroups.map(group => {
        const conditions = group.conditions.map(rule => convertRuleToCode(rule)).filter(c => c);
        // Les conditions dans un groupe sont combinées avec ET ou OU selon group.operator
        const innerOp = group.operator === 'OU' ? ' || ' : ' && ';
        return conditions.length > 1 ? `(${conditions.join(innerOp)})` : conditions[0];
      }).filter(c => c);

      const returnExpr = groupConditions.length > 1
        ? groupConditions.join(' || ')
        : groupConditions.length === 1
          ? groupConditions[0]
          : 'true';
      const prefix = needsDataParse
        ? `var _d = typeof data.data === 'string' ? JSON.parse(data.data || '{}') : (data.data || {});\n`
        : '';
      const code = groupConditions.length > 0
        ? `${prefix}return ${returnExpr};`
        : 'return true;';
      this.configForm.rules = code;
      if (this.$refs.rulesEditor) {
        this.$refs.rulesEditor.load(code);
      }
    },
    codeToVisualRules() {
      // Si le code est "return true;" ou vide, pas de règles visuelles à créer
      const code = (this.configForm.rules || '').trim();
      const isEmpty = !code || code === 'return true;' || code === 'return true';
      if (this.visualRuleGroups.length === 0 && !isEmpty) {
        this.addGroup();
      }
    },
    _normalizeGroups(groups) {
      // Garantit que chaque groupe/sous-groupe a 'operator' pour la réactivité Vue 2
      return groups.map(g => {
        if (!g.operator) this.$set(g, 'operator', 'ET');
        if (g.conditions) {
          g.conditions.forEach(c => {
            if (c.isGroup && !c.operator) this.$set(c, 'operator', 'ET');
          });
        }
        return g;
      });
    },
    async loadCategoriesTree() {
      try {
        const rows = await this.database.get('select', 'categories', {
          select: '*',
          end: 'LIMIT 10000',
        });
        
        console.log('Categories loaded:', rows?.length);
        
        // Construire l'arbre hiérarchique
        const buildTree = (rows, parentId = 0, depth = 0) => {
          const filtered = rows.filter(row => {
            const parent = row.id_cms_parent || row.id_parent || row.parent_id || 0;
            return parent == parentId;
          });
          
          return filtered.map(row => {
            const nom = typeof row.nom === 'string' ? JSON.parse(row.nom) : row.nom;
            const name = nom?.[this.defaultLang] || nom?.fr || row.name_fr || row.name || String(row.id_cms || row.id);
            const rowId = row.id_cms || row.id;
            // Utiliser l'ID de la table (unique) comme clé Vue, et id_cms pour la logique métier
            const uniqueId = row.id || rowId;
            return {
              id: rowId,  // Pour la logique métier
              uniqueId: uniqueId,  // Pour les clés Vue
              text: name,
              name_fr: name,
              nodes: buildTree(rows, rowId, depth + 1),
              state: {
                expanded: false,
                selected: false
              }
            };
          });
        };
        
        this.categoriesTree = buildTree(rows || []);
        console.log('Categories tree built:', this.categoriesTree.length, 'root nodes');
        
        // Remplir categoriesMap pour l'affichage
        this.categoriesMap = {};
        const fillMap = (nodes) => {
          for (const node of nodes) {
            this.categoriesMap[node.id] = node.text;
            if (node.nodes && node.nodes.length > 0) {
              fillMap(node.nodes);
            }
          }
        };
        fillMap(this.categoriesTree);
        
      } catch (e) {
        console.warn('loadCategoriesTree error', e);
        this.categoriesTree = [];
      }
    },
    async loadMarquesList() {
      if (this.ds.noMarquesList) { this.marquesList = []; return; }
      try {
        const rows = await this.database.get('select', this.ds.marquesTable || 'produits', {
          select: 'DISTINCT marque',
          where: 'marque IS NOT NULL AND marque != ""',
          end: 'ORDER BY marque ASC LIMIT 500',
        });
        this.marquesList = (rows || []).map(r => r.marque).filter(m => m);
      } catch (e) {
        console.warn('loadMarquesList error', e);
        this.marquesList = [];
      }
    },
    closeConfigView() {
      this.configView = false;
      this.configEditing = null;
      this.$emit('config-view-change', false);
    },
    async runConditionTest(page) {
      if (page !== undefined) this.conditionTestPage = page;
      // Sync rules code from visual mode if needed
      if (this.visualRulesMode) this.visualRulesToCode();
      else if (this.wizardActive && this.$refs.wizardRulesEditor) this.configForm.rules = this.$refs.wizardRulesEditor.save() ?? this.configForm.rules;
      else if (this.$refs.rulesEditor) this.configForm.rules = this.$refs.rulesEditor.save() ?? this.configForm.rules;
      const code = this.configForm.rules || 'return true;';
      let fn;
      try {
        fn = new Function('data', code);
      } catch (e) {
        alert('Erreur dans le code des conditions : ' + e.message);
        return;
      }
      this.conditionTestLoading = true;
      this.conditionTestRows = [];
      try {
        const _ctTable = this.ds.table || 'produits';
        const _ctSelect = this.ds.conditionTestSelect || 'id, id_cms, nom, marque, prix_ht, prix_ttc, categories, default_category';
        const ctParts = [this.ds.baseWhere || null, this.conditionTestWhere || null].filter(Boolean);
        const ctWhere = ctParts.join(' AND ');
        const ctExtraJoin = this.ds.extraJoin ? { extra_join: this.ds.extraJoin } : {};
        const offset = (this.conditionTestPage - 1) * this.conditionTestLimit;
        const [rows, countRows] = await Promise.all([
          this.database.get('select', _ctTable, {
            select: _ctSelect,
            where: ctWhere,
            end: `ORDER BY id DESC LIMIT ${this.conditionTestLimit} OFFSET ${offset}`,
            ...ctExtraJoin,
          }),
          this.database.get('select', _ctTable, {
            select: 'COUNT(*) AS total',
            where: ctWhere,
            ...ctExtraJoin,
          }),
        ]);
        this.conditionTestTotal = countRows?.[0]?.total || 0;
        this.conditionTestRows = (Array.isArray(rows) ? rows : []).map(row => {
          let passes = false;
          try { passes = !!fn(row); } catch (e) { passes = false; }
          return { ...row, _passes: passes };
        });
        // Détecter les clés disponibles pour le picker Colonnes
        if (this.conditionTestRows.length) {
          const skip = new Set(['id', '_passes', 'groupe']);
          this.conditionTestRowKeys = Object.keys(this.conditionTestRows[0]).filter(k => !k.startsWith('_') && !skip.has(k));
          // Extraire les clés du JSON data si présent
          if (this.conditionTestRows[0].data) {
            try {
              const dataObj = typeof this.conditionTestRows[0].data === 'string'
                ? JSON.parse(this.conditionTestRows[0].data)
                : this.conditionTestRows[0].data;
              this.conditionTestDataKeys = Object.keys(dataObj || {});
            } catch (e) { this.conditionTestDataKeys = []; }
          } else { this.conditionTestDataKeys = []; }
          // Sélectionner uniquement les colonnes de base par défaut
          const baseCols = (this.ds.conditionTestColumns || []).map(c => c.key);
          if (!this.testColsVisible.length) {
            this.testColsVisible = baseCols.length ? baseCols : this.allConditionTestCols.slice(0, 3).map(c => c.key);
          }
          // Auto-ajouter colonnes catégories si disponibles
          for (const key of ['categories', 'categorie_principale', 'default_category']) {
            if (!this.testColsVisible.includes(key) && this.conditionTestRowKeys.includes(key)) {
              this.testColsVisible.push(key);
            }
          }
        }
      } finally {
        this.conditionTestLoading = false;
      }
    },
    parseNomDisplay(val) {
      if (this.datasource && this.datasource.parseNomDisplay) {
        return this.datasource.parseNomDisplay(val);
      }
      if (!val) return '';
      try {
        const parsed = JSON.parse(val);
        if (typeof parsed === 'object' && parsed !== null) {
          return parsed.fr || parsed.en || Object.values(parsed)[0] || val;
        }
      } catch (e) { /* not JSON */ }
      return val;
    },
    initScrapTestFilterBuilder() {
      const self = this;
      const filters = this.ds.qbScrapTestFilters || this.ds.qbTestFilters || [
        { id: 'id',               label: 'id',               type: 'integer' },
        { id: 'id_cms',           label: 'id_cms',           type: 'integer' },
        { id: 'reference',        label: 'Référence',        type: 'string'  },
        { id: 'nom',              label: 'Nom',              type: 'string'  },
        { id: 'marque',           label: 'Marque',              type: 'string'  },
        { id: 'default_category', label: 'Catégorie principale', type: 'integer' },
        { id: 'prix_ht',          label: 'Prix HT',             type: 'double'  },
        { id: 'prix_ttc',         label: 'Prix TTC',            type: 'double'  },
        { id: 'categorie_nom',    label: 'Dans les catégories (nom)',  type: 'string'  },
      ];
      const operators = [
        { type: 'equal' }, { type: 'not_equal' },
        { type: 'less' },  { type: 'less_or_equal' },
        { type: 'greater' }, { type: 'greater_or_equal' },
        { type: 'contains' }, { type: 'not_contains' },
        { type: 'begins_with' }, { type: 'ends_with' },
        { type: 'is_empty',     apply_to: ['string','number','datetime','boolean'] },
        { type: 'is_not_empty', apply_to: ['string','number','datetime','boolean'] },
      ];
      try { this.$('#' + this.scrapTestFilterBuilderId).queryBuilder('destroy'); } catch (e) {}
      this.$('#' + this.scrapTestFilterBuilderId).queryBuilder({ filters, operators });
      this.$('#' + this.scrapTestFilterBuilderId).on('change afterDeleteRule.queryBuilder afterDeleteGroup.queryBuilder', function () {
        try {
          let sql = self.$('#' + self.scrapTestFilterBuilderId).queryBuilder('getSQL', false).sql;
          self.scrapTestWhere = self.postProcessSQL(sql);
        } catch (e) {
          self.scrapTestWhere = '';
        }
      });
    },
    initRoutingTestFilterBuilder() {
      const self = this;
      const filters = this.ds.qbScrapTestFilters || this.ds.qbTestFilters || [
        { id: 'id',               label: 'id',                  type: 'integer' },
        { id: 'id_cms',           label: 'id_cms',              type: 'integer' },
        { id: 'reference',        label: 'Référence',           type: 'string'  },
        { id: 'nom',              label: 'Nom',                 type: 'string'  },
        { id: 'marque',           label: 'Marque',              type: 'string'  },
        { id: 'ean13',            label: 'EAN13',               type: 'string'  },
        { id: 'default_category', label: 'Catégorie principale', type: 'integer' },
        { id: 'categorie_nom',    label: 'Dans les catégories (nom)', type: 'string' },
        { id: 'source',           label: 'Source',              type: 'string'  },
        { id: 'prix_ht',          label: 'Prix HT',             type: 'double'  },
        { id: 'prix_ttc',         label: 'Prix TTC',            type: 'double'  },
      ];
      const operators = [
        { type: 'equal' }, { type: 'not_equal' },
        { type: 'less' },  { type: 'less_or_equal' },
        { type: 'greater' }, { type: 'greater_or_equal' },
        { type: 'contains' }, { type: 'not_contains' },
        { type: 'begins_with' }, { type: 'ends_with' },
        { type: 'is_empty',     apply_to: ['string','number','datetime','boolean'] },
        { type: 'is_not_empty', apply_to: ['string','number','datetime','boolean'] },
      ];
      try { this.$('#' + this.routingTestFilterBuilderId).queryBuilder('destroy'); } catch (e) {}
      this.$('#' + this.routingTestFilterBuilderId).queryBuilder({ filters, operators });
      this.$('#' + this.routingTestFilterBuilderId).on('change afterDeleteRule.queryBuilder afterDeleteGroup.queryBuilder', function () {
        try {
          let sql = self.$('#' + self.routingTestFilterBuilderId).queryBuilder('getSQL', false).sql;
          self.routingTestWhere = self.postProcessSQL(sql);
        } catch (e) { self.routingTestWhere = ''; }
      });
    },
    openRoutingTest(domain, groupe) {
      this.routingTestDomain = domain;
      this.routingTestGroupe = groupe;
      this.routingTestRows = [];
      this.routingTestResolutions = {};
      this.routingTestSelectedIds = [];
      this.routingTestWhere = '';
      this.routingTestRunStatus = '';
      this.routingTestActive = true;
    },
    closeRoutingTest() {
      this.routingTestActive = false;
      this.routingTestDomain = null;
      this.routingTestGroupe = null;
    },
    resolveConfigForRow(row) {
      const configs = this.routingTestConfigs;
      const withCond = configs.filter(c => c.conditions);
      const fallback = configs.find(c => !c.conditions);
      for (const cfg of withCond) {
        if (!cfg.fn || cfg.fn(row)) return cfg;
      }
      return fallback || null;
    },
    async runRoutingResolution() {
      if (!this.routingTestRows.length) return;
      this.routingResolutionLoading = true;
      const result = {};
      for (const row of this.routingTestRows) {
        const resolved = this.resolveConfigForRow(row);
        result[row.id] = resolved ? { conditions: resolved.conditions || 'Défaut' } : null;
      }
      this.routingTestResolutions = result;
      this.routingResolutionLoading = false;
    },
    async runRoutingTestSearch(page) {
      if (page !== undefined) this.routingTestPage = page;
      this.routingTestLoading = true;
      this.routingTestRows = [];
      this.routingTestResolutions = {};
      try {
        const _table = this.ds.table || 'produits';
        const _select = this.ds.scrapTestSelect || this.ds.selectFields || 'id, id_cms, reference, nom, marque, prix_ht, categories, default_category';
        const whereParts = [this.ds.baseWhere || null, this.routingTestWhere || null].filter(Boolean);
        const where = whereParts.join(' AND ');
        const stExtraJoin = this.ds.extraJoin ? { extra_join: this.ds.extraJoin } : {};
        const offset = (this.routingTestPage - 1) * this.routingTestLimit;
        const [rows, countRows] = await Promise.all([
          this.database.get('select', _table, { select: _select, where, end: `ORDER BY id DESC LIMIT ${this.routingTestLimit} OFFSET ${offset}`, ...stExtraJoin }),
          this.database.get('select', _table, { select: 'COUNT(*) AS total', where, ...stExtraJoin }),
        ]);
        this.routingTestTotal = countRows?.[0]?.total || 0;
        const rawRows = rows || [];
        const isImport = this.ds.configType === 'scraping_file';
        if (!isImport && this.globalGroupFn) {
          this.routingTestRows = rawRows.map(p => ({ ...p, groupe: this.globalGroupFn(p) }));
        } else {
          this.routingTestRows = rawRows;
        }
        // Détecter les clés disponibles pour le picker Colonnes
        if (this.routingTestRows.length) {
          const skip = new Set(['id', 'groupe']);
          this.routingTestRowKeys = Object.keys(this.routingTestRows[0]).filter(k => !k.startsWith('_') && !skip.has(k));
          // Extraire les clés du JSON data si présent
          if (this.routingTestRows[0].data) {
            try {
              const dataObj = typeof this.routingTestRows[0].data === 'string'
                ? JSON.parse(this.routingTestRows[0].data)
                : this.routingTestRows[0].data;
              this.routingTestDataKeys = Object.keys(dataObj || {});
            } catch (e) { this.routingTestDataKeys = []; }
          } else { this.routingTestDataKeys = []; }
          // Sélectionner uniquement les colonnes de base par défaut
          const baseCols = (this.ds.conditionTestColumns || []).map(c => c.key);
          if (!this.testColsVisible.length) {
            this.testColsVisible = baseCols.length ? baseCols : this.allRoutingTestCols.slice(0, 3).map(c => c.key);
          }
          // Auto-ajouter colonnes catégories si disponibles
          for (const key of ['categories', 'categorie_principale', 'default_category']) {
            if (!this.testColsVisible.includes(key) && this.routingTestRowKeys.includes(key)) {
              this.testColsVisible.push(key);
            }
          }
          // Auto-ajouter colonnes data.xxx si disponibles (limité aux 5 premières)
          for (const k of this.routingTestDataKeys.slice(0, 5)) {
            const dkey = `data.${k}`;
            if (!this.testColsVisible.includes(dkey)) {
              this.testColsVisible.push(dkey);
            }
          }
        }
      } finally {
        this.routingTestLoading = false;
      }
    },
    async launchRoutingTest() {
      if (!this.scenarioUid || !this.routingTestSelectedIds.length) return;
      const selectedRows = this.routingTestRows.filter(r => this.routingTestSelectedIds.includes(r.id));
      if (!selectedRows.length) return;
      this.routingTestRunning = true;
      this.routingTestRunStatus = '';
      try {
        const _catField = this.ds.categoryPrimaryField || 'default_category';
        const isImport = this.ds.configType === 'scraping_file';
        const allRows = selectedRows.map(product => {
          const row = {
            reference: product.reference || '',
            nom: this.getNom(product),
            marque: product.marque || '',
            categories: product.categories || '',
            categorie_principale: product[_catField] || '',
            prix_ht: product.prix_ht || '',
            source: product.source || '',
            domaine: this.routingTestDomain,
            type: this.ds.configType || 'scraping',
            groupe: this.routingTestGroupe || '',
            scraping_config_id: '',
            scraping_config_id_current: '',
            data: product.data || '',
          };
          if (isImport) { row.id_import_produit = product.id; row.cle_produit = this._getCleProduir(product); }
          else { row.id_produit = product.id; row.cle_produit = this._getCleProduir(product); }
          return row;
        });
        const fields = Object.keys(allRows[0]);
        const csv = this.json2csv.parse(allRows, { fields, fieldNames: fields });
        const blob = new Blob([new TextEncoder().encode(csv)], { type: 'text/plain' });
        let t = await this.treatments.create(this.scenarioUid, null, blob, 'data.csv', ['test'], 'import');
        while (t.treatment.resultat !== 'ok' && t.treatment.resultat !== 'ko') {
          await new Promise(r => setTimeout(r, 2000));
          t = await this.treatments.get(t.treatment.uid);
        }
        this.routingTestRunStatus = t.treatment.resultat === 'ok' ? 'ok' : 'ko';
      } catch(e) {
        this.routingTestRunStatus = 'error';
        console.error('launchRoutingTest error', e);
      } finally {
        this.routingTestRunning = false;
      }
    },
    scrapTestPassesCurrentConfig(product) {
      return this.passesRules(product, this.scrapTestPseudoCfg);
    },
    toggleScrapTestAll(val) {
      this.scrapTestSelectedIds = val ? this.scrapTestApplicableRows.map(r => r.id) : [];
    },
    async launchBulkTest() {
      if (!this.scenarioUid || !this.scrapTestSelectedApplicable.length) return;
      this.scrapTestRunningBulk = true;
      try {
        // Sauvegarder en version test (-1) sans changer la version courante affichée
        const _savedId = this.configForm.id;
        const _savedVersion = this.configForm.version;
        const _savedProduction = this.configForm.production;
        await this.saveConfigAsTest();
        const testConfigId = this.configForm.id;
        // Restaurer la position courante
        this.configForm.id = _savedId;
        this.configForm.version = _savedVersion;
        this.configForm.production = _savedProduction;
        const _catField = this.ds.categoryPrimaryField || 'default_category';
        const isImport = this.ds.configType === 'scraping_file';
        const allRows = this.scrapTestSelectedApplicable.map(product => {
          const row = {
            reference: product.reference || '',
            nom: this.getNom(product),
            marque: product.marque || '',
            categories: product.categories || '',
            categorie_principale: product[_catField] || '',
            prix_ht: product.prix_ht || '',
            source: product.source || '',
            domaine: this.configForm.domain,
            type: this.configForm.type,
            groupe: this.configForm.groupe || '',
            scraping_config_id: testConfigId || '',
            scraping_config_id_current: _savedId || '',
            data: product.data || '',
          };
          if (isImport) {
            row.id_import_produit = product.id;
            row.cle_produit = this._getCleProduir(product);
          } else {
            row.id_produit = product.id;
            row.cle_produit = this._getCleProduir(product);
          }
          return row;
        });
        const fields = Object.keys(allRows[0]);
        const csv = this.json2csv.parse(allRows, { fields, fieldNames: fields });
        const blob = new Blob([new TextEncoder().encode(csv)], { type: 'text/plain' });
        let t = await this.treatments.create(this.scenarioUid, null, blob, 'data.csv', ['test'], 'import');
        while (t.treatment.resultat !== 'ok' && t.treatment.resultat !== 'ko') {
          await new Promise(r => setTimeout(r, 2000));
          t = await this.treatments.get(t.treatment.uid);
        }
        // Parser les résultats du CSV export et les stocker par id produit
        try {
          const allFiles = await this.treatments.getFiles(t.treatment.uid);
          const exportFiles = (allFiles || []).filter(f => f.type === 'export' && f.nom_fichier.endsWith('.csv'));
          for (const f of exportFiles) {
            const content = await this.treatments.getFile(f.uid, f.nom_fichier, false, false);
            const lines = content.trim().split('\n');
            if (lines.length < 2) continue;
            const parseCsvLine = (line) => {
              const result = []; let current = ''; let inQuotes = false;
              for (let i = 0; i < line.length; i++) {
                const ch = line[i];
                if (ch === '"') { if (inQuotes && line[i+1] === '"') { current += '"'; i++; } else inQuotes = !inQuotes; }
                else if (ch === ',' && !inQuotes) { result.push(current); current = ''; }
                else { current += ch; }
              }
              result.push(current);
              return result;
            };
            const headers = parseCsvLine(lines[0]);
            for (let i = 1; i < lines.length; i++) {
              if (!lines[i].trim()) continue;
              const values = parseCsvLine(lines[i]);
              const row = {};
              headers.forEach((h, idx) => { row[h] = values[idx] ?? ''; });
              const pid = parseInt(row.id_import_produit, 10) || parseInt(row.id_produit, 10);
              if (pid) this.$set(this.scrapTestResults, pid, row);
            }
          }
        } catch(e) { console.warn('launchBulkTest result parse error', e); }
      } catch(e) {
        console.error('launchBulkTest error', e);
      } finally {
        this.scrapTestRunningBulk = false;
      }
    },
    async validateSingleResult(product) {
      const result = this.scrapTestResults[product.id] || this.scrapTestResult;
      if (!result) return;
      this.scrapTestValidatingId = product.id;
      try {
        const isImport = this.ds.configType === 'scraping_file';
        const groupeVal = this.configForm.groupe || '';
        const cleProduir = result.cle_produit || this._getCleProduir(product);
        const row = {
          domaine: result.domaine || this.configForm.domain,
          type: result.type || this.configForm.type,
          groupe: groupeVal,
          url: result.url || '',
          statut_smd: result.statut_smd || '',
          query: result.query || '',
          result: result.result || '',
          identification: result.identification || '',
          identification_result: result.identification_result || '',
          statut: result.statut || '',
          veille_tarifaire: this.configForm.veille_tarifaire ? 1 : 0,
          date_recherche: new Date().toISOString().slice(0, 19).replace('T', ' '),
        };
        if (isImport) {
          row.id_import_produit = product.id;
        } else {
          row.id_produit = product.id;
        }
        // Upsert : chercher si une ligne existe déjà avec cette cle_produit
        const existing = await this.database.get('select', 'scraping', {
          select: 'id',
          where: `cle_produit = '${cleProduir.replace(/'/g, "''")}'`,
          end: 'LIMIT 1',
        });
        if (existing && existing.length > 0) {
          const setClause = Object.entries(row).map(([k, v]) => {
            if (v === null || v === undefined) return `\`${k}\` = NULL`;
            return `\`${k}\` = '${String(v).replace(/'/g, "\\'")}'`;
          }).join(', ');
          await this.database.get('update', 'scraping', {
            set: setClause,
            where: `cle_produit = '${cleProduir.replace(/'/g, "\\'")}'`,
          });
          this.$notify({ group: 'info', type: 'success', title: 'Résultat mis à jour en base' });
        } else {
          row.cle_produit = cleProduir;
          await this.database.get('insert into', 'scraping', { columns: Object.keys(row), data: row });
          this.$notify({ group: 'info', type: 'success', title: 'Résultat enregistré en base' });
        }
      } catch(e) {
        console.error('validateSingleResult error', e);
        this.$notify({ group: 'info', type: 'error', title: 'Erreur lors de l\'enregistrement' });
      } finally {
        this.scrapTestValidatingId = null;
      }
    },
    async runScrapTestSearch(page) {
      if (page !== undefined) this.scrapTestPage = page;
      this.scrapTestLoading = true;
      this.scrapTestRows = [];
      this.scrapTestSelectedIds = [];
      try {
        const _stTable = this.ds.table || 'produits';
        const _stSelect = this.ds.scrapTestSelect || this.ds.selectFields || 'id, id_cms, reference, nom, marque, prix_ht, categories, default_category';
        const stParts = [this.ds.baseWhere || null, this.scrapTestWhere || null].filter(Boolean);
        const stWhere = stParts.join(' AND ');
        const stExtraJoin = this.ds.extraJoin ? { extra_join: this.ds.extraJoin } : {};
        const offset = (this.scrapTestPage - 1) * this.scrapTestLimit;
        const [rows, countRows] = await Promise.all([
          this.database.get('select', _stTable, {
            select: _stSelect,
            where: stWhere,
            end: `ORDER BY id DESC LIMIT ${this.scrapTestLimit} OFFSET ${offset}`,
            ...stExtraJoin,
          }),
          this.database.get('select', _stTable, {
            select: 'COUNT(*) AS total',
            where: stWhere,
            ...stExtraJoin,
          }),
        ]);
        this.scrapTestTotal = countRows?.[0]?.total || 0;
        const rawRows = rows || [];
        // Appliquer globalGroupFn comme dans le listing principal (sauf pour les imports fichier où groupe est en DB)
        const isImport = this.ds.configType === 'scraping_file';
        if (!isImport && this.globalGroupFn) {
          this.scrapTestRows = rawRows.map(p => ({ ...p, groupe: this.globalGroupFn(p) }));
        } else {
          this.scrapTestRows = rawRows;
        }
        // Sync picker Colonnes avec tester les conditions
        const finalRows = this.scrapTestRows;
        if (finalRows.length) {
          const skip = new Set(['id', '_passes', 'groupe']);
          this.conditionTestRowKeys = Object.keys(finalRows[0]).filter(k => !k.startsWith('_') && !skip.has(k));
          // Extraire les clés du JSON data si présent
          if (finalRows[0].data) {
            try {
              const dataObj = typeof finalRows[0].data === 'string' ? JSON.parse(finalRows[0].data) : finalRows[0].data;
              this.conditionTestDataKeys = Object.keys(dataObj || {});
            } catch (e) { this.conditionTestDataKeys = []; }
          } else { this.conditionTestDataKeys = []; }
          const baseCols = (this.ds.conditionTestColumns || []).map(c => c.key);
          if (!this.testColsVisible.length) {
            this.testColsVisible = baseCols.length ? baseCols : this.allConditionTestCols.slice(0, 3).map(c => c.key);
          }
          // Auto-ajouter colonnes catégories si disponibles
          for (const key of ['categories', 'categorie_principale', 'default_category']) {
            if (!this.testColsVisible.includes(key) && this.conditionTestRowKeys.includes(key)) {
              this.testColsVisible.push(key);
            }
          }
        }
      } finally {
        this.scrapTestLoading = false;
      }
    },
    async launchScrapTest() {
      if (!this.scenarioUid || !this.scrapTestSelectedProduct || !this.configForm.domain) return;
      this.scrapTestLaunching = true;
      this.scrapTestStatus = 'running';
      try {
        // Sauvegarder en version test (-1) sans changer la version courante affichée
        const _savedId = this.configForm.id;
        const _savedVersion = this.configForm.version;
        const _savedProduction = this.configForm.production;
        await this.saveConfigAsTest();
        const testConfigId = this.configForm.id;
        // Restaurer la position courante
        this.configForm.id = _savedId;
        this.configForm.version = _savedVersion;
        this.configForm.production = _savedProduction;
        const product = this.scrapTestSelectedProduct;
        const isImport = this.ds.configType === 'scraping_file';
        const row = {
          nom: this.getNom(product),
          domaine: this.configForm.groupe ? `${this.configForm.domain}::${this.configForm.groupe}` : this.configForm.domain,
          type: this.configForm.type,
          groupe: this.configForm.groupe || '',
          scraping_config_id: testConfigId || '',
          scraping_config_id_current: _savedId || '',
          data: product.data || '',
        };
        if (isImport) {
          row.id_import_produit = product.id;
          row.cle_produit = this._getCleProduir(product);
        } else {
          row.id_produit = product.id;
          row.cle_produit = this._getCleProduir(product);
          row.reference = product.reference || '';
        }
        if (this.scrapTestUrl.trim()) row.url = this.scrapTestUrl.trim();
        const csv = this.json2csv.parse([row], { fields: Object.keys(row), fieldNames: Object.keys(row) });
        const blob = new Blob([new TextEncoder().encode(csv)], { type: 'text/plain' });
        let t = await this.treatments.create(this.scenarioUid, null, blob, 'data.csv', ['test'], 'import');
        while (t.treatment.resultat !== 'ok' && t.treatment.resultat !== 'ko') {
          await new Promise(r => setTimeout(r, 2000));
          t = await this.treatments.get(t.treatment.uid);
        }
        this.scrapTestStatus = t.treatment.resultat === 'ok' ? 'ok' : 'ko';
        // Récupérer les fichiers d'export du traitement
        this.scrapTestResultFiles = [];
        this.scrapTestResult = null;
        try {
          const allFiles = await this.treatments.getFiles(t.treatment.uid);
          const exportFiles = (allFiles || []).filter(f => f.type === 'export');
          for (const f of exportFiles) {
            const content = await this.treatments.getFile(f.uid, f.nom_fichier, false, false);
            // Tenter de parser le CSV (premier fichier data.csv)
            if (f.nom_fichier.endsWith('.csv') && !this.scrapTestResult) {
              try {
                const lines = content.trim().split('\n');
                if (lines.length >= 2) {
                  // Parser header/row CSV en tenant compte des guillemets
                  const parseCsvLine = (line) => {
                    const result = [];
                    let current = '';
                    let inQuotes = false;
                    for (let i = 0; i < line.length; i++) {
                      const ch = line[i];
                      if (ch === '"') {
                        if (inQuotes && line[i+1] === '"') { current += '"'; i++; }
                        else inQuotes = !inQuotes;
                      } else if (ch === ',' && !inQuotes) {
                        result.push(current); current = '';
                      } else {
                        current += ch;
                      }
                    }
                    result.push(current);
                    return result;
                  };
                  const headers = parseCsvLine(lines[0]);
                  const values = parseCsvLine(lines[1]);
                  const row = {};
                  headers.forEach((h, i) => { row[h] = values[i] ?? ''; });
                  this.scrapTestResult = row;
                  this.$set(this.scrapTestResults, product.id, row);
                }
              } catch (e) { console.warn('CSV parse error', e); }
            }
            let parsed = null;
            let isJson = false;
            try { parsed = JSON.parse(content); isJson = true; } catch (e) {}
            this.scrapTestResultFiles.push({ nom: f.nom_fichier, raw: content, parsed, isJson });
          }
        } catch (e) {
          console.warn('getFiles error', e);
        }
      } catch (e) {
        this.scrapTestStatus = 'error';
        console.error('launchScrapTest error', e);
      } finally {
        this.scrapTestLaunching = false;
      }
    },
    toggleExpandGroup(key) {
      // Par défaut expanded (undefined = true), toggle entre true et false
      const current = this.expandedGroups[key];
      this.$set(this.expandedGroups, key, current === false ? true : false);
    },
    onDragStart(cfg, groupKey) {
      this.draggingCfgId = cfg.id;
      this.dragSourceGroupKey = groupKey;
    },
    onDragOver(cfg, groupKey) {
      if (groupKey !== this.dragSourceGroupKey) return;
      this.dragOverCfgId = cfg.id;
    },
    onDragEnd() {
      this.draggingCfgId = null;
      this.dragOverCfgId = null;
      this.dragSourceGroupKey = null;
    },
    async onDrop(group) {
      if (!this.draggingCfgId || !this.dragOverCfgId || this.draggingCfgId === this.dragOverCfgId) {
        this.onDragEnd(); return;
      }
      const configs = group.configs;
      const fromIdx = configs.findIndex(c => c.id === this.draggingCfgId);
      const toIdx = configs.findIndex(c => c.id === this.dragOverCfgId);
      if (fromIdx === -1 || toIdx === -1) { this.onDragEnd(); return; }
      const reordered = [...configs];
      const [moved] = reordered.splice(fromIdx, 1);
      reordered.splice(toIdx, 0, moved);
      this.prioritySaving = true;
      const savedGroupKey = group.key;
      this.dragSourceGroupKey = savedGroupKey; // keep for spinner display during save
      try {
        for (let i = 0; i < reordered.length; i++) {
          const cfg = reordered[i];
          const type = cfg.type || this.ds.configType || 'scraping';
          const groupe = cfg.groupe && cfg.groupe.trim() ? cfg.groupe.trim() : null;
          const conditions = cfg.conditions && cfg.conditions.trim() ? cfg.conditions.trim() : null;
          let where = groupe
            ? `domain = '${cfg.domain}' AND type = '${type}' AND groupe = '${groupe}'`
            : `domain = '${cfg.domain}' AND type = '${type}' AND (groupe IS NULL OR groupe = '')`;
          if (conditions) where += ` AND conditions = '${conditions}'`;
          else where += ` AND (conditions IS NULL OR conditions = '')`;
          await this.database.get('update', 'scraping_config', { set: `priority = ${i}`, where });
          cfg.priority = i;
        }
        group.configs.splice(0, group.configs.length, ...reordered);
      } finally {
        this.prioritySaving = false;
      }
      this.onDragEnd();
    },
    async loadConfigList() {
      this.configLoading = true;
      try {
        const configType = this.ds.configType || 'scraping';
        const rows = await this.database.get('select', 'scraping_config', {
          select: '*',
          where: `type = '${configType}'`,
          end: 'ORDER BY domain ASC, groupe ASC, priority ASC, version DESC',
        });
        // Une seule ligne par domaine/groupe/conditions : la version prod si elle existe, sinon la plus récente
        const seen = new Map();
        for (const row of rows || []) {
          const key = `${row.domain}__${row.groupe || ''}__${row.conditions || ''}`;
          if (!seen.has(key) || row.production) seen.set(key, row);
        }
        this.configList = Array.from(seen.values());
      } finally {
        this.configLoading = false;
      }
    },
    async loadGroupesList() {
      const rows = await this.database.get('select', 'scraping_config', {
        select: 'DISTINCT groupe',
        where: `groupe IS NOT NULL AND groupe != ''`,
        end: 'ORDER BY groupe ASC',
      });
      this.groupesList = (rows || []).map(r => r.groupe).filter(Boolean);
    },
    newConfigForm() {
      const configType = this.ds.configType || 'scraping';
      this.configForm = {
        id: null, domain: '', type: configType, rules: '', query: '',
        search_mode: 'query', search_url: '',
        schema_result: '', identification: '[]', identification_result: '[]',
        prompt: '', projet: '', active: 1, veille_tarifaire: 0, before_code: '', after_code: '', status_code: '', render: '', groupe: '', conditions: '', priority: 0,
        version: -1, production: 0, description_version: '',
      };
      this.versionsList = [];
      this.identificationFields = [''];
      this.identificationResultFields = [''];
      this.statusIdentMode = 'standard';
      this.configEditing = {};
      this.visualRuleGroups = [];
      this.visualRulesMode = false;
      this.wizardActive = true;
      this.wizardStep = 1;
      this.wizardSkipConditions = false;
      this.conditionTestWhere = '';
      this.conditionTestRows = [];
      this.conditionTestPage = 1;
      this.conditionTestTotal = 0;
      this.conditionTestOpen = false;
      this.syncFields = { projet: false, schema: false, prompt: false };
      this._suppressDirty = true;
      this.$nextTick(() => { this.configFormDirty = false; this._suppressDirty = false; });
      this.$nextTick(() => {
        this.$nextTick(() => {
          if (this.$refs.wizardSchemaEditor) this.$refs.wizardSchemaEditor.load({ type: 'object', additionalProperties: false, properties: {} });
          this.refreshSchemaProperties();
        });
      });
    },
    editConfigForm(cfg) {
      this.configForm = {
        id: cfg.id,
        domain: cfg.domain || '',
        type: cfg.type || this.ds.configType || 'scraping',
        rules: cfg.rules || '',
        query: cfg.query || '',
        search_mode: cfg.search_mode || 'query',
        search_url: cfg.search_url || '',
        schema_result: cfg.schema_result || '',
        identification: cfg.identification || '[]',
        identification_result: cfg.identification_result || '[]',
        prompt: cfg.prompt || '',
        projet: cfg.projet || '',
        active: cfg.active ?? 1,
        veille_tarifaire: cfg.veille_tarifaire ?? 0,
        before_code: cfg.before_code || '',
        after_code: cfg.after_code || '',
        status_code: cfg.status_code || '',
        render: cfg.render || '',
        visual_rules: cfg.visual_rules || '',
        groupe: cfg.groupe || '',
        conditions: cfg.conditions || '',
        priority: cfg.priority ?? 0,
        version: cfg.version ?? -1,
        production: cfg.production ?? 0,
        description_version: cfg.description_version || '',
      };
      this.loadVersionsList();
      this.configEditing = cfg;
      this.syncFields = { projet: false, schema: false, prompt: false };
      this.statusIdentMode = cfg.status_code && cfg.status_code.trim() ? 'code' : 'standard';
      // Charger les champs d'identification depuis le JSON (avec rétrocompatibilité)
      if (!cfg.identification || cfg.identification.trim() === '') {
        // Pas de valeur : initialiser avec un champ vide
        this.identificationFields = [''];
      } else if (cfg.identification.startsWith('[')) {
        // Format nouveau : tableau JSON (peut contenir strings ou objets)
        try {
          const fields = JSON.parse(cfg.identification);
          this.identificationFields = Array.isArray(fields) && fields.length > 0
            ? fields.map(this._migrateIdentificationField)
            : [''];
        } catch (e) {
          console.error('Error parsing identification JSON array:', e);
          this.identificationFields = [''];
        }
      } else {
        // Format ancien : string simple (colonne unique)
        this.identificationFields = [this._migrateIdentificationField(cfg.identification)];
      }
      
      // Charger les champs de résultat d'identification depuis le JSON (avec rétrocompatibilité)
      if (!cfg.identification_result || cfg.identification_result.trim() === '') {
        // Pas de valeur : initialiser avec un champ vide
        this.identificationResultFields = [''];
      } else if (cfg.identification_result.startsWith('[')) {
        // Format nouveau : tableau JSON
        try {
          const resultFields = JSON.parse(cfg.identification_result);
          this.identificationResultFields = Array.isArray(resultFields) && resultFields.length > 0 ? resultFields : [''];
        } catch (e) {
          console.error('Error parsing identification_result JSON array:', e);
          this.identificationResultFields = [''];
        }
      } else {
        // Format ancien : string simple (colonne unique)
        this.identificationResultFields = [cfg.identification_result];
      }
      // Charger les règles visuelles si disponibles
      try {
        if (cfg.visual_rules && cfg.visual_rules.trim()) {
          this.visualRuleGroups = this._normalizeGroups(JSON.parse(cfg.visual_rules));
        } else {
          this.visualRuleGroups = [];
        }
      } catch (e) {
        console.error('Error parsing visual_rules:', e);
        this.visualRuleGroups = [];
      }
      this.visualRulesMode = false;
      // Compléter queryBuilderFields avec les champs data.* référencés dans visual_rules
      // mais absents du fichier actuellement sélectionné (autre fichier ouvert)
      try {
        if (cfg.visual_rules && cfg.visual_rules.trim()) {
          const collectFields = (groups) => {
            const fields = [];
            for (const g of groups) {
              for (const c of (g.conditions || [])) {
                if (c.isGroup) fields.push(...collectFields([c]));
                else if (c.field && c.field.startsWith('data.')) fields.push(c.field);
              }
            }
            return fields;
          };
          const usedFields = collectFields(this.visualRuleGroups);
          const existingIds = new Set((this.queryBuilderFields || []).map(f => f.value || f.id));
          for (const f of usedFields) {
            if (!existingIds.has(f)) {
              const label = f.slice(5);
              this.queryBuilderFields = [...(this.queryBuilderFields || []), { value: f, label, type: 'text' }];
              existingIds.add(f);
            }
          }
        }
      } catch (e) {}
      // Ne pas régénérer rules depuis visual_rules au chargement :
      // _persistConfig appelle déjà visualRulesToCode() avant de sauvegarder,
      // donc cfg.rules est toujours à jour. Écraser ici efface le code manuel.
      setTimeout(() => {
        if (this.$refs.schemaEditor) {
          try {
            const schema = cfg.schema_result
              ? (typeof cfg.schema_result === 'string' ? JSON.parse(cfg.schema_result) : cfg.schema_result)
              : { type: 'object', additionalProperties: false, properties: {} };
            this.$refs.schemaEditor.load(schema);
          } catch (e) {
            this.$refs.schemaEditor.load({ type: 'object', additionalProperties: false, properties: {} });
          }
        }
        if (this.$refs.rulesEditor) this.$refs.rulesEditor.load(this.configForm.rules || cfg.rules || '');
        if (this.$refs.beforeCodeEditor) this.$refs.beforeCodeEditor.load(cfg.before_code || '');
        if (this.$refs.afterCodeEditor) this.$refs.afterCodeEditor.load(cfg.after_code || '');
        if (this.$refs.statusCodeEditor) this.$refs.statusCodeEditor.load(cfg.status_code || '');
        if (this.$refs.wizardStatusCodeEditor) this.$refs.wizardStatusCodeEditor.load(cfg.status_code || '');
        this.refreshSchemaProperties();
      }, 150);
      this._suppressDirty = true;
      this.$nextTick(() => { this.configFormDirty = false; this._suppressDirty = false; });
    },
    backToConfigList() {
      this.configEditing = null;
      this.wizardActive = false;
    },
    openRenameGroupModal(group) {
      this.renameGroupTarget = group;
      this.renameGroupValue = group.groupe || '';
      this.renameGroupSaving = false;
      this.renameGroupModal = true;
    },
    async saveGroupRename() {
      if (!this.renameGroupTarget) return;
      this.renameGroupSaving = true;
      try {
        const { domain, groupe, configs } = this.renameGroupTarget;
        const type = (configs[0] && configs[0].type) || this.ds.configType || 'scraping';
        const domEsc = domain.replace(/'/g, "''");
        const typeEsc = type.replace(/'/g, "''");
        const oldGroupeWhere = groupe
          ? `domain = '${domEsc}' AND type = '${typeEsc}' AND groupe = '${groupe.replace(/'/g, "''")}'`
          : `domain = '${domEsc}' AND type = '${typeEsc}' AND (groupe IS NULL OR groupe = '')`;
        const newGroupe = this.renameGroupValue.trim();
        const newGroupeSet = newGroupe ? `groupe = '${newGroupe.replace(/'/g, "''")}'` : `groupe = NULL`;
        await this.database.get('update', 'scraping_config', { set: newGroupeSet, where: oldGroupeWhere });
        await this.loadConfigList();
        await this.loadGroupesList();
        await this.loadScrapingConfigs();
        this.renameGroupModal = false;
      } finally {
        this.renameGroupSaving = false;
      }
    },
    performCleanDomain(raw) {
      if (!raw) return '';
      let d = raw.trim();
      d = d.replace(/^https?:\/\//i, '');
      d = d.replace(/^www\./i, '');
      d = d.replace(/\/.*$/, '');
      d = d.toLowerCase();
      return d;
    },
    cleanDomain() {
      this.configForm.domain = this.performCleanDomain(this.configForm.domain);
    },
    wizardCancel() {
      this.wizardActive = false;
      this.configEditing = null;
    },
    exitWizardToAdvanced() {
      // Sauvegarder le schema du wizard dans configForm avant de quitter
      if (this.$refs.wizardSchemaEditor) {
        try {
          const schema = this.$refs.wizardSchemaEditor.save();
          this.configForm.schema_result = JSON.stringify(schema);
        } catch (e) {}
      }
      this.wizardActive = false;
      this.$nextTick(() => {
        this.$nextTick(() => {
          if (this.$refs.schemaEditor && this.configForm.schema_result) {
            try {
              this.$refs.schemaEditor.load(
                typeof this.configForm.schema_result === 'string'
                  ? JSON.parse(this.configForm.schema_result)
                  : this.configForm.schema_result
              );
            } catch (e) {}
          }
          if (this.$refs.rulesEditor) this.$refs.rulesEditor.load(this.configForm.rules || '');
          if (this.$refs.beforeCodeEditor) this.$refs.beforeCodeEditor.load(this.configForm.before_code || '');
          if (this.$refs.afterCodeEditor) this.$refs.afterCodeEditor.load(this.configForm.after_code || '');
          if (this.$refs.statusCodeEditor) this.$refs.statusCodeEditor.load(this.configForm.status_code || '');
        });
      });
    },
    switchToWizard() {
      // Synchroniser les éditeurs avancés vers configForm avant d'entrer dans le wizard
      if (this.$refs.schemaEditor) {
        try { this.configForm.schema_result = JSON.stringify(this._forceSchemaRequired(this.$refs.schemaEditor.save())); } catch (e) {}
      }
      if (this.$refs.rulesEditor) this.configForm.rules = this.$refs.rulesEditor.save() ?? this.configForm.rules;
      if (this.$refs.beforeCodeEditor) this.configForm.before_code = this.$refs.beforeCodeEditor.save() ?? this.configForm.before_code;
      if (this.$refs.afterCodeEditor) this.configForm.after_code = this.$refs.afterCodeEditor.save() ?? this.configForm.after_code;
      if (this.$refs.statusCodeEditor) this.configForm.status_code = this.$refs.statusCodeEditor.save() ?? this.configForm.status_code;
      this.wizardActive = true;
      this.wizardStep = 1;
      this.wizardSkipConditions = false;
      // Charger les règles visuelles si disponibles
      try {
        if (this.configForm.visual_rules && this.configForm.visual_rules.trim()) {
          this.visualRuleGroups = this._normalizeGroups(JSON.parse(this.configForm.visual_rules));
        } else {
          this.visualRuleGroups = [];
        }
      } catch (e) { this.visualRuleGroups = []; }
      // Compléter queryBuilderFields avec les champs data.* manquants utilisés dans les règles
      try {
        const collectFields = (groups) => {
          const fields = [];
          for (const g of groups) {
            for (const c of (g.conditions || [])) {
              if (c.isGroup) fields.push(...collectFields([c]));
              else if (c.field && c.field.startsWith('data.')) fields.push(c.field);
            }
          }
          return fields;
        };
        const usedFields = collectFields(this.visualRuleGroups);
        const existingIds = new Set((this.queryBuilderFields || []).map(f => f.value || f.id));
        for (const f of usedFields) {
          if (!existingIds.has(f)) {
            const label = f.slice(5);
            this.queryBuilderFields = [...(this.queryBuilderFields || []), { value: f, label, type: 'text' }];
            existingIds.add(f);
          }
        }
      } catch (e) {}
      // Charger le schema dans l'éditeur du wizard
      this.$nextTick(() => {
        if (this.$refs.wizardSchemaEditor && this.configForm.schema_result) {
          try {
            const schema = typeof this.configForm.schema_result === 'string'
              ? JSON.parse(this.configForm.schema_result)
              : this.configForm.schema_result;
            this.$refs.wizardSchemaEditor.load(schema);
          } catch (e) {}
        }
        if (this.$refs.wizardStatusCodeEditor) this.$refs.wizardStatusCodeEditor.load(this.configForm.status_code || '');
        if (this.$refs.wizardRulesEditor) this.$refs.wizardRulesEditor.load(this.configForm.rules || 'return true;');
      });
    },
    wizardPrev() {
      // Sauter l'étape 5 (veille tarifaire) si non applicable
      if (this.wizardStep === 6 && this.ds.showVeilleTarifaire === false) {
        this.wizardStep -= 2;
      } else {
        this.wizardStep--;
      }
    },
    async wizardNext() {
      if (this.wizardStep === 1) {
        this.cleanDomain();
      }
      if (this.wizardStep === 2) {
        // Étape 2 : conditions de déclenchement
        if (this.wizardSkipConditions) {
          this.configForm.rules = 'return true;';
        } else {
          this.visualRulesToCode();
        }
      }
      if (this.wizardStep === 3) {
        // Sauvegarder le schema et recalculer les propriétés
        if (this.$refs.wizardSchemaEditor) {
          try {
            const schema = this.$refs.wizardSchemaEditor.save();
            this.configForm.schema_result = JSON.stringify(this._forceSchemaRequired(schema));
            this.refreshSchemaProperties();
          } catch (e) {}
        }
      }
      if (this.wizardStep === 4 && !this.configForm.query.trim()) {
        this.generateQuery();
      }
      this.wizardStep++;
    },
    async _persistConfig() {
      // Sync editors into configForm before writing
      if (this.visualRulesMode) this.visualRulesToCode();
      else if (this.wizardActive && this.$refs.wizardRulesEditor) {
        this.configForm.rules = this.$refs.wizardRulesEditor.save() ?? this.configForm.rules;
      }
      const fields = ['domain','type','rules','query','search_mode','search_url','schema_result','identification','identification_result','prompt','projet','active','veille_tarifaire','before_code','after_code','status_code','visual_rules','groupe','conditions','priority','version','production','description_version'];
      const data = {};
      fields.forEach(f => { data[f] = this.configForm[f] ?? ''; });
      data.active = this.configForm.active ? 1 : 0;
      data.veille_tarifaire = this.configForm.veille_tarifaire ? 1 : 0;
      data.type = this.configForm.type || this.ds.configType || 'scraping';
      data.groupe = this.configForm.groupe && this.configForm.groupe.trim() ? this.configForm.groupe.trim() : null;
      data.conditions = this.configForm.conditions && this.configForm.conditions.trim() ? this.configForm.conditions.trim() : null;
      data.priority = this.configForm.priority ?? 0;
      data.version = this.configForm.version ?? -1;
      data.production = this.configForm.production ? 1 : 0;
      data.description_version = this.configForm.description_version || null;
      data.visual_rules = JSON.stringify(this.visualRuleGroups);
      const activeSchemaEditor = this.wizardActive ? this.$refs.wizardSchemaEditor : this.$refs.schemaEditor;
      if (activeSchemaEditor) {
        try { data.schema_result = JSON.stringify(this._forceSchemaRequired(activeSchemaEditor.save())); } catch (e) {}
      }
      if (this.$refs.rulesEditor) data.rules = this.$refs.rulesEditor.save() ?? '';
      if (this.$refs.wizardRulesEditor && this.wizardActive && !this.visualRulesMode) data.rules = this.$refs.wizardRulesEditor.save() ?? data.rules;
      if (this.$refs.beforeCodeEditor) data.before_code = this.$refs.beforeCodeEditor.save() ?? '';
      if (this.$refs.afterCodeEditor) data.after_code = this.$refs.afterCodeEditor.save() ?? '';
      if (this.$refs.statusCodeEditor) data.status_code = this.$refs.statusCodeEditor.save() ?? '';
      if (this.$refs.wizardStatusCodeEditor) data.status_code = this.$refs.wizardStatusCodeEditor.save() ?? data.status_code;
      if (this.configForm.id) {
        const setParts = fields.map(f => {
          const val = data[f];
          if (val === null || val === undefined) return `\`${f}\` = NULL`;
          return `\`${f}\` = "${String(val).replace(/"/g, '\\"')}"`;
        }).join(', ');
        await this.database.get('update', 'scraping_config', {
          set: setParts,
          where: `id = ${this.configForm.id}`,
        });
      } else {
        const result = await this.database.get('insert into', 'scraping_config', {
          columns: fields,
          data,
        });
        if (result && result.insertId) this.configForm.id = result.insertId;
      }
    },
    async navigateToSibling(sib) {
      if ((sib.conditions || '') === (this.configForm.conditions || '')) return;
      if (this.configFormDirty && this.configForm.id) {
        await this.saveConfig();
      }
      this.editConfigForm(sib);
    },
    async duplicateConfig() {
      if (this.configFormDirty && this.configForm.id) {
        await this.saveConfig();
      }
      const cfg = this.configForm;
      const copy = {
        id: null,
        domain: cfg.domain || '',
        type: cfg.type || this.ds.configType || 'scraping',
        rules: cfg.rules || '',
        query: cfg.query || '',
        search_mode: cfg.search_mode || 'query',
        search_url: cfg.search_url || '',
        schema_result: cfg.schema_result || '',
        identification: cfg.identification || '[]',
        identification_result: cfg.identification_result || '[]',
        prompt: cfg.prompt || '',
        projet: cfg.projet || '',
        active: cfg.active ?? 1,
        veille_tarifaire: cfg.veille_tarifaire ?? 0,
        before_code: cfg.before_code || '',
        after_code: cfg.after_code || '',
        status_code: cfg.status_code || '',
        render: cfg.render || '',
        visual_rules: cfg.visual_rules || '',
        groupe: cfg.groupe || '',
        conditions: '',
        priority: this.siblingConfigs.length ? Math.max(...this.siblingConfigs.map(c => c.priority || 0)) + 1 : 0,
        version: -1,
        production: 0,
        description_version: '',
      };
      this.configForm = copy;
      this.versionsList = [];
      this.configEditing = {};
      this.syncFields = { projet: false, schema: false, prompt: false };
      this._suppressDirty = true;
      this.$nextTick(() => { this.configFormDirty = false; this._suppressDirty = false; });
      this.$nextTick(() => {
        this.$nextTick(() => {
          const schemaRef = this.wizardActive ? this.$refs.wizardSchemaEditor : this.$refs.schemaEditor;
          if (schemaRef) { try { schemaRef.load(copy.schema_result ? JSON.parse(copy.schema_result) : {}); } catch(e) {} }
          if (this.$refs.rulesEditor) this.$refs.rulesEditor.load(copy.rules || '');
          if (this.$refs.wizardRulesEditor) this.$refs.wizardRulesEditor.load(copy.rules || '');
          if (this.$refs.beforeCodeEditor) this.$refs.beforeCodeEditor.load(copy.before_code || '');
          if (this.$refs.afterCodeEditor) this.$refs.afterCodeEditor.load(copy.after_code || '');
          if (this.$refs.statusCodeEditor) this.$refs.statusCodeEditor.load(copy.status_code || '');
          if (this.$refs.wizardStatusCodeEditor) this.$refs.wizardStatusCodeEditor.load(copy.status_code || '');
        });
      });
    },
    async syncToSiblings() {
      if (!this.configForm.id) return;
      this.syncingSiblings = true;
      try {
        await this._syncFieldsToSiblings();
        await this.loadConfigList();
      } finally {
        this.syncingSiblings = false;
      }
    },
    async _syncFieldsToSiblings() {
      const domain = this.configForm.domain.trim();
      const type = this.configForm.type || this.ds.configType || 'scraping';
      const groupe = this.configForm.groupe && this.configForm.groupe.trim() ? this.configForm.groupe.trim() : null;
      const conditions = this.configForm.conditions && this.configForm.conditions.trim() ? this.configForm.conditions.trim() : null;
      let siblingWhere = groupe
        ? `domain = '${domain}' AND type = '${type}' AND groupe = '${groupe}'`
        : `domain = '${domain}' AND type = '${type}' AND (groupe IS NULL OR groupe = '')`;
      if (conditions) {
        siblingWhere += ` AND (conditions IS NULL OR conditions = '' OR conditions != '${conditions}')`;
      } else {
        siblingWhere += ` AND conditions IS NOT NULL AND conditions != ''`;
      }
      const setParts = [];
      {
        const val = (this.configForm.projet || '').replace(/\\/g, '\\\\').replace(/"/g, '\\"');
        setParts.push(`projet = "${val}"`);
      }
      {
        const val = (this.configForm.prompt || '').replace(/\\/g, '\\\\').replace(/"/g, '\\"');
        setParts.push(`prompt = "${val}"`);
      }
      {
        const activeSchemaEditor = this.wizardActive ? this.$refs.wizardSchemaEditor : this.$refs.schemaEditor;
        let schemaVal = this.configForm.schema_result || '';
        if (activeSchemaEditor) {
          try { schemaVal = JSON.stringify(this._forceSchemaRequired(activeSchemaEditor.save())); } catch (e) {}
        }
        const val = schemaVal.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
        setParts.push(`schema_result = "${val}"`);
      }
      if (!setParts.length) return;
      await this.database.get('update', 'scraping_config', { set: setParts.join(', '), where: siblingWhere });
    },
    async saveConfig() {
      if (!this.configForm.domain.trim()) {
        alert('Domaine est obligatoire.');
        return;
      }
      this.configSaving = true;
      try {
        await this._persistConfig();
        await this.loadConfigList();
        await this.loadGroupesList();
        await this.loadScrapingConfigs();
        await this.loadVersionsList();
        this.configFormDirty = false;
      } finally {
        this.configSaving = false;
      }
    },
    async saveConfigAndContinue() {
      // Utilisé dans le wizard pour une nouvelle config : crée et bascule en mode avancé
      if (!this.configForm.domain.trim()) { alert('Domaine est obligatoire.'); return; }
      this.configSaving = true;
      try {
        await this._persistConfig();
        await this.loadConfigList();
        await this.loadGroupesList();
        await this.loadScrapingConfigs();
        await this.loadVersionsList();
        this.configFormDirty = false;
        this.wizardActive = false;
      } finally {
        this.configSaving = false;
      }
    },
    async toggleConfigActive(cfg) {
      const newVal = cfg.active ? 0 : 1;
      await this.database.get('update', 'scraping_config', {
        set: `active = ${newVal}`,
        where: `id = ${cfg.id}`,
      });
      cfg.active = newVal;
      await this.loadScrapingConfigs();
    },
    async toggleConfigVeilleTarifaire(cfg) {
      const newVal = cfg.veille_tarifaire ? 0 : 1;
      await this.database.get('update', 'scraping_config', {
        set: `veille_tarifaire = ${newVal}`,
        where: `id = ${cfg.id}`,
      });
      cfg.veille_tarifaire = newVal;
    },

    // --- Gestion des versions ---
    _versionWhere() {
      const domain = this.configForm.domain.trim();
      const type = this.configForm.type || this.ds.configType || 'scraping';
      const groupe = this.configForm.groupe && this.configForm.groupe.trim() ? this.configForm.groupe.trim() : null;
      const conditions = this.configForm.conditions && this.configForm.conditions.trim() ? this.configForm.conditions.trim() : null;
      let where = groupe
        ? `domain = '${domain}' AND type = '${type}' AND groupe = '${groupe}'`
        : `domain = '${domain}' AND type = '${type}' AND (groupe IS NULL OR groupe = '')`;
      if (conditions) {
        where += ` AND conditions = '${conditions}'`;
      } else {
        where += ` AND (conditions IS NULL OR conditions = '')`;
      }
      return where;
    },
    async loadVersionsList() {
      if (!this.configForm.domain) { this.versionsList = []; return; }
      const rows = await this.database.get('select', 'scraping_config', {
        select: 'id, version, production, description_version, updated_at',
        where: this._versionWhere(),
        end: 'ORDER BY version DESC',
      });
      this.versionsList = rows || [];
    },
    async switchVersion(versionRow) {
      const full = await this.database.get('select', 'scraping_config', {
        select: '*',
        where: `id = ${versionRow.id}`,
      });
      if (full && full[0]) this.editConfigForm(full[0]);
    },
    async saveAsTestOnly() {
      const _savedId = this.configForm.id;
      const _savedVersion = this.configForm.version;
      const _savedProduction = this.configForm.production;
      await this.saveConfigAsTest();
      this.configForm.id = _savedId;
      this.configForm.version = _savedVersion;
      this.configForm.production = _savedProduction;
      await this.loadVersionsList();
    },
    async saveConfigAsTest() {
      if (!this.configForm.domain.trim()) { alert('Domaine est obligatoire.'); return; }
      this.configSaving = true;
      try {
        const existing = await this.database.get('select', 'scraping_config', {
          select: 'id',
          where: `${this._versionWhere()} AND version = -1`,
        });
        if (existing && existing.length) {
          // Mettre à jour la version test existante (forcer production=0 si elle l'était)
          if (existing[0].production) {
            await this.database.get('update', 'scraping_config', {
              set: 'production = 0',
              where: `id = ${existing[0].id}`,
            });
          }
          this.configForm.id = existing[0].id;
          this.configForm.version = -1;
          this.configForm.production = 0;
        } else {
          // Créer une nouvelle version test
          this.configForm.id = null;
          this.configForm.version = -1;
          this.configForm.production = 0;
        }
        await this._persistConfig();
        await this.loadVersionsList();
        await this.loadScrapingConfigs();
        return this.configForm.id;
      } finally {
        this.configSaving = false;
      }
    },
    async saveConfigNewVersion() {
      if (!this.configForm.domain.trim()) { alert('Domaine est obligatoire.'); return; }
      // Calculer la prochaine version
      const rows = await this.database.get('select', 'scraping_config', {
        select: 'MAX(version) as maxv',
        where: `${this._versionWhere()} AND version >= 1`,
      });
      const nextVersion = ((rows && rows[0] && rows[0].maxv) ? parseInt(rows[0].maxv) : 0) + 1;
      this.configForm.id = null;
      this.configForm.version = nextVersion;
      this.configForm.production = this.versionSaveProd ? 1 : 0;
      this.configForm.description_version = this.versionSaveDescription || '';
      this.versionSaveModal = false;
      this.versionSaveDescription = '';
      this.versionSaveProd = false;
      // Si mise en prod directe, dépublier les autres versions d'abord
      if (this.configForm.production) {
        await this.database.get('update', 'scraping_config', {
          set: 'production = 0',
          where: this._versionWhere(),
        });
      }
      this.configSaving = true;
      try {
        await this._persistConfig();
        await this.loadConfigList();
        await this.loadVersionsList();
        await this.loadScrapingConfigs();
      } finally {
        this.configSaving = false;
      }
    },
    async promoteToProduction() {
      if (!this.configForm.id) return;
      if (this.configForm.version === -1) {
        // Version test → créer une nouvelle version numérotée en prod
        const rows = await this.database.get('select', 'scraping_config', {
          select: 'MAX(version) as maxv',
          where: `${this._versionWhere()} AND version >= 1`,
        });
        const nextVersion = ((rows && rows[0] && rows[0].maxv) ? parseInt(rows[0].maxv) : 0) + 1;
        // Dépublier toutes les versions existantes
        await this.database.get('update', 'scraping_config', {
          set: 'production = 0',
          where: this._versionWhere(),
        });
        // Créer la nouvelle version en prod
        this.configForm.id = null;
        this.configForm.version = nextVersion;
        this.configForm.production = 1;
        this.configSaving = true;
        try {
          await this._persistConfig();
          await this.loadVersionsList();
          await this.loadConfigList();
          await this.loadScrapingConfigs();
        } finally {
          this.configSaving = false;
        }
        return;
      }
      // Dépublier toutes les autres versions du même domaine/type/groupe
      await this.database.get('update', 'scraping_config', {
        set: 'production = 0',
        where: this._versionWhere(),
      });
      // Publier la version courante
      await this.database.get('update', 'scraping_config', {
        set: 'production = 1',
        where: `id = ${this.configForm.id}`,
      });
      this.configForm.production = 1;
      await this.loadVersionsList();
      await this.loadConfigList();
      await this.loadScrapingConfigs();
    },

    scrapingBadge(row) {
      const smd    = (row.statut_smd || '').toLowerCase();
      const statut = (row.statut     || '').toLowerCase();
      if (statut.includes('refus'))
        return { label: '✘ Refusé',    cls: 'badge-danger'  };
      if (statut.includes('valid') || (!statut && smd.includes('valid')))
        return { label: '✔ Validé',    cls: 'badge-success' };
      if (smd.includes('vérif') || smd.includes('verif') || smd.includes('diff') || smd.includes('à vérifier'))
        return { label: '⚠ ' + (row.statut_smd || 'À valider'), cls: 'badge-warning' };
      if (smd.includes('non tr'))
        return { label: 'Non trouvé',  cls: 'badge-danger'  };
      if (row.statut_smd)
        return { label: row.statut_smd, cls: 'badge-secondary' };
      if (row.identification_result || row.result)
        return { label: '● Trouvé',    cls: 'badge-info'    };
      return   { label: '⏳ En cours', cls: 'badge-secondary' };
    },
    addIdentificationField() {
      this.identificationFields.push('');
    },
    removeIdentificationField(idx) {
      this.identificationFields.splice(idx, 1);
      if (this.identificationFields.length === 0) {
        this.identificationFields.push('');
      }
      this.updateIdentificationFromFields();
    },
    addIdentificationResultField() {
      this.identificationResultFields.push('');
    },
    removeIdentificationResultField(idx) {
      this.identificationResultFields.splice(idx, 1);
      if (this.identificationResultFields.length === 0) {
        this.identificationResultFields.push('');
      }
      this.updateIdentificationResultFromFields();
    },
    updateIdentificationFromFields() {
      // Synchroniser identificationFields vers configForm.identification (JSON)
      // Filtrer les valeurs vides et les objets incomplets
      const validFields = this.identificationFields.filter(f => {
        if (!f && f !== 0) return false;
        if (typeof f === 'object') return (
          (f.type === 'caracteristique' && f.column && f.column.trim()) ||
          (f.type === 'data_key' && f.key && f.key.trim())
        );
        return typeof f === 'string' && f.trim() && f !== 'caracteristiques_custom' && f !== 'data_custom';
      });
      this.configForm.identification = JSON.stringify(validFields);
      this.generateBeforeCode();
    },
    generateBeforeCode() {
      // Passe unique : génère tout le code from scratch (identification + query)
      let generatedCode = '';
      const query = this.configForm.query || '';
      const searchUrl = this.configForm.search_url || '';
      const textToScan = query + ' ' + searchUrl;

      // ── 1. Champs JSON ──────────────────────────────────────────────────────
      const seenJson = new Set();
      const jsonLines = [];

      this.identificationFields
        .filter(f => typeof f === 'string' && f.trim() && !f.startsWith('caracteristiques_'))
        .forEach(fieldValue => {
          if (seenJson.has(fieldValue)) return;
          const opt = this.identificationOptions.find(o => o.value === fieldValue);
          if (opt && opt.isJson) { seenJson.add(fieldValue); jsonLines.push(`data.${opt.value} = JSON.parse(data.${opt.column}).${opt.lang};`); }
        });

      const varRegex = /\$\{data\.([^}]+)\}/g;
      let m;
      while ((m = varRegex.exec(textToScan)) !== null) {
        const field = m[1];
        if (seenJson.has(field) || field.startsWith('caracteristique_')) continue;
        const opt = this.identificationOptions.find(o => o.value === field);
        if (opt && opt.isJson) { seenJson.add(field); jsonLines.push(`data.${opt.value} = JSON.parse(data.${opt.column}).${opt.lang};`); }
      }

      if (jsonLines.length > 0) generatedCode += jsonLines.join('\n') + '\n';

      // ── 2. Caractéristiques ─────────────────────────────────────────────────
      const caracMap = new Map(); // key "safeName_lang" → { safeName, lang, column }

      this.identificationFields.forEach(f => {
        if (!(f && typeof f === 'object' && f.type === 'caracteristique' && f.column && f.column.trim())) return;
        const safeName = this._toSafeKey(f.column);
        const key = `${safeName}_${f.lang}`;
        if (!caracMap.has(key)) caracMap.set(key, { safeName, lang: f.lang, column: f.column });
      });

      varRegex.lastIndex = 0;
      while ((m = varRegex.exec(textToScan)) !== null) {
        const field = m[1];
        if (!field.startsWith('caracteristique_')) continue;
        const inner = field.substring('caracteristique_'.length);
        const parts = inner.split('_');
        const lang = parts[parts.length - 1];
        const safeName = parts.slice(0, -1).join('_');
        const key = `${safeName}_${lang}`;
        if (caracMap.has(key)) continue;
        const identCarac = this.identificationFields.find(f =>
          typeof f === 'object' && f.type === 'caracteristique' &&
          this._toSafeKey(f.column) === safeName && f.lang === lang
        );
        caracMap.set(key, { safeName, lang, column: identCarac ? identCarac.column : safeName });
      }

      if (caracMap.size > 0) {
        if (generatedCode) generatedCode += '\n';
        generatedCode += "// Extraction des caractéristiques\n";
        generatedCode += "const features = JSON.parse(data.caracteristiques || '[]');\n";
        caracMap.forEach(({ safeName, lang, column }) => {
          const escapedColumn = column.replace(/\\/g, '\\\\').replace(/'/g, "\\'");
          generatedCode += `const feature_${safeName} = features.find(feat => feat.feature_name && feat.feature_name['${this.defaultLang}'] === '${escapedColumn}');\n`;
          generatedCode += `data.caracteristique_${safeName}_${lang} = feature_${safeName}?.feature_value?.['${lang}'] || '';\n`;
        });
      }

      // ── 3. Clés data ──────────────────────────────────────────────────────────────────────
      const dataKeyMap = new Map(); // safeKey → original key
      this.identificationFields.forEach(f => {
        if (!(f && typeof f === 'object' && f.type === 'data_key' && f.key && f.key.trim())) return;
        const safeKey = this._toSafeKey(f.key);
        if (!dataKeyMap.has(safeKey)) dataKeyMap.set(safeKey, f.key);
      });
      // Scanner aussi la query et search_url pour les ${data.data_X} non couverts par identification
      varRegex.lastIndex = 0;
      while ((m = varRegex.exec(textToScan)) !== null) {
        const field = m[1];
        if (!field.startsWith('data_')) continue;
        const safeKey = field.substring('data_'.length);
        if (!dataKeyMap.has(safeKey)) dataKeyMap.set(safeKey, safeKey);
      }
      if (dataKeyMap.size > 0) {
        if (generatedCode) generatedCode += '\n';
        generatedCode += "// Extraction des clés data\n";
        generatedCode += "const _dataJson = JSON.parse(data.data || '{}');\n";
        dataKeyMap.forEach((originalKey, safeKey) => {
          const escaped = originalKey.replace(/\\/g, '\\\\').replace(/'/g, "\\'");
          generatedCode += `data.data_${safeKey} = _dataJson['${escaped}'] || '';\n`;
        });
      }

      this.updateBeforeCodeWithGenerated(generatedCode);
    },
    _toSafeKey(str) {
      return (str || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-zA-Z0-9]/g, '_');
    },
    _migrateIdentificationField(f) {
      // Convertit l'ancien format string "caracteristiques_Auteur_fr" vers le nouvel objet JSON
      if (typeof f === 'object' && f !== null) return f;
      if (typeof f === 'string' && f.startsWith('caracteristiques_') && f !== 'caracteristiques_custom') {
        const parts = f.substring('caracteristiques_'.length).split('_');
        const lang = parts[parts.length - 1];
        const column = parts.slice(0, -1).join('_');
        return { type: 'caracteristique', column, lang };
      }
      return f;
    },
    onIdentificationTypeChange(idx, value) {
      if (value === 'caracteristiques_custom') {
        this.$set(this.identificationFields, idx, { type: 'caracteristique', column: '', lang: this.defaultLang });
      } else if (value === 'data_custom') {
        this.$set(this.identificationFields, idx, { type: 'data_key', key: '' });
      } else if (value && value.startsWith('data_preset::')) {
        const presetKey = value.substring('data_preset::'.length);
        this.$set(this.identificationFields, idx, { type: 'data_key', key: presetKey });
      } else {
        this.$set(this.identificationFields, idx, value);
      }
      this.identificationFields = [...this.identificationFields];
    },
    updateCaracteristiqueObject(idx, key, value) {
      this.$set(this.identificationFields, idx, { ...this.identificationFields[idx], [key]: value });
      this.identificationFields = [...this.identificationFields];
    },
    updateDataKeyObject(idx, value) {
      this.$set(this.identificationFields, idx, { type: 'data_key', key: value });
      this.identificationFields = [...this.identificationFields];
    },
    updateIdentificationResultFromFields() {
      // Synchroniser identificationResultFields vers configForm.identification_result (JSON)
      this.configForm.identification_result = JSON.stringify(this.identificationResultFields.filter(f => f.trim()));
    },

    // ─── Query Builder Visuel ────────────────────────────────────────────────
    insertQueryVariable(opt) {
      let varStr;
      if (opt.segType === 'caracteristique') {
        const safeName = this._toSafeKey(opt.column || '');
        varStr = `\${data.caracteristique_${safeName}_${opt.lang}}`;
      } else {
        varStr = `\${data.${opt.value}}`;
      }
      if (this.configForm.search_mode === 'site_search') {
        this.configForm.search_url = (this.configForm.search_url || '') + varStr;
        return;
      }
      const q = this.configForm.query || '';
      const pos = this.queryCursorPos !== undefined ? this.queryCursorPos : q.length;
      this.configForm.query = q.slice(0, pos) + varStr + q.slice(pos);
      const newPos = pos + varStr.length;
      this.queryCursorPos = newPos;
      this.$nextTick(() => {
        const refName = this.wizardActive ? 'queryInputWiz' : 'queryInputAdv';
        const ref = this.$refs[refName];
        const el = ref && ref.$el ? ref.$el : ref;
        if (el) { el.focus(); el.setSelectionRange(newPos, newPos); }
      });
    },
    openQueryCaractModal() {
      this.queryCaractColumn = '';
      this.queryCaractLang = this.defaultLang || 'fr';
      this.showQueryCaractModal = true;
    },
    confirmAddCaractQuery(bvModalEvt) {
      if (!this.queryCaractColumn.trim()) {
        bvModalEvt && bvModalEvt.preventDefault();
        return;
      }
      this.insertQueryVariable({ segType: 'caracteristique', column: this.queryCaractColumn.trim(), lang: this.queryCaractLang });
      this.queryCaractColumn = '';
      this.showQueryCaractModal = false;
    },
    openQueryDataKeyModal() {
      this.queryDataKey = '';
      this.showQueryDataKeyModal = true;
    },
    confirmAddDataKeyQuery(bvModalEvt) {
      if (!this.queryDataKey.trim()) {
        bvModalEvt && bvModalEvt.preventDefault();
        return;
      }
      const originalKey = this.queryDataKey.trim();
      const safeKey = this._toSafeKey(originalKey);
      // Ajouter dans identificationFields si pas déjà présent (pour conserver la clé originale avec accents)
      const alreadyInIdent = this.identificationFields.some(
        f => f && typeof f === 'object' && f.type === 'data_key' && this._toSafeKey(f.key) === safeKey
      );
      if (!alreadyInIdent) {
        this.identificationFields = [...this.identificationFields.filter(f => f !== ''), { type: 'data_key', key: originalKey }];
      }
      this.insertQueryVariable({ value: `data_${safeKey}`, segType: 'data_key' });
      this.$nextTick(() => this.generateBeforeCode());
      this.queryDataKey = '';
      this.showQueryDataKeyModal = false;
    },
    // ─────────────────────────────────────────────────────────────────────────

    statutSmdClass(statut) {
      if (!statut) return 'badge-secondary';
      const s = statut.toLowerCase();
      if (s.includes('valid')) return 'badge-success';
      if (s.includes('diff') || s.includes('vérif')) return 'badge-warning';
      if (s.includes('erreur') || s.includes('error')) return 'badge-danger';
      return 'badge-info';
    },
  },
};
</script>

<style scoped>
.scraping-page {
  padding: 1rem;
}
thead th {
  vertical-align: middle;
  white-space: nowrap;
}
.scrap-row-selected td { background-color: #d1ecf1 !important; }
.scrap-row-active td   { background-color: #cfe2ff !important; }
.wizard-container {
  max-width: 680px;
  margin: 0 auto;
}
.wizard-step {
  min-height: 280px;
}
.wizard-step[style*="display: none"] {
  min-height: 0 !important;
  overflow: hidden;
}
.wizard-step h5 {
  margin-bottom: 0.5rem;
}
.wizard-step-label {
  cursor: default;
}
.wizard-choice-card {
  border: 2px solid #dee2e6;
  border-radius: 8px;
  padding: 16px 20px;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s;
  text-align: center;
}
.wizard-choice-card:hover {
  border-color: #007bff;
  background: #f0f6ff;
}
.wizard-choice-card.selected {
  border-color: #007bff;
  background: #e8f0fe;
}
.section-header {
  padding: 8px 12px;
  background: #f8f9fa;
  border-left: 3px solid #6c757d;
  border-radius: 0 4px 4px 0;
}
.section-header h6 {
  font-size: 0.95rem;
  font-weight: 600;
}
</style>

<style>
/* Styles non-scopés pour le composant récursif CategoryTreeItem */
.category-tree-node {
  user-select: none;
}

.category-tree-row {
  display: flex;
  align-items: center;
  padding: 6px 8px;
  cursor: pointer;
  transition: background-color 0.2s;
  border-radius: 3px;
  margin: 1px 0;
}

.category-tree-row:hover {
  background-color: #f0f0f0;
}

.category-tree-row.selected {
  background-color: #d4edff;
  border: 1px solid #0078d4;
}

.category-toggle {
  width: 24px;
  min-width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #666;
  font-size: 12px;
  margin-right: 4px;
}

.category-toggle:hover {
  background-color: rgba(0, 0, 0, 0.05);
  border-radius: 3px;
}

.category-checkbox {
  margin-right: 8px;
  cursor: pointer;
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

.category-text {
  flex: 1;
  font-size: 14px;
  cursor: pointer;
  padding: 2px 4px;
  border-radius: 3px;
}

.category-text:hover {
  background-color: rgba(0, 0, 0, 0.03);
}

.category-label {
  display: flex;
  align-items: center;
  flex: 1;
  margin: 0;
  cursor: pointer;
  padding: 2px 4px;
  border-radius: 3px;
}

.category-label:hover {
  background-color: rgba(0, 0, 0, 0.03);
}

.category-label input[type="checkbox"] {
  margin-right: 8px;
  cursor: pointer;
  width: 16px;
  height: 16px;
}

.category-label span {
  flex: 1;
  font-size: 14px;
}

.category-tree-children {
  margin-left: 24px;
  border-left: 1px dotted #d0d0d0;
  padding-left: 4px;
}
</style>